import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { clickEndsPress, isPlainMousePress } from '../hooks/usePressAction'; // [FLUID-8 2026-10-12]
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom'; // [CAMPAIGN-HISTORY 2026-09-04]
import styled, { css, keyframes } from 'styled-components';
import { lgSurface, lgPress, lgButtonPrimary, lgButtonSecondary, lgSunken, lgTile, LG_PRESS, LgPressProps, lgGreen } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]
import { usePresence } from '../hooks/usePresence'; // [FLUID-1 2026-10-12]
import { fluid } from '../styles/motion'; // [FLUID-1 2026-10-12]
import { fluidPop, fluidCascade, fluidDialog, fluidScrim, useLandWindow, useRowsLand } from '../components/Fluid'; // [FLUID-1 2026-10-12] [FLUID-3 2026-10-12] [FLUID-6 2026-10-12] useRowsLand
// [ONE GLASS RECIPE 2026-09-01, user] One tooltip surface for the whole app — see TipGlass.
import { GLASS_BLUR, GLASS_RADIUS, glassBackground, glassBorder, glassInk, glassShadow } from '../components/TipGlass';
import {
  fetchInventory, fetchInventoryFilters, updateInventoryRow, createInventoryRow, deleteInventoryRow,
  fetchInventoryKpis, InventoryKpis, InventoryRefundDispute, onDataChange,
  bulkUpdateInventory, undoBulkInventoryUpdate, // [BULK-EDIT 2026-08-03]
  peekCache, // [TAB-FAST 2026-08-13]
  fetchInventoryPmRanking, InventoryPmRanking, InventoryPmOption, // [INV-PM-RANK 2026-09-21]
  unsplitInventoryRow, // [INV-SPLIT 2026-10-07]
} from '../services/api';
import SplitInventoryModal from '../components/SplitInventoryModal'; // [INV-SPLIT 2026-10-07]
import { useRealtime } from '../hooks/useRealtime'; // [REALTIME] 2026-06-23
import { useSearchHighlight } from '../hooks/useSearchHighlight'; // [SEARCH-HIGHLIGHT 2026-09-29]
import { useIsMobile } from '../hooks/useIsMobile'; // [MOBILE 2026-07-07]
import { useFlipReorder } from '../utils/useFlipReorder'; // [FLIP-REORDER 2026-06-26]
import { effectiveCurrentMonth, MIN_DROPDOWN_MONTH } from '../utils/effectiveMonth'; // [EFFECTIVE-MONTH 2026-07-01] [INV-PM-RANK 2026-09-21]
import { InventoryRow, PaginationMeta, SortConfig, InventoryFiltersResponse } from '../types';
import SearchBar from '../components/SearchBar';
import { TableSkeleton, SkeletonBox } from '../components/Skeleton';
import { LoadFailed, EmptyState } from '../components/LoadState'; // [LOAD-STATES 2026-09-29]
import { useColumnResize, ResizeHandle, CutCell, CutLabel, cutCellStyle } from '../components/ColumnResize'; // [COL-RESIZE-ALL 2026-09-29]
import AnimatedNumber from '../components/AnimatedNumber'; // [MOTION-B 2026-08-20]
import ScaleToFit from '../components/ScaleToFit'; // [SINGLE-ROW-KPI 2026-06-23]
import GlassSelect from '../components/GlassSelect';
import ConfirmDialog from '../components/ConfirmDialog';
import { alertDialog } from '../components/dialogService';
import { toastSuccess, toastError } from '../components/toastService';
import { useSaveQueue, queuedToast } from '../hooks/useSaveQueue'; // [SAVE-QUEUE 2026-09-09]
import { useAuth } from '../contexts/AuthContext';
import { buildInventoryXlsx, downloadXlsx, inventoryExportFilename } from '../utils/inventoryExport'; // [INVENTORY-CSV-EXPORT 2026-07-03] [XLSX 2026-08-18]
// [AM-NAMES 2026-09-04, user] Account Manager roster + the parser for the ', '-joined
// am_names column. Never split that column by hand — see the prefix warning in amRoster.
import { AM_NAMES, parseAmNames, joinAmNames, normalizeAmNames } from '../utils/amRoster';
import { buttonSecondary } from '../styles/buttons'; // [BUTTON-STYLE 2026-09-30]
import { pageRect, pageViewport } from '../utils/appZoom'; // [APP-FIT 2026-10-26] popups placed in page px

/* ── Styled components ── */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  /* [STICKY-HEAD 2026-06-26] Fill the scroll card exactly and don't scroll HERE — the KPI
     bar + toolbar stay frozen and only the table rows (below) scroll. Covers My Inventory
     too, which renders this page. */
  height: 100%;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  background: ${(p) => p.theme.surface};

  /* [PHONE-LAYOUT 2026-10-23, user] phones: NOTHING is fixed at the top — the whole page (title, search, filters, My
     Inventory's banner + Performance Snapshot, the table) is ONE scroll, as Payments ("in the phone view nothing will
     be fixed on the top side otherwise how will i see the data for the below"). The table only swipes sideways
     (TableScroll below). */
  @media (max-width: 768px) {
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    -webkit-overflow-scrolling: touch;
  }
`;

// [STICKY-HEAD 2026-06-26] The FIXED top block — the (optional) Performance Snapshot KPI bar
// + the toolbar (title / Placed Inventory / search / filters / Add). Sits OUTSIDE the
// scrolling table area (flex-shrink:0) so it never scrolls; only the table rows below it do.
// The column header freezes via the table's own sticky <thead> pinned to the scroll area.
const FrozenTop = styled.div`
  flex-shrink: 0;
  background: ${(p) => p.theme.surface};
`;

// The scrolling table region — owns the vertical scroll (and horizontal on wide screens).
// The sticky <thead> pins to the top of THIS area, so the column header stays just under the
// frozen block while rows scroll. The bottom pad clears the floating dock at the very end of
// the scroll so the last row stays above the pill.
const TableScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-x: auto;
  overflow-y: scroll; /* [SCROLLBAR 2026-07-08] always show the vertical scroller on desktop */
  padding-bottom: 88px;

  /* [PHONE-LAYOUT 2026-10-22] Phones: a sideways swipe of the table stays in the table.
     [PHONE-LAYOUT 2026-10-23, user] ...and it is as tall as its rows (no box of its own to scroll up / down in): it
     only swipes sideways, the page scrolls (Container). The 88 px end pad still lifts the last row above the dock.
     (Was, 10-22: the title + filters stayed frozen and the rows scrolled in this box.) */
  @media (max-width: 768px) {
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
    flex: none;
    min-height: 0;
    overflow-y: hidden;
    overscroll-behavior-y: auto;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.04) 100%)'
    : 'linear-gradient(180deg, rgba(241,245,249,0.85) 0%, rgba(229,232,231,0.72) 100%)')};
  /* No backdrop-filter — it flashed white during AppLayout's PageFade tab
     transition (Chromium glitch) and the blur did nothing over the solid card. */
  border-bottom: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(22,25,23,0.10)')};
  box-shadow: inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.55)')};
  flex-wrap: wrap;
  gap: 10px;

  /* Phones: stack the title block / search / filters vertically (each full-width). */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
  @media (max-width: 600px) {
    padding: 10px 12px;
  }
`;

const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  /* [INV PHONE TOOLBAR 2026-09-23, user] Phones: "Inventory | Placed Inventory : $…" centred.
     Shared with My Inventory (same component), which centres too, by the user's OK. */
  @media (max-width: 768px) {
    justify-content: center;
  }
  /* [PHONE-LAYOUT 2026-10-22] Phones: the title on its own line and "Placed Inventory : $…" centred under it
     (the divider is hidden), so "My Inventory" + a 7-figure amount never runs past a 360 px screen. */
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 2px;
    text-align: center;
  }
`;

const PageTitle = styled.h1`
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  margin: 0;

  /* [PHONE-LAYOUT 2026-10-22] Phones: the page-head title size of the phone guide. */
  @media (max-width: 768px) {
    font-size: 17px;
    font-weight: 800;
    line-height: 1.2;
  }
`;

const TitleDivider = styled.span`
  width: 1px;
  height: 18px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(22,25,23,0.18)')};
  display: inline-block;

  /* [PHONE-LAYOUT 2026-10-22] Phones stack the title over the figure, so no divider between them. */
  @media (max-width: 768px) {
    display: none;
  }
`;

const EffectiveInventory = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => p.theme.muted};
  white-space: nowrap;

  strong {
    color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
    font-weight: 700;
    margin-left: 6px;
  }
`;

// [BUTTON-STYLE 2026-09-30] One button style: the green primary from styles/buttons.
const AddButton = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonPrimary}
  height: 32px; padding: 0 14px; /* [LIQUID-GLASS 2026-11-02, user] the glass capsule at the old size */
`;

// [INVENTORY-CSV-EXPORT 2026-07-03] Secondary (neutral glass) action button that
// sits after the green Add button in the toolbar — opens the download modal.
// [BUTTON-STYLE 2026-09-30] The recipe itself now lives in styles/buttons (buttonSecondary).
const DownloadButton = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonSecondary}
  height: 32px; padding: 0 14px; /* [LIQUID-GLASS 2026-11-02, user] the glass capsule at the old size */
`;

const Filters = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const RecordCount = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => p.theme.muted};
  white-space: nowrap;
`;

const FilterSelect = GlassSelect;

// [MOBILE 2026-07-07] Phone-only toolbar rows. The desktop toolbar wraps its
// controls freely; on phones the user wanted three deterministic rows instead of
// leaving it to flex-wrap. These are only rendered below 768px (see the render's
// `isMobile` branch) — the exact same controls/handlers, just grouped.
//
//   Row 1: search (grows) + record count (right)
//   Row 2: ALL filter dropdowns — Month / Type* / PM / Brand* / Status — three
//          per row, wrapping (My Inventory's three sit on one row; the full tab's
//          five wrap 3-then-2)
//   Row 3: actions — Add* + Download* — only rendered when those buttons exist
//          (* = only when that control shows)
//
// The `&&`-doubled specificity is needed because GlassSelect's own trigger sets
// min-width:130px; the doubled ampersand out-ranks that single class (same trick
// CellSelect uses) so the selects shrink to share the row instead of overflowing.
const MobileSearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const MobileFilterRow = styled.div`
  display: grid;
  /* [MYINV-MOBILE 2026-07-07] THREE filters per row, wrapping to the next line —
     so My Inventory's Month / PM / Status land on a single row, and the full
     Inventory tab's Month / Type / PM / Brand / Status wrap (3, then 2). */
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  width: 100%;

  && > * {
    min-width: 0;
    width: 100%;
    max-width: 100%;
  }

  /* [PHONE-LAYOUT 2026-10-22] TWO per row now: at three per row "October 2026" and "All Statuses" were cut to
     "Octobe…" / "All Statu…" (a 101 px cell at 360 px). Two per row shows every label in full; an odd last
     filter (Status on the Inventory tab) takes the whole row instead of leaving a hole. Triggers 38 px tall. */
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    && > * {
      min-height: 38px;
    }
    && > :last-child:nth-child(odd) {
      grid-column: 1 / -1;
    }
  }
`;

const MobileActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;

  /* [INV PHONE TOOLBAR 2026-09-23, user] Buttons only now: Status moved up into the filter
     grid, right after All Brands (this replaces [INV-MOBILE-STATUS-ROW 2026-07-07], where
     Status led this row and flexed to fill). Add + Download share the row as equal halves.
     flex-basis 0 with the default min-width:auto means they grow equally but never shrink
     below their own nowrap label, so on a very narrow phone "Download record" still reads
     in full and Add takes the rest. A lone button fills the row. Rendered on the Inventory
     tab only: My Inventory's Download sits in the filter grid instead (MobileGridWide). */
  & > * {
    flex: 1 1 0;
    justify-content: center;
  }

  /* [PHONE-LAYOUT 2026-10-22] Phones: Add + Download as 38 px tap targets. */
  @media (max-width: 768px) {
    & > * {
      min-height: 38px;
    }
  }
`;

/* [MYINV PHONE DOWNLOAD 2026-09-23, user] My Inventory, phones: "Download record" goes into the
   filter grid right after All Statuses. My Inventory always has 4 filters (Month / PM / AM /
   Status; Type and Brand are off), so Status sits alone on row 2 and this spans the two empty
   cells beside it. The button fills the cell with its label centred. At 320px the two cells
   are ~194px against the ~147px the nowrap label needs. */
const MobileGridWide = styled.div`
  grid-column: span 2;
  display: flex;

  & > * {
    flex: 1 1 auto;
    justify-content: center;
  }

  /* [PHONE-LAYOUT 2026-10-22] The filter grid is two per row now, so this is a full row of its own (span 2 = both
     columns) under Month | PM and AM | Status; a 38 px tap target. */
  @media (max-width: 768px) {
    & > * {
      min-height: 38px;
    }
  }
`;

const TableWrapper = styled.div`
  /* [MOTION-A 2026-08-20] Fades in when it replaces the skeleton (this wrapper is inside
     the non-loading branch, so it mounts at the handoff). Opacity only — this is the
     table's scroll container and a transform on it can break the sticky <thead>. */
  animation: pgReveal 200ms cubic-bezier(0.16, 1, 0.3, 1) both;
  @keyframes pgReveal {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @media (prefers-reduced-motion: reduce) { animation: none; }
  /* [FLUID-6 2026-10-12, user] …and every row on screen rises in, top to bottom, as the rows land (the ROWS move,
     never this wrapper): the page's useRowsLand on this wrapper, components/Fluid.tsx landRows. */
  /* [STICKY-HEAD 2026-06-26] Gives up its own scroll to the outer TableScroll so the
     sticky <thead> pins to that scroller (with the frozen toolbar above it). */
  overflow: visible;
`;

const Table = styled.table`
  width: 100%;
  table-layout: auto;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 2;
`;

const Th = styled.th<{ $sortable?: boolean; $align?: string }>`
  background: ${(p) => p.theme.pill};
  padding: 9px 10px;
  text-align: ${(p) => p.$align || 'left'};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  line-height: 1.3;
  /* Dark mode's green600 (#1a7f64) is too dim on the slate pill; use a
     brighter mint there so the headers read clearly. */
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  border-bottom: 1px solid ${(p) => p.theme.line};
  cursor: ${(p) => (p.$sortable ? 'pointer' : 'default')};
  white-space: nowrap;
  user-select: none;
  transition: color 180ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    color: ${(p) =>
      p.theme.colorScheme === 'dark'
        ? '#7de5ab'
        : p.$sortable
        ? p.theme.green700
        : p.theme.green600};
  }

  .sort-icon {
    margin-left: 4px;
    font-size: 9px;
    opacity: 0.5;
  }
  .sort-icon.active {
    opacity: 1;
    color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  }
`;

/* [MOTION-B 2026-08-20] The saved-row bloom, matching the shared DataTable's. Two fixed
   keyframe sets rather than one built from the theme inside the styled template: a nested
   template literal would terminate the outer styled`` string. */
const invRowFlashDark = keyframes`
  0%   { background: rgba(74, 205, 139, 0.26); }
  35%  { background: rgba(74, 205, 139, 0.20); }
  100% { background: transparent; }
`;
const invRowFlashLight = keyframes`
  0%   { background: rgba(19, 183, 144, 0.20); }
  35%  { background: rgba(19, 183, 144, 0.15); }
  100% { background: transparent; }
`;

const Tr = styled.tr<{ $flash?: boolean }>`
  & > td {
    transition: background 120ms cubic-bezier(0.4, 0, 0.2, 1), color 120ms cubic-bezier(0.4, 0, 0.2, 1), border-color 320ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* Row-hover identifier — same subtle neutral tint as the shared DataTable.
     Painted on the cells (not the row) so it overlays each row's own status
     colour (e.g. the flagged-red rows set via inline style) instead of being
     overridden by it, and guarded by hover:hover so touch/hybrid devices never
     get a sticky tint. The html.pointer-active prefix (set by DataTable on the
     first real pointer move) keeps a hard refresh with the cursor parked on a
     row from painting — and sticking — the highlight on load. */
  @media (hover: hover) {
    html.pointer-active &:hover > td {
      background: ${(p) =>
        p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(22,25,23,0.04)'};
    }
  }

  /* Declared after the hover rule so it wins while it runs, and it ends on transparent so
     the row hands straight back to its normal painting with nothing left behind. */
  ${(p) => (p.$flash ? css`
    & > td {
      animation: ${p.theme.colorScheme === 'dark' ? invRowFlashDark : invRowFlashLight}
        1100ms cubic-bezier(0.4, 0, 0.2, 1) both;
    }
    @media (prefers-reduced-motion: reduce) {
      & > td { animation: none; }
    }
  ` : '')}
`;

const Td = styled.td<{ $align?: string; $tint?: 'red' | 'amber' }>`
  padding: 4px 6px;
  font-size: 13px;
  /* [MYINV-ROW-TINT 2026-06-25] My Inventory colours the row's VALUES (not the row
     background) by status — crisp red/amber across the whole row on the clean surface
     in both themes. */
  color: ${(p) =>
    p.$tint === 'red' ? (p.theme.colorScheme === 'dark' ? '#f87171' : '#dc2626')
    : p.$tint === 'amber' ? (p.theme.colorScheme === 'dark' ? '#fbbf24' : '#d97706')
    : p.theme.text};
  font-weight: ${(p) => (p.$tint ? 600 : 'normal')};
  border-bottom: 1px solid ${(p) => p.theme.line};
  text-align: ${(p) => p.$align || 'left'};
  transition: color 120ms cubic-bezier(0.4, 0, 0.2, 1), border-color 320ms cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;

  /* [PHONE-LAYOUT 2026-10-22] Phones: more air between columns (10 px a side). The row height comes from the
     28 px row buttons below (copy / open / history, Split, Delete, the checkbox), so rows are about 36 px. Column
     resize is off on phones, so INV_CELL_PAD_X does not apply here. */
  @media (max-width: 768px) {
    padding: 4px 10px;
  }
`;

// [STATUS-TEXT 2026-06-25] The Status column shows its value as plain text, just
// coloured by status (the agreed legend) — in BOTH the Inventory and My Inventory tabs.
// Shades are theme-tuned (brighter on dark, deeper on light) so every status stays
// legible in both themes. Spelling variants ("At - Risk" vs "At-Risk") are normalised
// (strip spaces, lowercase) so both map to one colour.
const StatusText = styled.span<{ $status?: string }>`
  font-weight: 500;
  white-space: nowrap;
  color: ${(p) => {
    const s = String(p.$status ?? '').replace(/\s+/g, '').toLowerCase();
    const dark = p.theme.colorScheme === 'dark';
    if (s === 'expected') return dark ? '#60a5fa' : '#2563eb';
    if (s === 'at-risk') return dark ? '#fbbf24' : '#d97706';
    if (s === 'retained') return dark ? '#4fdc95' : '#1e7c63';
    if (s === 'cancelled') return dark ? '#f87171' : '#dc2626';
    if (s === 'at-riskexpected') return dark ? '#fdba74' : '#c2410c';
    return p.theme.text;
  }};
`;

// [CAMPAIGN-STATUS 2026-07-06] "Existing Campaign" uses the DEFAULT cell color (theme.text) —
// the same shade as every other column (PM Name / Brand / Type). "New Campaign" uses the app's
// theme GREEN — the exact pairing DataTable uses for its accent text (#1fd8a4 on dark /
// green600 on light) — so it matches the theme and clears WCAG-AA in both modes (7.6:1 dark,
// 4.9:1 light). Per user 2026-07-06. nowrap keeps "Existing Campaign" on one line in its column.
const CampaignTag = styled.span<{ $isNew?: boolean }>`
  white-space: nowrap;
  color: ${(p) =>
    p.$isNew
      ? (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)
      : p.theme.text};
`;

const CellInput = styled.input`
  width: 100%;
  /* [CELL-WIDTH 2026-06-26] min-width:0 lets the input shrink to the cell instead of its
     default ~20-char intrinsic width forcing a narrow column wider while editing. */
  min-width: 0;
  box-sizing: border-box;
  /* [SLIM-EDIT 2026-06-25 · taller box] A little vertical padding makes the editor box
     taller and easier to read; an equal NEGATIVE vertical margin cancels it out so the
     cell's content height — and therefore the row height — never changes on edit. The
     INSET box-shadow border traces the (now taller) box without adding any layout height. */
  border: none;
  border-radius: 4px;
  background: transparent;
  color: ${(p) => p.theme.text};
  font-size: 13px;
  padding: 3px 8px;
  margin: -3px 0;
  font-family: inherit;
  outline: none;
  box-shadow: inset 0 0 0 1px ${(p) => p.theme.line};
  transition: box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &:focus {
    box-shadow: inset 0 0 0 1.5px ${(p) => p.theme.green600};
  }

  /* Make the date/month calendar icon visible in dark theme */
  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    filter: ${(p) => (p.theme.colorScheme === 'dark' ? 'invert(1)' : 'none')};
  }
`;

/* [AM-NAMES 2026-09-04, user] The AM Names cell in DISPLAY mode. Several names share one
   150px column, so the text ellipsises and carries a `title` with the full list — the same
   treatment the Second Voice field uses when it holds more than one name. A count badge
   would hide who is on the row, which is the one thing this column exists to show. */
const AmCell = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CellSelect = styled(GlassSelect)`
  /* [SLIM-EDIT 2026-06-25] [CELL-WIDTH 2026-06-26] Keep the in-cell editor EXACTLY the size
     of the read-only cell so opening the dropdown never grows the ROW or the COLUMN. The base
     GlassSelect trigger sets min-width:130px + 7px vertical padding; a plain rule here ties on
     specificity and loses, so the doubled-ampersand specificity bump is what actually overrides
     them — the trigger then fills the cell (width:100%) and shrinks below 130px (min-width:0)
     instead of forcing the column wider. The options list is portaled so it overlays w/o layout. */
  && {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    padding-top: 0;
    padding-bottom: 0;
  }
`;

const DeleteBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  border: none;
  background: transparent;
  color: ${(p) => p.theme.muted};
  font-size: 13px;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  transition: all 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    color: #dc2626;
    background: rgba(220, 38, 38, 0.08);
  }

  /* [PHONE-LAYOUT 2026-10-22] Phones: a 28 px tap target (was 23 x 24). */
  @media (max-width: 768px) {
    min-width: 28px;
    min-height: 28px;
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  position: relative;
  isolation: isolate;
  overflow: hidden;
  ${lgPress}
  border-radius: 8px;
`;


/* [INV-SPLIT 2026-10-07, user] "Split 1/2" beside the website of each PM part of a split website. */
const SplitTag = styled.span`
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  white-space: nowrap;
  vertical-align: middle;
  color: ${(p) => p.theme.greenInk};
  background: ${(p) => p.theme.greenTint};

  /* [PHONE-LAYOUT 2026-10-22] Phones: labels at least 11 px. */
  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

/* [INV-SPLIT 2026-10-07, user] The text buttons in the row's action cell ("Split" / "Undo split"). */
const SplitActionBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  border: none;
  background: transparent;
  color: ${(p) => p.theme.greenInk};
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  transition: all 120ms cubic-bezier(0.4, 0, 0.2, 1);
  &:hover { background: ${(p) => p.theme.pill}; }
  &:focus-visible { outline: 2px solid ${(p) => p.theme.greenInk}; outline-offset: 1px; }

  /* [PHONE-LAYOUT 2026-10-22] Phones: a 28 px tall tap target (was 24). */
  @media (max-width: 768px) {
    min-height: 28px;
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  position: relative;
  isolation: isolate;
  overflow: hidden;
  ${lgPress}
  border-radius: 8px;
`;

const ActionBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  border: none;
  background: transparent;
  color: ${(p) => p.theme.muted};
  font-size: 13px;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  transition: all 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    color: ${(p) => p.theme.greenInk}; /* [GREEN-ON-DARK 2026-08-27] */
    background: ${(p) => p.theme.pill};
  }

  /* [PHONE-LAYOUT 2026-10-22] Phones: a 28 px tap target (the PIA renewal pen). */
  @media (max-width: 768px) {
    min-width: 28px;
    min-height: 28px;
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  position: relative;
  isolation: isolate;
  overflow: hidden;
  ${lgPress}
  border-radius: 8px;
`;

/* ── [BULK-EDIT 2026-08-03] Multi-row selection + bulk action bar ──
   The selection column is a real table column (not an overlay) so it scrolls and
   sticks with the rest of the header. Everything in this block is additive — with
   bulk disabled (`bulkCols` empty) none of it renders and the table is byte-identical
   to before. */

const SelectTh = styled.th`
  background: ${(p) => p.theme.pill};
  padding: 9px 6px 9px 12px;
  width: 34px;
  border-bottom: 1px solid ${(p) => p.theme.line};
  vertical-align: middle;
  line-height: 0;
`;

const SelectTd = styled.td`
  padding: 4px 6px 4px 12px;
  width: 34px;
  border-bottom: 1px solid ${(p) => p.theme.line};
  vertical-align: middle;
  line-height: 0;
  transition: border-color 320ms cubic-bezier(0.4, 0, 0.2, 1);
`;

const RowCheckbox = styled.input`
  appearance: none;
  -webkit-appearance: none;
  width: 15px;
  height: 15px;
  margin: 0;
  border-radius: 4px;
  border: 1.5px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.32)' : 'rgba(22,25,23,0.30)')};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.75)')};
  cursor: pointer;
  display: inline-block;
  vertical-align: middle;
  position: relative;
  transition: background 120ms cubic-bezier(0.4, 0, 0.2, 1), border-color 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover:not(:disabled) {
    border-color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  }

  &:checked,
  &:indeterminate {
    background: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
    border-color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  }

  /* Tick — drawn with borders so it needs no icon font or SVG. */
  &:checked::after {
    content: '';
    position: absolute;
    left: 4px;
    top: 1px;
    width: 3px;
    height: 7px;
    border: solid ${(p) => (p.theme.colorScheme === 'dark' ? '#121212' : '#ffffff')};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  /* Dash for the header's "some but not all" state. */
  &:indeterminate::after {
    content: '';
    position: absolute;
    left: 2.5px;
    top: 5.5px;
    width: 7px;
    height: 2px;
    background: ${(p) => (p.theme.colorScheme === 'dark' ? '#121212' : '#ffffff')};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  /* [PHONE-LAYOUT 2026-10-22] Phones: an 18 px box inside a 28 px tap target (it was a 15 px target). The box is
     the padding area inside a 5 px transparent border, its ring an inset shadow; the -5 px margins keep the cell
     the size an 18 px box gives. The tick and the dash are re-centred for the bigger box. */
  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
    margin: -5px;
    border: 5px solid transparent;
    border-radius: 9px;
    background-clip: padding-box;
    box-shadow: inset 0 0 0 1.5px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.32)' : 'rgba(22,25,23,0.30)')};

    &:hover:not(:disabled) {
      border-color: transparent;
      box-shadow: inset 0 0 0 1.5px ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
    }
    &:checked,
    &:indeterminate {
      border-color: transparent;
      box-shadow: none;
      /* the checked fill above is a background shorthand, which resets the clip: clip it to the box again */
      background-clip: padding-box;
    }
    &:checked::after {
      left: 6px;
      top: 2.5px;
      width: 4px;
      height: 9px;
    }
    &:indeterminate::after {
      left: 4.5px;
      top: 8px;
      width: 9px;
    }
  }
`;

const BulkBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 9px 20px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(74, 205, 139, 0.10)' : 'rgba(26, 159, 125, 0.08)')};
  border-bottom: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.28)' : 'rgba(26,159,125,0.22)')};
  animation: bulkIn 0.16s ease-out;

  @keyframes bulkIn {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @media (max-width: 768px) {
    padding: 9px 12px;
    gap: 8px;
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  background-image: linear-gradient(180deg, rgba(255,255,255,${(p) => (p.theme.colorScheme === 'dark' ? '0.08' : '0.65')}), rgba(255,255,255,0) 60%);
  box-shadow: inset 0 1px 0 rgba(255,255,255,${(p) => (p.theme.colorScheme === 'dark' ? '0.12' : '0.85')}), 0 1px 2px rgba(0,0,0,0.08);
`;

const BulkCount = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  white-space: nowrap;
`;

const BulkLink = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  &:hover { text-decoration: underline; }
  &:disabled { opacity: 0.5; cursor: default; text-decoration: none; }

  /* [PHONE-LAYOUT 2026-10-22] Phones: "Select all N" / "Clear" as 32 px tall tap targets (were 15). */
  @media (max-width: 768px) {
    min-height: 32px;
    padding: 0 2px;
  }
`;

const BulkSep = styled.span`
  width: 1px;
  height: 14px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(22,25,23,0.15)')};
`;

// [BUTTON-STYLE 2026-09-30] Both variants (Edit, and the $muted Undo) are the secondary style.
// [BUTTON-STYLE 2026-09-30] The bulk bar's main action keeps standing out (it was a green tint): main
// green button; a $muted action is the plain one.
const BulkBtn = styled.button.attrs<LgPressProps>(LG_PRESS)<{ $muted?: boolean }>`
  ${(p) => (p.$muted ? lgButtonSecondary : lgButtonPrimary)}
  height: 32px; padding: 0 14px; /* [LIQUID-GLASS 2026-11-02, user] the glass capsule at the old size */
`;

const BulkPreview = styled.div`
  font-size: 12px;
  line-height: 1.5;
  color: ${(p) => p.theme.muted};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(22,25,23,0.04)')};
  border-radius: 8px;
  padding: 10px 12px;
  margin-top: 4px;

  strong { color: ${(p) => p.theme.text}; font-weight: 700; }
`;

// [AUTO-REFRESH 2026-08-03] See reloadGuard — blocks the auto-reload while any of this page's
// modals (renewal, add row, bulk edit, download) is open.
// [FLUID-3 2026-10-12, user] `$open` drives the fluid recipe (components/Fluid.tsx): the shade fades in, fades
// out quicker, and takes no clicks while it closes. Was a fadeIn keyframe, and the windows vanished on close —
// each is now held mounted through its 0.18 s close by usePresence (see the page below).
const Overlay = styled.div.attrs<{ 'data-reload-block'?: string; $open: boolean }>({ 'data-reload-block': '' })`
  position: fixed;
  inset: 0;
  background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.35)' : 'rgba(20, 30, 25, 0.18)'};
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  ${(p) => fluidScrim(p.$open)}

  /* [PHONE-LAYOUT 2026-10-23] Phones: the window (Add row, Edit records, PIA Renewal, Download) covers the floating
     dock (850), as the Edit Mapping (1000) and Split (1100) windows do: at 100 the dock sat over the end of the long
     Add form, so its Cancel / Save could not be tapped (a tap landed on the dock). Dropdown lists (1200), calendars
     (1300), confirm boxes (1100), tips and toasts still open above it. */
  @media (max-width: 768px) {
    z-index: 1000;
  }
`;

// [FLUID-3 2026-10-12] $land = the window's blocks may rise in now (default: whenever it is open). The PIA
// Renewal window passes a short land window instead, so the two fields "Future Renewal" adds later appear at
// once, as before, rather than rising in late.
const Modal = styled.div<{ $open: boolean; $land?: boolean }>`
  position: relative;
  background: ${(p) => p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(86, 86, 86, 0.42) 0%, rgba(54, 54, 54, 0.55) 100%)'
    : 'linear-gradient(180deg, rgba(255, 255, 255, 0.55) 0%, rgba(230, 233, 231, 0.68) 100%)'};
  backdrop-filter: blur(48px) saturate(180%);
  -webkit-backdrop-filter: blur(48px) saturate(180%);
  border: 1px solid ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.65)'};
  border-radius: 20px;
  padding: 24px;
  width: 380px;
  max-width: calc(90vw / var(--app-zoom, 1)); /* [APP-FIT 2026-10-26] the window in page px */
  isolation: isolate;
  box-shadow:
    0 30px 80px ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.55)' : 'rgba(0, 0, 0, 0.22)'},
    0 8px 24px ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(0, 0, 0, 0.12)'},
    inset 0 1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.55)'},
    inset 0 -1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.04)'};
  /* [FLUID-3 2026-10-12, user] Rises 12px and grows from 0.95 on the spring (readable after ~0.1 s, open by
     0.36 s), closes in 0.18 s; the title, fields and buttons rise in one after another (25 ms apart, all landed
     by ~0.45 s). Replaces the popIn keyframe. */
  ${(p) => fluidDialog({ open: p.$open })}
  ${(p) => (p.$open && p.$land !== false ? fluidCascade('> *', 0, 20, 6) /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 20px;
    background: linear-gradient(180deg, rgba(0,0,0,0) 80%, rgba(0,0,0,0.12) 100%);
    pointer-events: none;
    z-index: 0;
  }

  & > * { position: relative; z-index: 1; }

  /* [PHONE-LAYOUT 2026-10-22] Phones: 8 px from each screen edge, 16 px inside, and never taller than the screen
     (it scrolls inside, so Cancel / Save stay reachable). WideModal and DownloadModal set their own width. */
  @media (max-width: 768px) {
    width: min(380px, calc(100vw - 16px));
    max-width: calc(100vw - 16px);
    max-height: calc(100dvh - 24px);
    overflow-y: auto;
    padding: 18px 16px;

    /* [PHONE-LAYOUT 2026-10-23] The soft bottom shade is one window-height tall and scrolls up with a long form, so
       it drew a false rounded edge across the middle of the Add form. Phones: no shade. */
    &::before {
      display: none;
    }
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgSurface(36)}
  border-color: transparent;
`;

/** [FLUID-3 2026-10-12] A window's content, kept from its last open render while the window plays its close: the
 *  page clears the window's state the moment it closes (the renewal row and its fields, the bulk selection), and
 *  the box should fade out showing what it showed, not half-emptied. The closing window is inert, so nothing in
 *  the kept copy can be clicked or typed into. */
const KeepWhileClosing: React.FC<{ open: boolean; children: React.ReactNode }> = ({ open, children }) => {
  const kept = useRef<React.ReactNode>(children);
  if (open) kept.current = children;
  return <>{kept.current}</>;
};

const ModalTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
`;

const ModalSubtitle = styled.div`
  font-size: 13px;
  color: ${(p) => p.theme.muted};
  margin-bottom: 20px;
`;

const ModalField = styled.div`
  margin-bottom: 16px;
`;

const ModalLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => p.theme.muted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

const ModalInput = styled.input`
  width: 100%;
  height: 38px;
  border: 1px solid ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'};
  border-radius: 10px;
  background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.65)'};
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: ${(p) => p.theme.colorScheme === 'dark' ? '#ffffff' : p.theme.text};
  font-size: 13px;
  font-weight: 500;
  padding: 0 12px;
  font-family: inherit;
  outline: none;
  transition: border-color 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1), background 120ms cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
  box-shadow:
    inset 0 1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.80)'},
    inset 0 -1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.20)' : 'rgba(0,0,0,0.03)'};

  &:hover {
    border-color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.35)' : 'rgba(26,159,125,0.30)'};
  }
  &:focus {
    border-color: ${(p) => p.theme.green600};
    background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.85)'};
    box-shadow:
      0 0 0 3px ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.20)' : 'rgba(26,159,125,0.15)'},
      inset 0 1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.90)'};
  }

  /* Remove number input spinner arrows */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type='number'] {
    -moz-appearance: textfield;
  }

  /* Make the date/month calendar icon visible in dark theme */
  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    filter: ${(p) => (p.theme.colorScheme === 'dark' ? 'invert(1)' : 'none')};
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgSunken}
`;

/* Match ModalInput exactly — same background, border, height, focus ring */
const ModalSelect = styled(GlassSelect)`
  && {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    height: 38px;
    padding: 0 32px 0 12px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.3;
    text-align: left;
    justify-content: flex-start;
    border: 1px solid ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'};
    background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.65)'};
    background-image: none;
    box-shadow:
      inset 0 1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.80)'},
      inset 0 -1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.20)' : 'rgba(0,0,0,0.03)'};
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    color: ${(p) => p.theme.colorScheme === 'dark' ? '#ffffff' : p.theme.text};
    transition: border-color 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1), background 120ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  &&:hover:not(:disabled) {
    border-color: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.35)' : 'rgba(26,159,125,0.30)'};
  }

  &&[aria-expanded="true"],
  &&:focus,
  &&:focus-visible {
    outline: none;
    border-color: ${(p) => p.theme.green600};
    background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.85)'};
    box-shadow:
      0 0 0 3px ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.20)' : 'rgba(26,159,125,0.15)'},
      inset 0 1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.90)'};
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  /* "the dropdown should mach the other text coour what we have": pressed into the glass like the text boxes (ModalInput), the
     same green ring and glow while open */
  && {
    border-color: transparent;
    background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)')};
    box-shadow: ${(p) => (p.theme.colorScheme === 'dark' ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)' : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.07)')};
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
  &&:hover:not(:disabled) { border-color: transparent; }
  &&[aria-expanded="true"],
  &&:focus,
  &&:focus-visible {
    border-color: color-mix(in srgb, ${lgGreen} 70%, transparent);
    background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)')};
    box-shadow: ${(p) => (p.theme.colorScheme === 'dark' ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)' : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.07)')}, 0 0 0 4px color-mix(in srgb, ${lgGreen} 16%, transparent), 0 0 18px color-mix(in srgb, ${lgGreen} 24%, transparent);
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
`;

// [BUTTON-STYLE 2026-09-30] $primary = the green primary, otherwise the secondary (Cancel).
const ModalBtn = styled.button.attrs<LgPressProps>(LG_PRESS)<{ $primary?: boolean }>`
  ${(p) => (p.$primary ? lgButtonPrimary : lgButtonSecondary)}
  height: 32px; padding: 0 14px; /* [LIQUID-GLASS 2026-11-02, user] the glass capsule at the old size */
`;

const RenewalBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: ${(p) => p.theme.muted};
  margin-left: 4px;
`;

const WideModal = styled(Modal)`
  width: 600px;
  max-height: calc(88vh / var(--app-zoom, 1)); /* [APP-FIT 2026-10-26] the window in page px */
  overflow-y: auto;

  /* [PHONE-LAYOUT 2026-10-22] Phones: the full width less 8 px a side, as tall as the screen allows. */
  @media (max-width: 768px) {
    width: min(600px, calc(100vw - 16px));
    max-height: calc(100dvh - 24px);
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 16px;

  /* [RESPONSIVE 2026-06-23] Stack to one column on phones — at 90vw the two
     columns get too narrow for the month picker / labels. FullRow (1 / -1)
     still spans correctly in a single-column grid. */
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FullRow = styled.div`
  grid-column: 1 / -1;
`;

/* ── [INVENTORY-CSV-EXPORT 2026-07-03] Download-record modal ── */

const DownloadModal = styled(Modal)`
  width: 660px;
  max-width: calc(94vw / var(--app-zoom, 1)); /* [APP-FIT 2026-10-26] the window in page px */

  /* [PHONE-LAYOUT 2026-10-22] Phones: the full width less 8 px a side. No inner scroll here (it fits a phone
     screen): a bar's dropdown hangs below its bar and an inner scroll box would clip it. */
  @media (max-width: 768px) {
    width: min(660px, calc(100vw - 16px));
    max-width: calc(100vw - 16px);
    max-height: none;
    overflow: visible;
  }
`;

/* [REDESIGN 2026-07-03] "Export builder": the modal mirrors the file — every
   choice that shapes the CSV is a full-width bar that opens an anchored dropdown
   (filter-menu pattern), with the selection summarised on the bar itself.
   [CSV-COLUMN-PICKER 2026-07-28] The old static "always included" column grid is
   gone: columns are now CHOSEN (Columns bar, "Select all" at the top), and PM Name
   joined Month / Type / Status as a row filter. */

// The filter controls as full-width bars, stacked Columns → Month → Type → PM Name
// → Status. position+z-index lift the stack ABOVE the footer (Modal gives every
// direct child z-index:1, so an open dropdown would otherwise paint under the
// later-DOM footer buttons).
const FilterStack = styled.div`
  position: relative;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// One full-width filter bar: label · current value · chevron, opening a dropdown.
// Glassy interactive fill; Month carries the subtle-rose highlight. When open it
// gets a z-index so its dropdown floats above the bars stacked below it.
const FilterBar = styled.div<{ $open?: boolean; $warn?: boolean; $month?: boolean }>`
  position: relative;
  ${(p) => p.$open && 'z-index: 50;'}
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 46px;
  padding: 0 40px;
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1), background 120ms cubic-bezier(0.4, 0, 0.2, 1);
  background: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(255,255,255,0.09) 0%, rgba(255,255,255,0.05) 100%)'
    : 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.78) 100%)')};
  backdrop-filter: blur(12px) saturate(150%);
  -webkit-backdrop-filter: blur(12px) saturate(150%);
  border: 1px solid ${(p) =>
    p.$warn ? (p.theme.colorScheme === 'dark' ? 'rgba(248,113,113,0.70)' : 'rgba(220,38,38,0.55)')
    : p.$open ? (p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.70)' : 'rgba(26,159,125,0.60)')
    : p.$month ? (p.theme.colorScheme === 'dark' ? 'rgba(248,113,113,0.42)' : 'rgba(225,29,72,0.34)')
    : (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.16)' : 'rgba(22,25,23,0.14)')};
  box-shadow: ${(p) => (p.$open
    ? (p.theme.colorScheme === 'dark' ? '0 8px 22px rgba(61,158,108,0.28)' : '0 8px 22px rgba(26,159,125,0.18)')
    : (p.theme.colorScheme === 'dark' ? '0 2px 8px rgba(0,0,0,0.22)' : '0 2px 8px rgba(22,25,23,0.06)'))};

  &:hover {
    border-color: ${(p) => (p.$warn
      ? (p.theme.colorScheme === 'dark' ? 'rgba(248,113,113,0.85)' : 'rgba(220,38,38,0.70)')
      : (p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.60)' : 'rgba(26,159,125,0.52)'))};
  }

  .flabel {
    flex: none;
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${(p) => (p.$month ? (p.theme.colorScheme === 'dark' ? 'rgba(252,165,165,0.9)' : '#e11d48') : p.theme.muted)};
  }
  .fval {
    flex: none;
    max-width: 62%;
    font-size: 14px;
    font-weight: 800;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${(p) =>
      p.$warn ? (p.theme.colorScheme === 'dark' ? '#f87171' : '#dc2626')
      : p.$month ? (p.theme.colorScheme === 'dark' ? '#fca5a5' : '#e11d48')
      : p.theme.text};
  }
  /* Chevron pinned to the right edge so the label+value can center in the bar. */
  .chevron {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 11px;
    color: ${(p) => p.theme.muted};
  }

  /* [PHONE-LAYOUT 2026-10-22] Phones: the label at 11 px, and a long value (a PM's full name) wraps inside the
     bar instead of ending in an ellipsis. */
  @media (max-width: 768px) {
    padding: 6px 34px;
    .flabel {
      font-size: 11px;
    }
    .fval {
      flex: 0 1 auto;
      min-width: 0;
      max-width: none;
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
      overflow-wrap: anywhere;
      text-align: center;
    }
  }
`;

// Anchored dropdown panel, CENTERED under its filter bar. Uses the app's shared
// "Glass popover" recipe (see SectionSwitcher): a near-solid sheen-topped surface
// — NOT a heavy blur — so text is fully readable and the bars/footer behind never
// bleed through. [FLUID-1 2026-10-12, user] It used to appear in one frame on purpose; now it
// opens like every other dropdown in the app (grows down out of its bar, see components/Fluid.tsx).
// $wide = the Month panel (month-grid needs more room than a value list).
const ColDropdown = styled.div<{ $wide?: boolean; $open: boolean }>`
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  width: ${(p) => (p.$wide ? '340px' : '260px')};
  max-width: calc(100vw / var(--app-zoom, 1) - 40px); /* [APP-FIT 2026-10-26] the window in page px */
  max-height: 340px;
  overflow-y: auto;
  z-index: 40;
  padding: 6px;
  border-radius: 12px;
  cursor: default;
  background:
    linear-gradient(180deg,
      rgba(255, 255, 255, ${(p) => (p.theme.colorScheme === 'dark' ? 0.08 : 0.40)}) 0%,
      rgba(255, 255, 255, 0) 40%),
    ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(38, 38, 38, 0.985)' : 'rgba(250, 251, 253, 0.985)')};
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(22,25,23,0.10)')};
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, ${(p) => (p.theme.colorScheme === 'dark' ? 0.14 : 0.6)}),
    0 16px 40px rgba(15, 23, 42, ${(p) => (p.theme.colorScheme === 'dark' ? 0.55 : 0.18)}),
    0 2px 6px rgba(15, 23, 42, ${(p) => (p.theme.colorScheme === 'dark' ? 0.40 : 0.10)});
  /* [FLUID-1 2026-10-12] Centred under its bar (the translateX base), growing from the top centre;
     the close plays before it unmounts (BarDropdown below). */
  ${(p) => fluidPop({ open: p.$open, base: 'translateX(-50%)', origin: '50% 0', from: 'translateY(-8px) scale(0.92)' })}
  ${(p) => (p.$open ? fluidCascade('> *', 0, 12, 10, 'soft') /* [FLUID-7 2026-10-12] readable at once */ : '')}
`;

/** [FLUID-1 2026-10-12] One bar's dropdown: stays mounted through its 200 ms close so the exit can play. */
const BarDropdown: React.FC<{ open: boolean; wide: boolean; children: React.ReactNode }> = ({ open, wide, children }) => {
  const pres = usePresence(open, fluid.outMs);
  if (!pres.mounted) return null;
  return (
    <ColDropdown $wide={wide} $open={open && pres.entered} onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation() /* [FLUID-7 2026-10-12] a press inside never reaches the bar's toggle */}>
      {children}
    </ColDropdown>
  );
};

// Preset strip pinned at the top of the Month dropdown.
const DropHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 4px 10px;
  margin-bottom: 6px;
  border-bottom: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(22,25,23,0.08)')};
`;

// Quick-select pill ("This month" / "Last 3 months").
// [BUTTON-STYLE 2026-09-30] Now the secondary button style.
const PresetBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonSecondary}
  height: 32px; padding: 0 14px; /* [LIQUID-GLASS 2026-11-02, user] the glass capsule at the old size */
`;

// One selectable value row: label left, ✓ right (visible only when selected —
// hidden, not removed, so rows never shift). Selected rows carry a soft green
// fill + green ink so the current picks read at a glance.
const OptRow = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 8px 11px;
  border: none;
  border-radius: 8px;
  background: ${(p) => (p.$active
    ? (p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.18)' : 'rgba(26,159,125,0.12)')
    : 'transparent')};
  color: ${(p) => (p.$active
    ? (p.theme.colorScheme === 'dark' ? '#7de8ac' : p.theme.green700)
    : p.theme.text)};
  font-family: inherit;
  font-size: 12.5px;
  font-weight: ${(p) => (p.$active ? 700 : 600)};
  text-align: left;
  cursor: pointer;

  i { font-size: 10px; visibility: ${(p) => (p.$active ? 'visible' : 'hidden')}; }

  &:hover {
    background: ${(p) => (p.$active
      ? (p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.26)' : 'rgba(26,159,125,0.18)')
      : (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(22,25,23,0.05)'))};
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  margin: 4px 6px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(22,25,23,0.08)')};
`;

// Month-grid picker pieces: a compact year header + 4-column grid of month
// cells — every month visible at a glance, no scrolling a text list.
const YearBlock = styled.div`
  padding: 2px 4px 8px;
  & + & {
    border-top: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(22,25,23,0.08)')};
    margin-top: 4px;
    padding-top: 10px;
  }
  .yr {
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.6px;
    color: ${(p) => p.theme.muted};
    margin: 0 2px 7px;
  }
  .cells {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
    /* [FLUID-1 2026-10-12] A year's month cells ripple in when it is expanded (and when the
       dropdown opens on it), instead of appearing in one frame. */
    ${fluidCascade('> button', 0, 12)}
  }
`;

/* [YEAR-COLLAPSE 2026-07-30] Header row of a collapsible year group: chevron + year +
   "n of 12" count on the left, a per-year All/Clear on the right. */
const YearHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 0 2px 7px;

  .yrbtn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    flex: 1;
    min-width: 0;
    padding: 2px 0;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    color: ${(p) => p.theme.muted};

    i { font-size: 9px; width: 9px; flex: none; }
    .yr { font-size: 10.5px; font-weight: 800; letter-spacing: 0.6px; }
    .cnt {
      font-size: 10px;
      font-weight: 600;
      opacity: 0.75;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &:hover { color: ${(p) => p.theme.text}; }
  }

  .all {
    flex: none;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 700;
    cursor: pointer;
    color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
    background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.12)' : 'rgba(26,159,125,0.10)')};
    border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.28)' : 'rgba(26,159,125,0.22)')};
    &:hover { filter: brightness(1.1); }
  }
`;

const MonthCell = styled.button<{ $active?: boolean }>`
  height: 32px;
  border-radius: 9px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$active
    ? (p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.55)' : 'rgba(26,159,125,0.45)')
    : (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(22,25,23,0.10)'))};
  background: ${(p) => (p.$active
    ? (p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.20)' : 'rgba(26,159,125,0.12)')
    : 'transparent')};
  color: ${(p) => (p.$active
    ? (p.theme.colorScheme === 'dark' ? '#7de8ac' : p.theme.green700)
    : p.theme.text)};

  &:hover {
    border-color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.50)' : 'rgba(26,159,125,0.40)')};
    background: ${(p) => (p.$active
      ? (p.theme.colorScheme === 'dark' ? 'rgba(70,191,130,0.28)' : 'rgba(26,159,125,0.18)')
      : (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(22,25,23,0.04)'))};
  }
  &:active { transform: translateY(1px); }
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${(p) => (p.$active ? 'background-image: linear-gradient(180deg, rgba(255,255,255,0.30), rgba(255,255,255,0) 60%); box-shadow: inset 0 1px 0 rgba(255,255,255,0.45);' : '')}
`;

const EmptyOpt = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme.muted};
  padding: 2px 0;
`;

// Footer: live result count on the left, actions on the right, separated from
// the pickers by a hairline so the "outcome" zone reads distinctly.
const FooterRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 22px;
  padding-top: 16px;
  border-top: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(22,25,23,0.08)')};

  /* [PHONE-LAYOUT 2026-10-22] Phones: the warning line and the two buttons do not fit one row, so the buttons
     drop under it (right-aligned) instead of squeezing the warning to one word per line. */
  @media (max-width: 768px) {
    flex-wrap: wrap;
    row-gap: 8px;
    & > :last-child {
      margin-left: auto;
    }
  }
`;

const CountText = styled.div<{ $warn?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => (p.$warn ? '#f59e0b' : p.theme.text)};
  i { font-size: 12px; color: ${(p) => (p.$warn ? '#f59e0b' : (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600))}; }
  strong { font-weight: 800; }
`;

/** Multi-select value list inside a column dropdown. With `allLabel`, a leading
 *  "All …" row clears the selection (empty = all); tapping values narrows. */
const OptionList: React.FC<{
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  allLabel?: string;
  formatOption?: (o: string) => string;
}> = ({ options, selected, onChange, allLabel, formatOption }) => {
  const toggle = (o: string) =>
    onChange(selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o]);
  return (
    <>
      {allLabel && options.length > 0 && (
        <>
          <OptRow type="button" $active={selected.length === 0} aria-pressed={selected.length === 0} onClick={() => onChange([])}>
            {allLabel}<i className="fas fa-check" />
          </OptRow>
          <MenuDivider />
        </>
      )}
      {options.map((o) => {
        const on = selected.includes(o);
        return (
          <OptRow key={o} type="button" $active={on} aria-pressed={on} onClick={() => toggle(o)}>
            {formatOption ? formatOption(o) : o}<i className="fas fa-check" />
          </OptRow>
        );
      })}
      {options.length === 0 && <EmptyOpt style={{ padding: '6px 10px' }}>No values for the selected month(s)</EmptyOpt>}
    </>
  );
};

/** [CSV-COLUMN-PICKER 2026-07-28] Column picker inside the Columns dropdown: a
 *  leading "Select all" row (ticked when every column is on — clicking it turns
 *  them ALL on, or clears them when they already are), then one togglable row per
 *  column of the page's table. Selection is held as a set of column KEYS; the CSV
 *  keeps the table's own column ORDER, so click order never reshuffles the file. */
const ColumnList: React.FC<{
  options: { key: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
}> = ({ options, selected, onChange }) => {
  const sel = new Set(selected);
  const allOn = options.length > 0 && selected.length === options.length;
  const toggle = (k: string) =>
    onChange(sel.has(k) ? selected.filter((x) => x !== k) : [...selected, k]);
  return (
    <>
      <OptRow
        type="button"
        $active={allOn}
        aria-pressed={allOn}
        onClick={() => onChange(allOn ? [] : options.map((o) => o.key))}
      >
        Select all<i className="fas fa-check" />
      </OptRow>
      <MenuDivider />
      {options.map((o) => {
        const on = sel.has(o.key);
        return (
          <OptRow key={o.key} type="button" $active={on} aria-pressed={on} onClick={() => toggle(o.key)}>
            {o.label}<i className="fas fa-check" />
          </OptRow>
        );
      })}
      {options.length === 0 && <EmptyOpt style={{ padding: '6px 10px' }}>No columns available</EmptyOpt>}
    </>
  );
};

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Month dropdown body: a real month-picker — one block per year (newest year
 *  first), its available months as a 4-column grid of toggle cells. Everything
 *  is visible at a glance; no scrolling through a text list. */
const MonthPanel: React.FC<{
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}> = ({ options, selected, onChange }) => {
  const byYear = new Map<string, string[]>();
  for (const m of options) {
    const y = m.slice(0, 4);
    if (!byYear.has(y)) byYear.set(y, []);
    byYear.get(y)!.push(m);
  }
  const groups = [...byYear.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([year, ms]) => ({ year, months: [...ms].sort() }));

  // [YEAR-COLLAPSE 2026-07-30] Now that every month back to Apr-2024 is offered (29 and
  // counting), showing them all at once is a wall of cells. Each YEAR is collapsible with
  // its own count + select-all; open years start as: the newest year expanded, plus any
  // year that already has a selection (so a reopened modal shows what is picked). A month
  // stays selected while its year is collapsed — picking across years is the whole point.
  const newest = groups[0]?.year;
  const [openYears, setOpenYears] = useState<string[]>([]);
  const [touched, setTouched] = useState(false);
  const autoOpen = useMemo(() => {
    const withSel = new Set(selected.map((m) => m.slice(0, 4)));
    return groups.filter((g) => g.year === newest || withSel.has(g.year)).map((g) => g.year);
  }, [newest, options.length]);
  const open = touched ? openYears : autoOpen;
  // [FLUID-8 2026-10-12, user] A year opens / closes on the mouse PRESS (the bars above already do); touch / keyboard click.
  const yearPressedRef = useRef(false);
  const yearPress = (y: string) => ({
    onPointerDown: (e: React.PointerEvent) => { yearPressedRef.current = isPlainMousePress(e); if (yearPressedRef.current) toggleYear(y); },
    onClick: (e: React.MouseEvent) => { if (clickEndsPress(yearPressedRef, e)) return; toggleYear(y); },
  });
  const toggleYear = (y: string) => {
    setTouched(true);
    const base = touched ? openYears : autoOpen;
    setOpenYears(base.includes(y) ? base.filter((x) => x !== y) : [...base, y]);
  };

  const toggle = (m: string) =>
    onChange(selected.includes(m) ? selected.filter((x) => x !== m) : [...selected, m]);
  const toggleAllInYear = (months: string[], allOn: boolean) =>
    onChange(allOn ? selected.filter((m) => !months.includes(m))
                   : [...selected, ...months.filter((m) => !selected.includes(m))]);

  return (
    <>
      {groups.map((g) => {
        const picked = g.months.filter((m) => selected.includes(m)).length;
        const allOn = picked === g.months.length && picked > 0;
        const isOpen = open.includes(g.year);
        return (
          <YearBlock key={g.year}>
            <YearHead>
              <button type="button" className="yrbtn" aria-expanded={isOpen} {...yearPress(g.year)}>
                <i className={`fas fa-chevron-${isOpen ? 'down' : 'right'}`} aria-hidden="true" />
                <span className="yr">{g.year}</span>
                <span className="cnt">{picked ? `${picked} of ${g.months.length}` : `${g.months.length} months`}</span>
              </button>
              <button type="button" className="all" onClick={() => toggleAllInYear(g.months, allOn)}>
                {allOn ? 'Clear' : 'All'}
              </button>
            </YearHead>
            {isOpen && (
              <div className="cells">
                {g.months.map((m) => {
                  const on = selected.includes(m);
                  return (
                    <MonthCell key={m} type="button" $active={on} aria-pressed={on} onClick={() => toggle(m)}>
                      {MONTH_ABBR[parseInt(m.slice(5), 10) - 1]}
                    </MonthCell>
                  );
                })}
              </div>
            )}
          </YearBlock>
        );
      })}
      {groups.length === 0 && <EmptyOpt style={{ padding: '6px 10px' }}>No months available</EmptyOpt>}
    </>
  );
};

/* Segmented progress ring for the Download button — 10 wedge segments (like a
   classic loading donut); `pct` fills them clockwise. White-on-green so it reads
   on the primary button. */
const RING_SEGS = 10;
function ringArc(cx: number, cy: number, r: number, a0: number, a1: number): string {
  const rad = (a: number) => ((a - 90) * Math.PI) / 180;
  const x0 = cx + r * Math.cos(rad(a0));
  const y0 = cy + r * Math.sin(rad(a0));
  const x1 = cx + r * Math.cos(rad(a1));
  const y1 = cy + r * Math.sin(rad(a1));
  return `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 0 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
}

const ProgressRing: React.FC<{ pct: number }> = ({ pct }) => {
  const filled = Math.round((pct / 100) * RING_SEGS);
  const segs = [];
  for (let i = 0; i < RING_SEGS; i++) {
    const start = i * 36 + 5; // 5° gap on each side → distinct wedges
    const end = (i + 1) * 36 - 5;
    segs.push(
      <path
        key={i}
        d={ringArc(9, 9, 6.5, start, end)}
        stroke={i < filled ? '#ffffff' : 'rgba(255,255,255,0.30)'}
        strokeWidth={2.8}
        fill="none"
      />
    );
  }
  return <svg width={18} height={18} viewBox="0 0 18 18" aria-hidden style={{ flex: 'none' }}>{segs}</svg>;
};

/** [INVENTORY-CSV-EXPORT 2026-07-03, perf-refactor] Self-contained download modal.
 *  It is its OWN component with LOCAL state so toggling a value / opening a
 *  dropdown re-renders only this small subtree — NOT the 2000-line InventoryPage
 *  and its DataTable (that whole-page re-render was the click lag). Rows are
 *  fetched PER MONTH and cached; Type/Status then filter that set CLIENT-SIDE, so
 *  the count + every selection update instantly with no server round-trip except a
 *  month's first load. `monthOptions` = the full month list (from the toolbar). */
// The download modal's five bars: which COLUMNS the file carries, and the four
// filters that decide which ROWS it carries.
type FilterKey = 'columns' | 'month' | 'type' | 'pm' | 'status';

// A row whose PM is blank — offered as its own pick in the PM Name dropdown so
// unassigned inventory can be exported (or excluded) deliberately.
const UNASSIGNED_PM = '(Unassigned)';

// [EXPORT-DEBOUNCE 2026-07-30] Collapse a burst of month ticks into ONE rows request.
// Long enough to absorb rapid clicking, short enough to feel instant on a single pick.
const FETCH_DEBOUNCE_MS = 300;

const DownloadRecordsModal: React.FC<{
  monthOptions: string[];
  /** The page's OWN grid columns — Inventory's full set, My Inventory's narrower
   *  one — which are exactly the columns offered in the Columns picker. */
  gridColumns: ColDef[];
  /** [MYINV-EXPORT-RECURRING 2026-09-29] Starting Type selection; unset = all types. */
  defaultTypes?: string[];
  /** [FLUID-3 2026-10-12] True while the page holds the window mounted for its close (usePresence). */
  closing?: boolean;
  onClose: () => void;
}> = ({ monthOptions, gridColumns, defaultTypes, closing = false, onClose }) => {
  // [FLUID-3 2026-10-12] Open one frame after mount (so the spring has a closed state to start from) until the
  // page starts the close.
  const shown = usePresence(!closing, fluid.dialogOutMs).entered;
  const [months, setMonths] = useState<string[]>([getCurrentMonth()]);
  const [types, setTypes] = useState<string[]>(() => defaultTypes ?? []);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [pms, setPms] = useState<string[]>([]);
  const [openCol, setOpenCol] = useState<FilterKey | null>(null);
  const barPressedRef = useRef(false); // [FLUID-7 2026-10-12] the mouse press already opened / closed a bar's list

  // [CSV-COLUMN-PICKER 2026-07-28] Every column of the table this modal was opened
  // from is exportable, and ALL of them are ticked by default (the old file's fixed
  // 10 columns are now just the default-on set of a fuller list).
  const colOptions = useMemo(
    () => gridColumns.map((c) => ({ key: c.key, label: c.label })),
    [gridColumns],
  );
  const [pickedCols, setPickedCols] = useState<string[]>(() => colOptions.map((c) => c.key));

  // [ALL-MONTHS 2026-07-30] The toolbar's month list is floored at Jan-2026 (MIN_DROPDOWN_MONTH),
  // which meant history could never be exported even though inventory holds 29 months back to
  // Apr-2024. The picker therefore asks the server for the UNFLOORED list. `monthOptions` (the
  // toolbar's) seeds it so the dropdown is never momentarily empty, and the full list replaces
  // it as soon as it lands. Everything downstream already handles old months — the list query
  // has no floor and pre-2026 payments exist to fill the computed columns.
  const [allMonthOptions, setAllMonthOptions] = useState<string[]>(monthOptions);
  useEffect(() => {
    let alive = true;
    fetchInventoryFilters(undefined, { allMonths: true })
      .then((f) => { if (alive && f.months?.length) setAllMonthOptions(f.months); })
      .catch(() => { /* keep the seeded (floored) list — the picker still works */ });
    return () => { alive = false; };
  }, []);
  const [monthRows, setMonthRows] = useState<InventoryRow[]>([]);
  const [loadingRows, setLoadingRows] = useState(false);
  // 0–100 while the download ring is running on the button; null = idle.
  const [progress, setProgress] = useState<number | null>(null);
  const rafRef = useRef<number | null>(null);
  // Per-month row cache so removing/re-adding a month is instant (no refetch).
  const cacheRef = useRef<Map<string, InventoryRow[]>>(new Map());

  // [EXPORT-INTEGRITY 2026-07-30] Months whose fetch did NOT succeed. Non-empty means the
  // rows on screen are INCOMPLETE, so the file must not be written. Previously the .catch
  // silently fell back to whatever happened to be cached and the download carried on: a
  // failed 7-month request left only the default month cached, so the user got a
  // July-only file WITH a "Downloaded 31 records" success toast. A failed export must
  // fail loudly, never hand over a quietly wrong spreadsheet.
  const [failedMonths, setFailedMonths] = useState<string[]>([]);

  // Fetch rows for any newly-selected months (ONE request for all missing ones,
  // split into the per-month cache), then assemble monthRows from the cache.
  // Removing a month is pure recompute — no fetch. Server enforces PM scope.
  useEffect(() => {
    let alive = true;
    const cache = cacheRef.current;
    const missing = months.filter((m) => !cache.has(m));
    if (missing.length === 0) {
      setFailedMonths([]);
      setMonthRows(months.flatMap((m) => cache.get(m) || []));
      return;
    }
    setLoadingRows(true);
    // [EXPORT-DEBOUNCE 2026-07-30] Ticking N months in the picker re-ran this effect N
    // times, firing N overlapping "every row for these months" requests of which only the
    // last was ever used — six ticks meant six full-table queries racing on one DB. The
    // debounce collapses a burst of ticks into ONE request; the cleanup cancels the timer
    // so a superseded selection never reaches the network at all.
    const timer = window.setTimeout(() => {
      fetchInventory({ page: 1, pageSize: 0, month: missing })
        .then((res) => {
          if (!alive) return;
          const byMonth = new Map<string, InventoryRow[]>();
          for (const r of res.data) {
            const ym = String(r.month || '').slice(0, 7);
            if (!byMonth.has(ym)) byMonth.set(ym, []);
            byMonth.get(ym)!.push(r);
          }
          missing.forEach((m) => cache.set(m, byMonth.get(m) || [])); // cache empty months too
          setFailedMonths([]);
          setMonthRows(months.flatMap((m) => cache.get(m) || []));
        })
        .catch(() => {
          if (!alive) return;
          // Deliberately do NOT cache `missing` — leaving those months uncached means the
          // next attempt refetches them instead of trusting a half-loaded cache.
          setFailedMonths(missing);
          setMonthRows(months.flatMap((m) => cache.get(m) || []));
          toastError(
            `Could not load ${missing.length} month${missing.length === 1 ? '' : 's'} — nothing was downloaded. Please try again.`,
          );
        })
        .finally(() => { if (alive) setLoadingRows(false); });
    }, FETCH_DEBOUNCE_MS);
    return () => { alive = false; window.clearTimeout(timer); };
  }, [months]);

  // Type/Status OPTIONS come from the authoritative /filters endpoint
  // (SELECT DISTINCT type/status WHERE month IN (...)) — the same source the
  // toolbar dropdowns use — so EVERY value that exists in the selected month(s)
  // shows, however many there are. (Deriving from the fetched rows under-reported
  // values.) Re-fetched when the month set changes; the count/download still use
  // the client-side row filter below, so toggling a value stays instant.
  const [typeOptions, setTypeOptions] = useState<string[]>([]);
  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  useEffect(() => {
    if (months.length === 0) { setTypeOptions([]); setStatusOptions([]); return; }
    let alive = true;
    fetchInventoryFilters(months)
      .then((f) => { if (alive) { setTypeOptions(f.types); setStatusOptions(f.statuses); } })
      .catch(() => {});
    return () => { alive = false; };
  }, [months]);

  // [CSV-PM-FILTER 2026-07-28] PM options come from the FETCHED ROWS, not /filters:
  // the rows are already PM-scoped server-side, so this offers exactly the people
  // whose inventory this viewer can export (and, unlike the roster endpoint, it
  // still lists a PM who has since left HubSpot but owns rows this month). Rows
  // with no PM are offered as one "(Unassigned)" pick.
  const pmOptions = useMemo(() => {
    const set = new Set<string>();
    let hasBlank = false;
    for (const r of monthRows) {
      const v = (r.pm || '').trim();
      if (v) set.add(v); else hasBlank = true;
    }
    const list = [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return hasBlank ? [...list, UNASSIGNED_PM] : list;
  }, [monthRows]);

  // Drop any picked Type/Status/PM that no longer exists after a month change
  // (return the same ref when nothing changes so we don't force a needless render).
  useEffect(() => {
    // [MYINV-EXPORT-RECURRING 2026-09-29] Not while the options are still loading (empty on mount):
    // pruning against an empty list wiped the popup's starting Type (My Inventory's "Recurring").
    setTypes((prev) => { if (!typeOptions.length) return prev; const n = prev.filter((t) => typeOptions.includes(t)); return n.length === prev.length ? prev : n; });
    setStatuses((prev) => { const n = prev.filter((s) => statusOptions.includes(s)); return n.length === prev.length ? prev : n; });
  }, [typeOptions, statusOptions]);
  useEffect(() => {
    setPms((prev) => { const n = prev.filter((p) => pmOptions.includes(p)); return n.length === prev.length ? prev : n; });
  }, [pmOptions]);

  // Client-side filter → instant count + the exact rows the file will contain.
  const filteredRows = useMemo(() => {
    const tset = new Set(types);
    const sset = new Set(statuses);
    const pset = new Set(pms);
    return monthRows.filter((r) =>
      (tset.size === 0 || tset.has((r.type || '').trim())) &&
      (sset.size === 0 || sset.has((r.status || '').trim())) &&
      (pset.size === 0 || pset.has((r.pm || '').trim() || UNASSIGNED_PM))
    );
  }, [monthRows, types, statuses, pms]);

  // The chosen columns, kept in the TABLE's order (not the order they were ticked).
  const csvCols = useMemo(
    () => colOptions.filter((c) => pickedCols.includes(c.key)),
    [colOptions, pickedCols],
  );

  const noMonth = months.length === 0;
  const noCols = pickedCols.length === 0;

  // Live refs so the download animation loop reads the CURRENT rows/columns/
  // loading state each frame — not the values captured when the click happened.
  const filteredRowsRef = useRef(filteredRows);
  filteredRowsRef.current = filteredRows;
  const csvColsRef = useRef(csvCols);
  csvColsRef.current = csvCols;
  const loadingRowsRef = useRef(loadingRows);
  loadingRowsRef.current = loadingRows;
  // (monthsRef removed 2026-07-30 — it existed only to name the file after the selected
  // months; the file is now always "Inventory Data.csv".)
  // [EXPORT-INTEGRITY 2026-07-30] Read live by the animation loop: a fetch can fail WHILE
  // the ring is running, and that must abort the save rather than write a partial file.
  const failedMonthsRef = useRef(failedMonths);
  failedMonthsRef.current = failedMonths;

  useEffect(() => () => { if (rafRef.current != null) cancelAnimationFrame(rafRef.current); }, []);

  const selectLast3Months = () => {
    const [y, m] = getCurrentMonth().split('-').map(Number);
    const wanted: string[] = [];
    for (let i = 0; i < 3; i++) {
      const d = new Date(y, m - 1 - i, 1);
      wanted.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    const avail = new Set(allMonthOptions);
    const picked = wanted.filter((x) => avail.has(x));
    setMonths(picked.length ? picked : [wanted[0]]);
  };

  // Click → the button's ring tracks the REAL work, not a fixed timer. An exact
  // percentage isn't knowable (one gzipped API response, no Content-Length; DB
  // time unpredictable), so we use the standard determinate-feel pattern:
  //   • while the rows fetch is genuinely in flight → trickle asymptotically
  //     toward ~92% (fast at first, easing off — always moving, never parked);
  //   • the moment the data lands → sprint to 100% (~450ms) and save the file.
  // Rows are usually PREFETCHED when the months were picked, so the common case
  // is a single quick 1→100 sweep with no waiting phase at all.
  const startDownload = () => {
    if (progress != null || noMonth || noCols) return;
    // [EXPORT-INTEGRITY 2026-07-30] Refuse to build a file from an incomplete row set.
    if (failedMonths.length) {
      toastError(`${failedMonths.join(', ')} failed to load — reselect the months to retry.`);
      return;
    }
    setProgress(1);
    let p = 1;
    let last = performance.now();
    const TRICKLE_TARGET = 92;   // ceiling while the fetch is running
    const TRICKLE_TAU = 700;     // ms "time constant" — how fast it eases toward the ceiling
    const FINISH_RATE = 100 / 450; // %/ms once the data is ready (~450ms full sweep)
    const tick = () => {
      const now = performance.now();
      const dt = Math.min(100, now - last);
      last = now;
      if (loadingRowsRef.current) {
        p = Math.min(TRICKLE_TARGET, p + ((TRICKLE_TARGET - p) * dt) / TRICKLE_TAU);
      } else {
        p += dt * FINISH_RATE;
        if (p >= 100) {
          setProgress(100);
          // Brief beat at 100% so completion registers, then save the file.
          setTimeout(() => {
            const rows = filteredRowsRef.current;
            // [EXPORT-INTEGRITY 2026-07-30] Re-checked HERE too: the fetch may have failed
            // after the click, while the ring was running. Writing the file at that point
            // is exactly how the July-only export happened.
            if (failedMonthsRef.current.length) {
              toastError(
                `${failedMonthsRef.current.join(', ')} failed to load — nothing was downloaded. Please try again.`,
              );
            } else if (rows.length === 0) {
              toastError('No records match your selection');
            } else {
              // `sortValue` resolves the five COMPUTED columns (Sales Pending
              // Payment, At-Risk Expected, Unmapped Payments, Pending Payment,
              // Delayed Days) exactly as the grid shows them; every other key
              // falls through to the raw field.
              const book = buildInventoryXlsx(rows, csvColsRef.current, sortValue);
              downloadXlsx(inventoryExportFilename(), book);
              toastSuccess(`Downloaded ${rows.length.toLocaleString()} record${rows.length === 1 ? '' : 's'}`);
              onClose();
            }
            setProgress(null);
          }, 150);
          return;
        }
      }
      setProgress(Math.floor(p));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const summaryFor = (key: FilterKey) => {
    switch (key) {
      case 'columns':
        return noCols ? 'Select…'
          : pickedCols.length === colOptions.length ? `All ${colOptions.length} columns`
            : `${pickedCols.length} of ${colOptions.length}`;
      case 'month':
        return noMonth ? 'Select…' : months.length === 1 ? formatMonthLabel(months[0]) : `${months.length} months`;
      case 'type':
        return types.length === 0 ? 'All' : types.length === 1 ? types[0] : `${types.length} types`;
      case 'pm':
        return pms.length === 0 ? 'All' : pms.length === 1 ? pms[0] : `${pms.length} PMs`;
      default:
        return statuses.length === 0 ? 'All' : statuses.length === 1 ? statuses[0] : `${statuses.length} statuses`;
    }
  };

  // [FLUID-3 2026-10-12] Inert while it closes: nothing in it may be used during the 0.18 s exit.
  return createPortal(
    <Overlay onClick={() => { if (progress == null) onClose(); }} $open={shown} inert={closing}>
      {/* Click on the modal body (not a card/dropdown) closes any open dropdown. */}
      <DownloadModal onClick={(e) => { e.stopPropagation(); setOpenCol(null); }} $open={shown}>
        <ModalTitle>Download Records</ModalTitle>
        <ModalSubtitle>
          Open <strong>Columns</strong> to choose what the file carries (<strong>Select all</strong> ticks every
          column), and <strong>Month</strong>, <strong>Type</strong>, <strong>PM Name</strong> or <strong>Status</strong> to
          choose which rows to export.
        </ModalSubtitle>

        {/* The 5 bars, stacked Columns → Month → Type → PM Name → Status. */}
        <FilterStack>
          {(['columns', 'month', 'type', 'pm', 'status'] as const).map((key) => {
            const open = openCol === key;
            const isMonth = key === 'month';
            const isColumns = key === 'columns';
            const warn = (isMonth && noMonth) || (isColumns && noCols);
            const label = isColumns ? 'Columns'
              : isMonth ? 'Month'
                : key === 'type' ? 'Type'
                  : key === 'pm' ? 'PM Name' : 'Status';
            return (
              <FilterBar
                key={key}
                $open={open}
                $warn={warn}
                $month={isMonth}
                // [FLUID-7 2026-10-12, user] Opens on the mouse PRESS (no wait for the release); touch / keyboard keep the click.
                onPointerDown={(e) => {
                  barPressedRef.current = isPlainMousePress(e); // [FLUID-8 2026-10-12] the shared press rule
                  if (barPressedRef.current) setOpenCol(open ? null : key);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (clickEndsPress(barPressedRef, e)) return; // the press already opened / closed it
                  setOpenCol(open ? null : key);
                }}
              >
                <span className="flabel">{label}</span>
                <span className="fval">{summaryFor(key)}</span>
                <i className={`chevron fas fa-chevron-${open ? 'up' : 'down'}`} />
                {/* Wide panel for the month grid AND the column list (long labels
                    like "At-Risk Expected Payment" need the room). */}
                <BarDropdown open={open} wide={isMonth || isColumns}>
                    {isMonth && (
                      <>
                        <DropHead>
                          <PresetBtn type="button" onClick={() => setMonths([getCurrentMonth()])}>This month</PresetBtn>
                          <PresetBtn type="button" onClick={selectLast3Months}>Last 3 months</PresetBtn>
                        </DropHead>
                        {/* Pin any picked month missing from the fetched list (e.g. the
                            current month right at rollover) so it stays visible. */}
                        <MonthPanel
                          options={[...months.filter((m) => !allMonthOptions.includes(m)), ...allMonthOptions]}
                          selected={months}
                          onChange={setMonths}
                        />
                      </>
                    )}
                    {isColumns && <ColumnList options={colOptions} selected={pickedCols} onChange={setPickedCols} />}
                    {key === 'type' && <OptionList options={typeOptions} selected={types} onChange={setTypes} allLabel="All types" />}
                    {key === 'pm' && <OptionList options={pmOptions} selected={pms} onChange={setPms} allLabel="All PMs" />}
                    {key === 'status' && <OptionList options={statusOptions} selected={statuses} onChange={setStatuses} allLabel="All statuses" />}
                </BarDropdown>
              </FilterBar>
            );
          })}
        </FilterStack>

        <FooterRow>
          {/* No count/processing line — download progress lives ON the button. The
              left slot only ever shows a blocking warning (no month / no column). */}
          <CountText $warn style={{ visibility: noMonth || noCols ? 'visible' : 'hidden' }}>
            <i className="fas fa-circle-exclamation" /> {noMonth ? 'Select at least one month' : 'Select at least one column'}
          </CountText>
          <ModalActions style={{ marginTop: 0 }}>
            <ModalBtn onClick={onClose} disabled={progress != null}>Cancel</ModalBtn>
            <ModalBtn
              $primary
              onClick={startDownload}
              disabled={noMonth || noCols}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, minWidth: 172 }}
            >
              {progress != null ? (<><ProgressRing pct={progress} /><span>{progress}%</span></>) : 'Download Records'}
            </ModalBtn>
          </ModalActions>
        </FooterRow>
      </DownloadModal>
    </Overlay>,
    document.body
  );
};

/* ── Helpers ── */

// [EFFECTIVE-MONTH 2026-07-01] The app-wide "effective current month" (YYYY-MM) — rolls
// over at 12:30 PM IST on the 1st (not midnight), evaluated in IST. Delegates to the
// shared util so the page and the idle prefetch compute the exact same month (cache-key
// alignment). Re-exported here since MyInventoryPage and this file import it from here.
export function getCurrentMonth(): string {
  return effectiveCurrentMonth();
}

/**
 * [INV-OVERRIDE 2026-08-04] The month BEFORE the effective current month ('YYYY-MM') —
 * the only month whose calculated columns an admin may override. Derived from
 * getCurrentMonth so it rolls over on the same 12:30-IST boundary as everything else,
 * and needs no extra round trip to ask the server where the boundary sits.
 */
export function getLastMonth(): string {
  const [y, m] = getCurrentMonth().split('-').map(Number);
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * [PERF 2026-08-14] Same trap as MONEY_FMT below: `toLocaleDateString(locale, options)` builds a
 * fresh Intl.DateTimeFormat per call. The `month` column runs through formatMonthLabel for every
 * row, so it is another ~306 constructions per table build. Smaller than the money columns, but
 * free to fix and on the identical hot path. Verified byte-identical on 7 inputs including the
 * out-of-range rollovers the callers rely on ('2026-13' -> Jan 2027, '2026-00' -> Dec 2025).
 */
const MONTH_SHORT_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' });
const MONTH_LONG_FMT = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });

function formatMonthLabel(m: string): string {
  const [year, month] = m.split('-');
  return MONTH_SHORT_FMT.format(new Date(parseInt(year), parseInt(month) - 1));
}

// [MONTH-FULL-NAME] 2026-06-23 — the month *dropdown* shows the full month name
// (e.g. "June 2026") to match the EOM page. The short form (formatMonthLabel) is
// kept for the dense table month column and the KPI bar context where width matters.
function formatMonthFull(m: string): string {
  const [year, month] = m.split('-');
  return MONTH_LONG_FMT.format(new Date(parseInt(year), parseInt(month) - 1));
}

// [KPI-TOOLTIP 2026-06-24] The calendar month AFTER `m` (YYYY-MM), formatted like
// formatMonthLabel — used by the "Next Month Inventory" KPI tooltip so it names the
// actual upcoming month relative to the one the user picked, not the literal word.
function formatNextMonthLabel(m: string): string {
  const [year, month] = m.split('-').map(Number);
  // `month` is 1-based, so this IS the next month.
  return MONTH_SHORT_FMT.format(new Date(year, month, 1));
}

/**
 * [PERF 2026-08-14] Built ONCE. `Number.prototype.toLocaleString(locale, options)` constructs a
 * fresh Intl.NumberFormat on EVERY call, and this is the hottest function on the page.
 *
 * MEASURED (Node/V8, same engine as Chrome, at the real Aug-2026 row counts): renderCell routes
 * 11 of My Inventory's 21 columns and 12 of Inventory's 26 through formatMoney, so one table
 * build is 306 x 11 = 3,366 calls = **84 ms**, and Inventory's 3,672 calls = **91 ms**. With the
 * formatter hoisted the same work is **1.9 ms** - 44x faster.
 *
 * That cost was paid on every table build, NOT just on mount: the memoised <tbody> rebuilds on
 * any data/filter/sort/selection change, and useRealtime('inventory') silently reloads whenever
 * a background job touches the table. So it was ~84 ms of blocked main thread over and over
 * while the tab sat open - which is why the page felt laggy rather than merely slow to arrive.
 *
 * Output is byte-identical: verified across 32 value shapes (null, undefined, NaN, +/-Infinity,
 * negative zero, numeric strings, '', booleans, arrays, MAX_SAFE_INTEGER, 0.1+0.2, 1e21) - all
 * 32 match the previous implementation exactly, including the "$NaN" and "$∞" cases.
 */
const MONEY_FMT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function formatMoney(val: number | null): string {
  if (val == null) return '—';
  return `$${MONEY_FMT.format(Number(val))}`;
}

/* [INV-PM-RANK 2026-09-21, user] One ranked PM as the PM Name dropdown prints it — word for word
   the PM Recommendations dropdown: "1. Name · 20 campaigns · $15,230 (Sep)", and for a
   ROW sale "Name · ROW rule · 19 campaigns · $8,120 (Sep)". The count and $ are that PM's book in
   the ROW'S OWN month (the user: "if it is seen in august the count and amount will be for
   august"). Whole dollars, as on that tab. Only runs when a PM dropdown opens.
   [PM-DD-NO-NEWPAIR 2026-10-01, user] One difference: that tab ends a never-tried rep+PM pair with
   " · new pair"; this dropdown (Inventory + My Inventory) no longer does — the user: remove "new
   pair" from the PM dropdown values. c.explore still arrives from the server, it is just not
   printed here. Rank, order, name, campaigns, $ and month are unchanged; the PM Recommendations
   tab's own label is NOT changed. */
const PM_RANK_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function pmRankLabel(c: InventoryPmOption, rule: 'ROW' | 'RANKED', rowMonth: string): string {
  const book = `${c.count} ${c.count === 1 ? 'campaign' : 'campaigns'} · $${Math.round(c.usd).toLocaleString('en-US')}`
    + ` (${PM_RANK_MONTHS[Number(rowMonth.split('-')[1]) - 1] || rowMonth})`;
  return rule === 'ROW'
    ? `${c.pm} · ROW rule · ${book}`
    : `${c.rank}. ${c.pm} · ${book}`; // [PM-DD-NO-NEWPAIR 2026-10-01] no " · new pair" ending any more
}

// ── Derived (computed) column values — shared by the cell renderer AND the
// client-side sort comparator, so each column sorts by exactly what it shows. ──
const num = (v: any) => Number(v) || 0;

// Status comparison must be space- AND case-insensitive: the inventory table
// holds two spellings of the same status ("At - Risk" from the reconciler/CR
// sheet, "At-Risk" from the old UI dropdown). Comparing the raw string made the
// two look like different statuses. normStatus collapses them; statusEq matches.
const normStatus = (v: any) => String(v ?? '').replace(/\s+/g, '').toLowerCase();
const statusEq = (a: any, b: any) => normStatus(a) === normStatus(b);

// Sales Pending Payment = SUM of recurring new sale from the payments table (matched by website + month).
const computeSalesPending = (row: any): number => num(row.sales_pending_payment);

// At-Risk Expected Payment = a PM-entered override when set; otherwise the
// Amount, but only when Status is exactly 'At-Risk Expected'. The override is a
// real stored column (at_risk_expected_payment); NULL means "use the auto figure".
const computeAtRiskExpected = (row: any): number => {
  if (row.at_risk_expected_payment != null && row.at_risk_expected_payment !== '')
    return num(row.at_risk_expected_payment);
  return statusEq(row.status, 'At - Risk Expected') ? num(row.amount) : 0;
};

// [UNMAPPED 2026-06-25] Unmapped Payments = Total Payment Received minus every column that
// already accounts for received money: Refunded/Disputes + Retention + Recurring Upsell +
// One-Time Upsell + Sales Pending Payment + Recovery + Discount + PIA Amount. Whatever is
// left over — the part of the received money not explained by any of those — is the unmapped
// balance. Only the positive remainder is shown; else 0. (e.g. received $1,000 with $500 in
// Refunded/Disputes and $250 Retention → $250 unmapped.)
const computeUnmapped = (row: any): number => {
  // [INV-OVERRIDE 2026-08-04] An admin override is the last word. Checked FIRST so the
  // typed figure shows verbatim instead of being re-derived from the parts below.
  if (row.unmapped_payments_ovr != null && row.unmapped_payments_ovr !== '') return num(row.unmapped_payments_ovr);
  const received = num(row.total_payment_received);
  const accounted =
    num(row.refunded_disputed)
    + num(row.recurring_retention)
    + num(row.recurring_so)        // Recurring Upsell
    + num(row.one_time_so)         // One-Time Upsell
    + num(row.sales_pending_payment)
    + num(row.recovery)
    + num(row.discount)
    + num(row.pia_amount);
  const diff = received - accounted;
  return diff > 0.01 ? diff : 0;
};

// [MYINV-ROW-TINT 2026-06-25] My-Inventory-only tint that colours the row's VALUES (the
// main Inventory tab does NOT use this — it keeps its own missing-PM red row-background):
//   RED (outranks amber) — a CURRENT-month row that still carries an Unmapped Payments
//           balance (received money not accounted for by any of the Refund/Dispute,
//           Retention, Upsell, Sales-Pending, Recovery, Discount or PIA columns).
//   AMBER — a CURRENT-month row with partial Retention (0 < Retention < Amount) whose Amount
//           is ALSO not covered by Retention + Discount (i.e. Retention + Discount ≠ Amount),
//           so a discount that closes the remaining gap clears the tint.
// The current-month conditions auto-shift as the calendar advances (June → July → …)
// since they compare each row's own month to getCurrentMonth(). Red outranks amber.
function myInventoryRowTint(row: any): 'red' | 'amber' | undefined {
  const isCurrentMonth = String(row.month || '').slice(0, 7) === getCurrentMonth();
  const amount = num(row.amount);
  const retention = num(row.recurring_retention);
  const discount = num(row.discount);
  // computeUnmapped nets out every column that accounts for received money (Refund/Dispute,
  // Retention, Upsells, Sales-Pending, Recovery, Discount, PIA), so unmapped > 0 means the
  // row still has received money sitting in the Unmapped Payments column.
  const unmapped = computeUnmapped(row);
  // RED (outranks amber) — a current-month row that still carries an Unmapped Payments balance.
  if (isCurrentMonth && unmapped > 0) return 'red';
  // AMBER — a current-month row with PARTIAL retention (0 < Retention < Amount) whose Amount is
  // ALSO not yet covered by Retention + Discount. Reuses the same 0.01-epsilon "uncovered" test
  // as the Delayed Days calc, so a discount that closes the gap (Retention + Discount = Amount)
  // clears the tint.
  const uncoveredByRetentionAndDiscount = Math.abs(amount - (retention + discount)) > 0.01;
  if (isCurrentMonth && retention > 0 && retention < amount && uncoveredByRetentionAndDiscount) return 'amber';
  return undefined;
}

// Pending Payment = Amount − Total Payment Received; only the positive shortfall, else 0.
const computePendingPayment = (row: any): number => {
  // [INV-OVERRIDE 2026-08-04] see computeUnmapped. NOTE the column key here is
  // `pending_good`, but the override lives in `pending_payment_ovr`.
  if (row.pending_payment_ovr != null && row.pending_payment_ovr !== '') return num(row.pending_payment_ovr);
  const shortfall = num(row.amount) - num(row.total_payment_received);
  return shortfall > 0.01 ? shortfall : 0;
};

// Delayed Days = today's day-of-month − billing day, only for current-month 'Expected'
// rows whose Amount isn't covered by Retention + Discount; never negative, else 0.
const computeDelayedDays = (row: any): number => {
  // [INV-OVERRIDE 2026-08-04] see computeUnmapped.
  if (row.delayed_days_ovr != null && row.delayed_days_ovr !== '') return num(row.delayed_days_ovr);
  const now = new Date();
  // [EFFECTIVE-MONTH 2026-07-01] current-month test uses the app-wide effective month
  // (12:30 PM IST on the 1st rollover) so it stays in step with editing/labels.
  const isCurrentMonth = String(row.month || '').slice(0, 7) === getCurrentMonth();
  const isExpected = statusEq(row.status, 'Expected');
  const uncovered = Math.abs(num(row.amount) - (num(row.recurring_retention) + num(row.discount))) > 0.01;
  const billingDay = num(row.billing_date);
  if (isCurrentMonth && isExpected && uncovered && billingDay > 0) {
    const delayed = now.getDate() - billingDay;
    return delayed > 0 ? delayed : 0;
  }
  return 0;
};

// Value used to sort a column: the computed value for derived columns, else the raw field.
function sortValue(row: any, key: string): any {
  switch (key) {
    case 'sales_pending_payment': return computeSalesPending(row);
    case 'at_risk_expected_payment': return computeAtRiskExpected(row);
    case 'unmapped_payments': return computeUnmapped(row);
    case 'pending_good': return computePendingPayment(row);
    case 'delayed_days': return computeDelayedDays(row);
    default: return row[key];
  }
}

/* ── Column config ── */

export interface ColDef {
  key: string;
  /** [INV-OVERRIDE 2026-08-04] Set on the CALCULATED columns: the DB column holding an
   *  admin's manual override for this cell. Its presence is what makes the cell editable
   *  (admins, last month only) and is the key we read the editor's initial value from and
   *  write the save to — never `key`, which is the computed figure.
   *  NOTE: for 'Pending Payment' the column key is `pending_good` while the override is
   *  `pending_payment_ovr` — the two names differ on purpose, so always go via this. */
  overrideKey?: string;
  label: string;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  width?: string;
  editable?: boolean;
  type?: 'text' | 'number' | 'select';
  options?: string[];
}

const TYPE_OPTIONS = ['Recurring', 'One-Time', 'PIA'];
const PAY_TYPE_OPTIONS = ['Subscription', 'Manual'];
// Canonical spelling uses spaces around the hyphen ("At - Risk", "At - Risk
// Expected") — that's what the reconciler writes and what ~all existing rows
// hold. The old no-space option ("At-Risk") let the UI write a SECOND spelling
// that exact-match status queries (e.g. Team Performance's Cancelled bucket)
// silently missed, inflating the residual. All comparisons are space/case-
// insensitive (statusEq / normStatus), but new edits now write the canonical form.
const STATUS_OPTIONS = ['Expected', 'At - Risk', 'Retained', 'Cancelled', 'At - Risk Expected'];
const RENEWAL_STATUS_OPTIONS = ['Live', 'No Renewal', 'Future Renewal', 'Renewed'];

// Read-only money columns — displayed, not hand-edited here. total_payment_received
// and refunded_disputed are computed live from the payments table (by website+month).
const MONEY_DISPLAY_COLS = new Set([
  'total_payment_received', 'refunded_disputed', 'recurring_retention',
  'recurring_so', 'one_time_so', 'recovery', 'pia_amount', 'discount',
]);

// [BULK-EDIT 2026-08-03] Columns a multi-row edit may set. MUST stay in sync with
// BULK_EDITABLE in server/src/routes/inventory.ts (the server is the real gate; this
// list only decides what the picker offers).
//
// Excluded on purpose:
//   • website / email / company_record_id — per-row identity. One value across many
//     rows is meaningless, and `website` carries a UNIQUE (website, month) key plus
//     the join to payments/deals.
//   • month — a month change is a row MOVE (merge + delete + exclusion marker), not a
//     column write. It needs its own action, not this one.
// The effective list is narrowed per view in two steps: a column is offered only when it
// is `editable` in THIS page's column set, AND (when the view passes `bulkEditKeys`) only
// when it appears in that list. My Inventory pins it to PM Name alone — see the prop.
const BULK_SAFE_KEYS = new Set([
  // [PAYMENT-MODE 2026-10-18] 'subscription_status' removed — it fills itself from the payments now.
  'type', 'pm', 'brand', 'status', 'payment_type',
  'billing_date', 'num_campaigns', 'amount', 'discount', 'at_risk_expected_payment',
  // [AM-NAMES 2026-09-04, user] Bulk-assign an AM across a selection. ONE name here, by the
  // user's ruling — so a bulk write REPLACES the cell, and a row that held two AMs comes out
  // with the one picked. Intended; the modal's Undo is what makes it safe.
  'am_names',
]);

// Inventory table columns, in the exact requested order. The original fields stay
// editable; the CR-sheet–derived columns are shown read-only. Three columns are
// computed for display: Sales Pending Payment (sum of the two pending columns),
// At-Risk Expected Payment, and Unmapped Payments.
export const columns: ColDef[] = [
  { key: 'company_record_id', label: 'Company Record ID', sortable: true, editable: true, type: 'text', align: 'center', width: '120px' },
  { key: 'website', label: 'Website', sortable: true, editable: true, type: 'text', width: '120px' },
  { key: 'email', label: 'Email', sortable: true, editable: true, type: 'text', width: '140px' },
  // [CAMPAIGN-STATUS 2026-07-06] Read-only, computed server-side — 'New Campaign' vs
  // 'Existing Campaign' based on the previous month's inventory. Sits before Type,
  // left-aligned as plain (tinted) text like the neighbouring columns.
  { key: 'campaign_status', label: 'Campaign Status', sortable: true, align: 'center', width: '128px' },
  { key: 'type', label: 'Type', sortable: true, editable: true, type: 'select', options: TYPE_OPTIONS, align: 'center', width: '78px' },
  { key: 'pm', label: 'PM Name', sortable: true, editable: true, type: 'select', width: '120px' },
  // [AM-NAMES 2026-09-04, user] Account Manager(s), immediately after PM Name on BOTH tabs.
  // `type: 'select'` on purpose even though the CELL editor is multi-select: it is what makes
  // the BULK modal offer a single-name dropdown (the user's ruling) and the export treat the
  // value as text, both for free. The multi behaviour is keyed on `key === 'am_names'` inside
  // renderCell — the same way status / website / month are special-cased there.
  { key: 'am_names', label: 'AM Names', sortable: true, editable: true, type: 'select', width: '150px' },
  { key: 'brand', label: 'Brand', sortable: true, editable: true, type: 'select', width: '140px' },
  { key: 'amount', label: 'Amount', sortable: true, editable: true, type: 'number', align: 'center', width: '90px' },
  { key: 'billing_date', label: 'Billing Date', sortable: true, editable: true, type: 'number', align: 'center', width: '72px' },
  { key: 'month', label: 'Month', sortable: true, editable: true, type: 'text', align: 'center', width: '80px' },
  { key: 'status', label: 'Status', sortable: true, editable: true, type: 'select', options: STATUS_OPTIONS, align: 'center', width: '110px' },
  { key: 'total_payment_received', label: 'Total Payment Received', sortable: true, type: 'number', align: 'center', width: '96px', overrideKey: 'total_payment_received_ovr' },
  { key: 'refunded_disputed', label: 'Refunded/Disputes', sortable: true, type: 'number', align: 'center', width: '94px', overrideKey: 'refunded_disputed_ovr' },
  { key: 'recurring_retention', label: 'Retention', sortable: true, type: 'number', align: 'center', width: '88px', overrideKey: 'recurring_retention_ovr' },
  { key: 'recurring_so', label: 'Recurring Upsell', sortable: true, type: 'number', align: 'center', width: '92px', overrideKey: 'recurring_so_ovr' },
  { key: 'one_time_so', label: 'One-Time Upsell', sortable: true, type: 'number', align: 'center', width: '92px', overrideKey: 'one_time_so_ovr' },
  { key: 'sales_pending_payment', label: 'Sales Pending Payment', sortable: true, type: 'number', align: 'center', width: '96px', overrideKey: 'sales_pending_payment_ovr' },
  { key: 'recovery', label: 'Recovery', sortable: true, type: 'number', align: 'center', width: '86px', overrideKey: 'recovery_ovr' },
  { key: 'discount', label: 'Discount', sortable: true, editable: true, type: 'number', align: 'center', width: '84px' },
  { key: 'at_risk_expected_payment', label: 'At-Risk Expected Payment', sortable: true, type: 'number', align: 'center', width: '98px' },
  { key: 'pia_amount', label: 'PIA Amount', sortable: true, type: 'number', align: 'center', width: '88px', overrideKey: 'pia_amount_ovr' },
  { key: 'pia_month', label: 'PIA Month', sortable: true, type: 'number', width: '84px', overrideKey: 'pia_month_ovr' },
  { key: 'delayed_days', label: 'Delayed Days', sortable: true, type: 'number', align: 'center', width: '84px', overrideKey: 'delayed_days_ovr' },
  { key: 'unmapped_payments', label: 'Unmapped Payments', sortable: true, type: 'number', align: 'center', width: '96px', overrideKey: 'unmapped_payments_ovr' },
  // [PAYMENT-MODE 2026-10-18, user] Fills itself from June 2026 — Subscription / Manual / "Subscription + Manual" from the
  // row's payments (server: SUBSCRIPTION_SUBQUERY); earlier months show the value people picked. Nobody edits it ("no").
  { key: 'subscription_status', label: 'Subscription', sortable: true, editable: false, width: '100px' },
  { key: 'pending_good', label: 'Pending Payment', sortable: true, type: 'number', align: 'center', width: '100px', overrideKey: 'pending_payment_ovr' },
];

/* ── [AM-REMINDER 2026-09-24, user] My Inventory "add the AM" banner ──
 * A one-line notice directly ABOVE the Performance Snapshot, on My Inventory ONLY (the
 * amReminder prop — the Inventory tab never passes it). PMs were told to fill the AM Names
 * column and nobody had: 0 of 300 September rows and 0 of 231 October rows on 2026-09-24,
 * while the same PMs saved 46 other My Inventory edits without a single failure. So this
 * sits where every PM looks first. It counts the rows in view whose AM cell the signed-in
 * user can edit (current + future months) and unmounts once every one of them has an AM,
 * so each person loses it as soon as their own campaigns are done.
 * No close button, by the user's choice: it stays until the work is done.
 *
 * LOOK, after three rounds with the user: a pale tinted card (washed out), then a solid green
 * panel (too dark, and it felt permanent). Final pick: a SLIM NOTICE PILL, i.e. fully rounded,
 * a light green wash, a thin green border, green title and icon, a gently pulsing dot that
 * marks it as a live to-do, and the count as a small pill whose fill shows the progress.
 * ⛔ No green rail on the left edge (removed at the user's request). */
const amBannerIn = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`;
// The "live to-do" dot: a ring that grows out and fades, every 2.4s.
// [GRAPHITE-EMERALD 2026-09-28] Keyframes cannot read the theme, so the ring has a light (jade mint)
// and a dark (emerald) set, picked where it is used — the same pattern as invRowFlashDark/Light.
const amBannerPulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(31,216,164,0.55); }
  70%  { box-shadow: 0 0 0 6px rgba(31,216,164,0); }
  100% { box-shadow: 0 0 0 0 rgba(31,216,164,0); }
`;
const amBannerPulseDark = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(74,205,139,0.55); }
  70%  { box-shadow: 0 0 0 6px rgba(74,205,139,0); }
  100% { box-shadow: 0 0 0 0 rgba(74,205,139,0); }
`;

// Same flat tint as the top of KpiBarWrap below, and no bottom padding, so the banner reads
// as part of the header block rather than a separate strip with a seam under it.
const AmBannerWrap = styled.div`
  padding: 12px 20px 0;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(248,250,252,0.92)')};
  @media (max-width: 600px) {
    padding: 10px 12px 0;
  }
`;

const AmBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 40px;
  padding: 5px 6px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.30)' : 'rgba(26,127,100,0.26)')};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.08)' : 'rgba(26,127,100,0.07)')};
  box-shadow: 0 2px 10px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.22)' : 'rgba(20,107,84,0.06)')};
  animation: ${amBannerIn} 320ms cubic-bezier(0.2, 0.8, 0.2, 1) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
  /* Phones: icon, text and count stack and centre, like the rest of the phone headers. A
     stacked block cannot keep the pill ends, so it takes a normal card radius there. */
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 8px;
    padding: 12px 14px;
    border-radius: 16px;
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  background-image: linear-gradient(180deg, rgba(255,255,255,${(p) => (p.theme.colorScheme === 'dark' ? '0.08' : '0.65')}), rgba(255,255,255,0) 60%);
  box-shadow: inset 0 1px 0 rgba(255,255,255,${(p) => (p.theme.colorScheme === 'dark' ? '0.12' : '0.85')}), 0 1px 2px rgba(0,0,0,0.08);
`;

// Small green disc with a white figure, plus the pulsing dot on its top-right edge.
const AmBannerIcon = styled.span`
  position: relative;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  color: #ffffff;
  background: ${(p) => p.theme.greenPanel};
  box-shadow: 0 2px 8px ${(p) => p.theme.greenPanelShadow};

  svg {
    width: 16px;
    height: 16px;
  }

  &::after {
    content: '';
    position: absolute;
    top: -2px;
    right: -2px;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : '#1fd8a4')};
    border: 2px solid ${(p) => p.theme.surface};
    animation: ${(p) => (p.theme.colorScheme === 'dark' ? amBannerPulseDark : amBannerPulse)} 2.4s ease-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after { animation: none; }
  }
`;

// [AM-REMINDER 2026-09-24, user] ONE line: "Add the AM on your campaigns | You now have access
// ...". Plain inline flow (not a flex row), so on a narrower screen the sentence wraps like
// text and the divider stays between title and sentence instead of dangling at a line end.
// Phones keep the stacked, centred layout; the divider is hidden there.
const AmBannerText = styled.div`
  flex: 1;
  min-width: 0;
  line-height: 1.45;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
`;

// Thin vertical rule, the same idea as the toolbar's TitleDivider.
const AmBannerDivider = styled.span`
  display: inline-block;
  width: 1px;
  height: 13px;
  margin: 0 10px;
  vertical-align: -2px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.22)' : 'rgba(22,25,23,0.20)')};

  @media (max-width: 768px) {
    display: none;
  }
`;

const AmBannerTitle = styled.strong`
  font-size: 13.5px;
  font-weight: 700;
  white-space: nowrap;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
`;

const AmBannerBody = styled.span`
  font-size: 12.5px;
  color: ${(p) => p.theme.dim};

  b {
    font-weight: 700;
    color: ${(p) => p.theme.text};
  }
`;

// The count, e.g. "0 / 300 have an AM". Its green fill (AmBannerPillFill) is the progress bar.
const AmBannerPill = styled.span`
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 5px 14px;
  border-radius: 999px;
  white-space: nowrap;
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.28)' : 'rgba(26,127,100,0.22)')};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.90)')};
`;

// Width comes in as an inline style (it changes with every AM saved), so styled-components
// does not mint a new class per percentage.
const AmBannerPillFill = styled.span`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.20)' : 'rgba(26,127,100,0.16)')};
  transition: width 500ms cubic-bezier(0.2, 0.8, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  background-image: linear-gradient(180deg, rgba(255,255,255,0.30), rgba(255,255,255,0) 60%);
`;

const AmBannerPillText = styled.span`
  position: relative;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${(p) => p.theme.dim};

  strong {
    font-size: 14px;
    font-weight: 800;
    color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  }
`;

const formatWholeNumber = (n: number) => String(Math.round(n));

/* ── [AM-REMINDER-SKELETON 2026-09-24, user] The banner's place in the loading skeleton ──
 * The banner counts the rows on screen, so while they load the page cannot know yet whether
 * it will show. It remembers the last answer per person and per view (month + PM + filters)
 * in this browser: shown last time → the skeleton holds the banner's place; gone last time
 * (all AMs in, or a past month) → the skeleton leaves that area out too. A view never seen
 * here guesses from the month: current or future → placeholder, past → none. The answer is
 * re-saved as AMs are filled, so a person's last AM also takes the placeholder off their
 * next load. A per-browser convenience (like the theme toggle): if storage fails, the month
 * guess covers it. */
const AM_BANNER_MEMO_KEY = 'dt-am-reminder-shown';
// Views remembered per person; past this the least recently opened is dropped, so it cannot
// grow for ever while the views a person actually uses stay in.
const AM_BANNER_MEMO_MAX = 40;
let amBannerMemo: Record<string, Record<string, boolean>> | null = null;

function loadAmBannerMemo(): Record<string, Record<string, boolean>> {
  if (amBannerMemo) return amBannerMemo;
  try {
    const parsed = JSON.parse(localStorage.getItem(AM_BANNER_MEMO_KEY) || '{}');
    amBannerMemo = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    amBannerMemo = {};
  }
  return amBannerMemo as Record<string, Record<string, boolean>>;
}

function recallAmBanner(userKey: string, viewKey: string): boolean | undefined {
  const v = loadAmBannerMemo()[userKey]?.[viewKey];
  return typeof v === 'boolean' ? v : undefined;
}

function rememberAmBanner(userKey: string, viewKey: string, shown: boolean): void {
  // Re-read first, so a second browser tab's answers are merged rather than overwritten.
  amBannerMemo = null;
  const memo = loadAmBannerMemo();
  const views = memo[userKey] && typeof memo[userKey] === 'object' ? memo[userKey] : (memo[userKey] = {});
  // Called each time a view finishes loading (and when its answer changes). Nothing to write
  // when the answer is the same and this view is already the newest entry.
  const keysBefore = Object.keys(views);
  if (views[viewKey] === shown && keysBefore[keysBefore.length - 1] === viewKey) return;
  delete views[viewKey]; // re-added at the end, so the first key is always the least recently opened
  views[viewKey] = shown;
  const keys = Object.keys(views);
  for (let i = 0; i < keys.length - AM_BANNER_MEMO_MAX; i++) delete views[keys[i]];
  try {
    localStorage.setItem(AM_BANNER_MEMO_KEY, JSON.stringify(memo));
  } catch {
    /* private mode / quota exceeded: non-fatal, the month guess covers it */
  }
}

// Names one view: every filter visibleData applies. load() stamps the view its rows belong to
// with the same function, so the two keys can only match when the rows really are this view's.
const amViewKeyOf = (...filters: Array<string | string[]>) => JSON.stringify(filters);

// The skeleton is BUILT FROM THE BANNER ITSELF: the same styled parts and the same words, with
// the words invisible and each line of text drawn as a grey bar. So it wraps line for line like
// the banner at every width (one line on a wide screen, two or three on a narrow one, the
// stacked phone layout), and the real banner lands on it without a jump. Only the colours and
// the entrance animation are overridden.
const AmBannerSkeletonPill = styled(AmBanner)`
  border-color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(22,25,23,0.08)')};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.70)')};
  box-shadow: none;
  animation: none;
`;

// SkeletonBox's grey and highlight, drawn as a moving background instead of its ::after
// overlay, because an overlay cannot follow text that wraps onto several lines.
const amSkSweep = keyframes`
  from { background-position: 100% 0; }
  to   { background-position: 0 0; }
`;
const amSkShimmer = css`
  background-color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(22,25,23,0.06)')};
  background-image: linear-gradient(
    90deg,
    transparent 33%,
    ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.55)')} 50%,
    transparent 67%
  );
  background-size: 300% 100%;
  background-repeat: no-repeat;
  animation: ${amSkSweep} 1.5s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// Wraps the banner's words: they stay invisible, and box-decoration-break gives every wrapped
// line its own rounded grey bar. Doubled class so it outranks AmBannerBody's bold colour.
const AmSkInk = styled.span`
  ${amSkShimmer}
  border-radius: 6px;
  -webkit-box-decoration-break: clone;
  box-decoration-break: clone;
  user-select: none;

  &&,
  && * {
    color: transparent;
  }
`;

// The count pill, same size as AmBannerPill (it IS AmBannerPill), as one grey bar.
const AmSkPill = styled(AmBannerPill)`
  ${amSkShimmer}
  border-color: transparent;

  && * {
    color: transparent;
  }
`;

// The icon disc keeps its size, like AmBannerIcon.
const AmSkFixed = styled(SkeletonBox)`
  flex-shrink: 0;
`;

// ⚠ The words below are the banner's own copy (see the banner in the render). Keep the two in
// step, or the skeleton wraps differently from the banner and the jump comes back.
const AmBannerSkeleton: React.FC = () => (
  <AmBannerWrap aria-busy="true" aria-label="Loading">
    <AmBannerSkeletonPill>
      <AmSkFixed $w="30px" $h="30px" $radius="50%" />
      <AmBannerText aria-hidden="true">
        <AmBannerTitle><AmSkInk>Add the AM on your campaigns</AmSkInk></AmBannerTitle>
        <AmBannerDivider style={{ visibility: 'hidden' }} />
        <AmBannerBody>
          <AmSkInk>
            You now have access to the <b>AM Names</b> column. Click the AM Names cell on each
            campaign and select the Account Manager working on it.
          </AmSkInk>
        </AmBannerBody>
      </AmBannerText>
      {/* The count is not known yet; "0 / 000" is the width of a typical one. */}
      <AmSkPill aria-hidden="true">
        <AmBannerPillText>
          <strong>0</strong>{' '}/ 000 have an AM
        </AmBannerPillText>
      </AmSkPill>
    </AmBannerSkeletonPill>
  </AmBannerWrap>
);

/* ── My Inventory KPI strip ── */

const KpiBarWrap = styled.div`
  padding: 14px 20px 16px;
  background: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.03) 100%)'
    : 'linear-gradient(180deg, rgba(248,250,252,0.92) 0%, rgba(241,245,249,0.78) 100%)')};
  /* No backdrop-filter — it flashed white during AppLayout's PageFade tab
     transition (Chromium glitch) and the blur did nothing over the solid card. */
  border-bottom: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(22,25,23,0.08)')};

  /* [PHONE-LAYOUT 2026-10-22] Phones: the 12 px side padding of the rest of the phone page. */
  @media (max-width: 768px) {
    padding: 12px 12px 14px;
  }
`;

const KpiBarHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const KpiBarTitle = styled.span`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  display: flex;
  align-items: center;
  gap: 8px;
  &::before {
    content: '';
    width: 3px;
    height: 13px;
    border-radius: 2px;
    background: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  }
`;

const KpiBarContext = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${(p) => p.theme.muted};
`;

// Per-cell natural width (px) + the grid gap — used to size the <ScaleToFit>
// wrapper so the whole strip is one readable row that scales down (rather than
// wrapping) on narrower screens.
const KPI_CELL_W = 150;
const KPI_GAP = 8;
const kpiRowMinWidth = (n: number) => n * KPI_CELL_W + (n - 1) * KPI_GAP;

const KpiGrid = styled.div<{ $land?: boolean }>`
  display: grid;
  /* [SINGLE-ROW-KPI 2026-06-23] The Performance Snapshot KPIs must ALWAYS sit on
     one line (live bar AND skeleton). grid-auto-flow:column + auto-columns:1fr
     lays every cell in a single row — this STRUCTURALLY cannot wrap to a 2nd row,
     so the layout can't silently regress again. The row fills the width on wide
     screens; the <ScaleToFit> wrapper around this grid shrinks the whole row
     uniformly on narrower screens so values stay fully visible and never truncate
     (the readability problem that caused the earlier auto-fit/wrap workaround). */
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: ${KPI_GAP}px;
  /* [FLUID-6 2026-10-12, user] The tiles rise in one after another each time new figures land (InventoryKpiBar's
     tilesLand). Only the live bar passes $land, never the skeleton. */
  ${(p) => (p.$land ? fluidCascade('> *', 0, 12, 12) /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}
`;

// [MYINV-MOBILE-KPI 2026-07-07] Phone-only Performance Snapshot layout. The desktop
// single-row ScaleToFit strip would shrink 12 tiles to unreadable slivers, and a
// wrapping multi-row grid was too tall and truncated the values. Instead the tiles
// sit in ONE horizontally-scrolling row, sized so THREE show per screen width at
// full readable size; the user swipes sideways for the rest, with a sliver of the
// next tile peeking as the scroll affordance and scroll-snap settling each tile to
// the left edge — a clean, premium carousel. Desktop keeps the single-row ScaleToFit
// treatment above; this strip is only mounted in the isMobile branch of the KPI bar
// (and its skeleton), so desktop is byte-for-byte unchanged.
const KpiGridMobile = styled.div<{ $land?: boolean }>`
  display: flex;
  gap: ${KPI_GAP}px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  /* No visible scrollbar — the peeking next tile is the scroll affordance. */
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }

  > * {
    /* Three tiles per viewport (two ${KPI_GAP}px gaps between them) minus a small
       amount so a sliver of the next tile peeks — hinting the row scrolls.
       flex-shrink:0 keeps each tile at full width instead of squeezing to fit. */
    flex: 0 0 calc((100% - ${KPI_GAP * 2}px) / 3 - 12px);
    scroll-snap-align: start;
  }
  /* [FLUID-6 2026-10-12, user] Phones: the same tile rise as the desktop strip (KpiGrid). */
  ${(p) => (p.$land ? fluidCascade('> *', 0, 12, 12) /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}

  /* [PHONE-LAYOUT 2026-10-22] TWO tiles per screen with the next one peeking (about 24 px): at three per screen
     every figure was cut ("$255,14…", 99 px tiles at 390 px). A swipe here stays in this row (it does not
     carry on into the table below it). */
  @media (max-width: 768px) {
    overscroll-behavior-x: contain;
    & > * {
      flex: 0 0 calc((100% - ${KPI_GAP}px) / 2 - 16px);
    }
  }
`;

const KpiCellBox = styled.div`
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 10px 10px;
  border-radius: 12px;
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(22,25,23,0.08)')};
  background: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(86,86,86,0.40) 0%, rgba(54,54,54,0.52) 100%)'
    : 'rgba(255,255,255,0.94)')};
  box-shadow:
    0 4px 14px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.28)' : 'rgba(22,25,23,0.06)')},
    inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.70)')};
  transition: transform 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 180ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-1px);
    box-shadow:
      0 8px 20px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.36)' : 'rgba(22,25,23,0.10)')},
      inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.85)')};
  }

  /* [PHONE-LAYOUT 2026-10-22] Phones: one height for every tile (a two-line label + the figure), the skeleton
     tiles included, so nothing jumps when the figures land. */
  @media (max-width: 768px) {
    min-height: 70px;
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgTile}
`;

const KpiCellLabel = styled.span`
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${(p) => p.theme.muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  /* [PHONE-LAYOUT 2026-10-22] Phones: 11 px and never cut; a long label ("NEXT MONTH INVENTORY") wraps to a
     second line, and every label keeps two lines of room so the figures line up across the tiles. */
  @media (max-width: 768px) {
    font-size: 11px;
    line-height: 1.25;
    min-height: 2.5em;
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
  }
`;

// [KPI-TOOLTIP 2026-06-24] Self-contained themed capsule for the KPI tiles, copied
// from the Payments "In Bank Summary" tooltip. We deliberately do NOT route these
// through the native `title` / GlobalTooltip path: GlobalTooltip strips the `title`
// on hover and restores it on pointerdown, which makes the browser's plain native
// tooltip flash the moment a tile is clicked. This capsule is driven purely by
// hover state and never touches `title`, so clicking a tile can never surface the
// native box. Styling mirrors GlobalTooltip / InBankTipBox so they look identical —
// theme-aware, viewport-clamped, flips above when it would clip.
const KpiTipBox = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 6000;
  pointer-events: none;
  max-width: 300px;
  white-space: normal;
  overflow-wrap: anywhere;
  text-align: left;

  padding: 8px 12px;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.45;
  letter-spacing: 0.01em;

  /* [ONE GLASS RECIPE 2026-09-01, user] Shared tooltip surface — see components/TipGlass. These
     were hand-copies of GlobalTooltip's old capsule and would have been left behind by the
     app-wide glass change. */
  border-radius: ${GLASS_RADIUS}px;
  color: ${(p) => glassInk(p.theme.colorScheme === 'dark')};
  background: ${(p) => glassBackground(p.theme.colorScheme === 'dark')};
  backdrop-filter: ${GLASS_BLUR};
  -webkit-backdrop-filter: ${GLASS_BLUR};
  border: ${(p) => glassBorder(p.theme.colorScheme === 'dark')};
  box-shadow: ${(p) => glassShadow(p.theme.colorScheme === 'dark')};
`;

interface KpiTip {
  text: string;
  anchor: DOMRect;
}

// Positions the capsule under its anchor tile (pre-paint, so no flash), centred
// and clamped inside the viewport, flipping above when the bottom would clip.
const KpiInfoTip: React.FC<{ tip: KpiTip | null }> = ({ tip }) => {
  const boxRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!tip || !box) return;
    // [APP-FIT 2026-10-26] In page px (utils/appZoom): the left / top set below are page px under the app zoom.
    const { width: bw, height: bh } = pageRect(box);
    const { width: vw, height: vh } = pageViewport();
    const anchor = pageRect(tip.anchor);
    const GAP = 8;
    const MARGIN = 8;
    let left = anchor.left + anchor.width / 2 - bw / 2;
    left = Math.max(MARGIN, Math.min(left, vw - bw - MARGIN));
    let top = anchor.bottom + GAP;
    if (top + bh > vh - MARGIN) {
      const above = anchor.top - GAP - bh;
      top = above >= MARGIN ? above : Math.max(MARGIN, Math.min(top, vh - bh - MARGIN));
    }
    box.style.left = `${Math.round(left)}px`;
    box.style.top = `${Math.round(top)}px`;
  }, [tip]);
  if (!tip) return null;
  return createPortal(<KpiTipBox ref={boxRef}>{tip.text}</KpiTipBox>, document.body);
};

/* [MYINV-KPI-BREAKDOWN 2026-07-07] The Refunds/Disputes and Disputes Won tiles reveal a
   hover breakdown of the individual rows behind the number (website · person · amount +
   an exact Total) — the SAME card the My Sales snapshot uses (MySalesPage), ported here
   so the My Inventory strip matches. Portaled to <body> so the frozen KPI strip's
   overflow can't clip it, positioned + clamped below the tile like KpiInfoTip. When the
   tile has no rows (e.g. a month with no refunds/disputes) the strip falls back to the
   plain descriptive text tip instead.

   [ALL-ROWS 2026-07-07] The card shows EVERY row (no "+N more" cap) so the user sees the
   complete breakdown; the list scrolls internally (BdList) when a month has many rows,
   and the card is INTERACTIVE (pointer-events: auto) so the cursor can travel onto it and
   scroll without it vanishing — the tile→card gap is bridged by a short hide delay in
   InventoryKpiBar (breakdownHideTimer). */
const KpiBreakdownBox = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 6000;
  pointer-events: auto;
  min-width: 244px;
  max-width: 320px;
  padding: 11px 14px;
  text-align: left;
  /* [ONE GLASS RECIPE 2026-09-01, user] Shared tooltip surface — see components/TipGlass. Keeps
     its own size, padding and pointer-events: auto (this card is interactive and scrollable). */
  border-radius: ${GLASS_RADIUS}px;
  color: ${(p) => glassInk(p.theme.colorScheme === 'dark')};
  background: ${(p) => glassBackground(p.theme.colorScheme === 'dark')};
  backdrop-filter: ${GLASS_BLUR};
  -webkit-backdrop-filter: ${GLASS_BLUR};
  border: ${(p) => glassBorder(p.theme.colorScheme === 'dark')};
  box-shadow: ${(p) => glassShadow(p.theme.colorScheme === 'dark')};
`;

const BdHeader = styled.div`
  font-size: 9.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: ${(p) => p.theme.muted};
  margin-bottom: 9px;
  padding-bottom: 7px;
  border-bottom: 1px solid ${(p) => p.theme.line};
`;

const BdItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  & + & { margin-top: 8px; }
`;

const BdItemLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const BdSite = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: ${(p) => p.theme.text};
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BdSub = styled.span`
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${(p) => p.theme.muted};
  max-width: 190px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BdAmt = styled.span<{ $tone: 'red' | 'green'; $strong?: boolean }>`
  flex: none;
  font-size: 13px;
  font-weight: ${(p) => (p.$strong ? 800 : 700)};
  font-variant-numeric: tabular-nums;
  color: ${(p) =>
    p.$tone === 'green'
      ? (p.theme.colorScheme === 'dark' ? '#4acd8b' : '#1a9f7d')
      : (p.theme.colorScheme === 'dark' ? '#f87171' : '#dc2626')};
`;

// The scrollable list region — every row renders (no cap); when a month has more
// rows than fit, this scrolls internally while the header + Total stay pinned. The
// card is interactive so the cursor can enter and scroll it.
const BdList = styled.div`
  /* ~7 rows visible (a sliver of the 8th hints "scroll for more"); the rest scroll. */
  max-height: min(50vh / var(--app-zoom, 1), 256px); /* [APP-FIT 2026-10-26] the window in page px */
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  margin-right: -6px;   /* let the scrollbar sit at the edge without cramping amounts */
  padding-right: 6px;
  scrollbar-width: thin;
  scrollbar-color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.28)' : 'rgba(22,25,23,0.22)')} transparent;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.24)' : 'rgba(22,25,23,0.20)')};
    border-radius: 3px;
  }
`;

const BdDivider = styled.div`
  height: 1px;
  background: ${(p) => p.theme.line};
  margin: 9px 0 8px;
`;

const BdTotalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
`;

const BdTotalLabel = styled.span`
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: ${(p) => p.theme.muted};
`;

interface BreakdownTip {
  anchor: DOMRect;
  items: InventoryRefundDispute[];
  tone: 'red' | 'green';
  showType: boolean;         // Refunds/Disputes shows "· Refund"/"· Dispute"; Disputes Won hides it
  countLabel: string;
}

// Portaled breakdown card, positioned + clamped below its anchor tile like KpiInfoTip
// (flips above when it would clip the viewport bottom), so it never runs off-screen
// when the tile sits near the right edge of the strip. Renders EVERY row (the list
// scrolls when long) and is interactive — onEnter/onLeave keep it open while the
// cursor is over it so it can be scrolled to see the complete breakdown.
const KpiBreakdownCard: React.FC<{
  tip: BreakdownTip | null;
  onEnter: () => void;
  onLeave: () => void;
}> = ({ tip, onEnter, onLeave }) => {
  const boxRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!tip || !box) return;
    // [APP-FIT 2026-10-26] In page px (utils/appZoom): the left / top set below are page px under the app zoom.
    const { width: bw, height: bh } = pageRect(box);
    const { width: vw, height: vh } = pageViewport();
    const anchor = pageRect(tip.anchor);
    const GAP = 8;
    const MARGIN = 8;
    let left = anchor.left + anchor.width / 2 - bw / 2;
    left = Math.max(MARGIN, Math.min(left, vw - bw - MARGIN));
    let top = anchor.bottom + GAP;
    if (top + bh > vh - MARGIN) {
      const above = anchor.top - GAP - bh;
      top = above >= MARGIN ? above : Math.max(MARGIN, Math.min(top, vh - bh - MARGIN));
    }
    box.style.left = `${Math.round(left)}px`;
    box.style.top = `${Math.round(top)}px`;
  }, [tip]);
  if (!tip) return null;
  const total = tip.items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
  return createPortal(
    <KpiBreakdownBox ref={boxRef} onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <BdHeader>{tip.countLabel}</BdHeader>
      <BdList>
        {tip.items.map((it, i) => (
          <BdItem key={`${it.website}-${it.person}-${i}`}>
            <BdItemLeft>
              <BdSite>{it.website || '—'}</BdSite>
              <BdSub>{(it.person || 'Unassigned')}{tip.showType ? ` · ${it.type}` : ''}</BdSub>
            </BdItemLeft>
            <BdAmt $tone={tip.tone}>{formatMoney(it.amount)}</BdAmt>
          </BdItem>
        ))}
      </BdList>
      <BdDivider />
      <BdTotalRow>
        <BdTotalLabel>Total</BdTotalLabel>
        <BdAmt $tone={tip.tone} $strong>{formatMoney(total)}</BdAmt>
      </BdTotalRow>
    </KpiBreakdownBox>,
    document.body,
  );
};

const KpiCellValue = styled.strong<{ $tone?: 'pos' | 'neg' | 'pct' | 'muted' }>`
  font-size: 15px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(p) => {
    if (p.$tone === 'pos') return p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600;
    if (p.$tone === 'neg') return '#ef4444';
    if (p.$tone === 'pct') return p.theme.colorScheme === 'dark' ? '#60a5fa' : '#2563eb';
    return p.theme.text;
  }};

  /* [PHONE-LAYOUT 2026-10-22] Phones: the figure is never cut (the two-per-screen tile fits $1,234,567.89). */
  @media (max-width: 768px) {
    overflow: visible;
    text-overflow: clip;
  }
`;

const fmtPct = (v: number): string => `${v.toFixed(1)}%`;

/**
 * Loading placeholder for the KPI strip. Reuses the SAME wrap / head / grid /
 * cell styled-components as the real bar (not the generic <KpiSkeleton>), so the
 * skeleton lays out as one full-width row of equal cells — matching the live
 * single-row grid exactly, with no wrap-to-second-row jump when data lands.
 */
const InventoryKpiBarSkeleton: React.FC<{ count: number }> = ({ count }) => {
  // [MYINV-MOBILE-KPI 2026-07-07] Match the live bar's layout per breakpoint so the
  // strip doesn't jump when the numbers land: horizontal-scroll carousel on phones,
  // single-row ScaleToFit on desktop.
  const isMobile = useIsMobile();
  const tiles = Array.from({ length: count }).map((_, i) => (
    <KpiCellBox key={i}>
      <SkeletonBox $w="72%" $h="9px" />
      <SkeletonBox $w="58%" $h="16px" />
    </KpiCellBox>
  ));
  return (
    <KpiBarWrap aria-busy="true" aria-label="Loading">
      <KpiBarHead>
        <SkeletonBox $w="150px" $h="12px" />
        <SkeletonBox $w="90px" $h="11px" />
      </KpiBarHead>
      {isMobile ? (
        <KpiGridMobile>{tiles}</KpiGridMobile>
      ) : (
        // Same single-row + scale-to-fit treatment as the live bar, so the skeleton
        // lays out as ONE row of equal cells and never wraps to a 2nd row.
        <ScaleToFit contentMinWidth={kpiRowMinWidth(count)}>
          <KpiGrid>{tiles}</KpiGrid>
        </ScaleToFit>
      )}
    </KpiBarWrap>
  );
};

/** KPI snapshot for the selected PM + month, sourced live from inventory + payments. */
const InventoryKpiBar: React.FC<{ pm: string; month: string }> = ({ pm, month }) => {
  /**
   * [TAB-FAST 2026-08-13] Seed the first render from the SWR cache.
   *
   * This strip is the reason My Inventory felt slower to come back to than any other tab: it is
   * the ONLY thing on the page that Inventory does not also have, it remounts on every tab
   * switch, and it used to start at loading=true and refetch unconditionally. Worse, its
   * endpoint is the one HubSpot-backed call in the KPI set (the upsell figure, raced against an
   * 8s timeout server-side), so the spinner it showed was not a token flash — it sat above the
   * toolbar, at the top of the page, for as long as HubSpot took.
   *
   * Lazy initialisers, not a seed inside reloadKpis: the fetch resolves a tick later even on a
   * cache HIT, so anything short of seeding the INITIAL state still paints the spinner first.
   * Mirrors ProcessAnalysisPage. The params must match reloadKpis's call exactly or the key
   * never lines up — cacheKey drops the undefined ones, so the empty pm/month cases agree too.
   */
  const peekKpis = () =>
    peekCache<InventoryKpis>('/inventory/kpis', { pm: pm || undefined, month: month || undefined });
  const [kpis, setKpis] = useState<InventoryKpis | null>(() => peekKpis() ?? null);
  const [loading, setLoading] = useState(() => !peekKpis());
  // [FLUID-6 2026-10-12, user] The PM + month the figures on the tiles belong to, set as they land for it (a cached
  // copy at once, or the answer that replaces the skeleton / the dimmed old figures). Each new value makes the tiles
  // rise in one after another (tilesLand below); a silent refresh of the same PM + month leaves it as it is, so the
  // tiles never replay on a save or a live update.
  const [landedFor, setLandedFor] = useState<string | null>(() => (peekKpis() ? `${pm}|${month}` : null));

  // Reloadable so the KPI strip can refresh both on pm/month change AND on any
  // write (via the live-refresh bus below). Without the bus subscription the
  // strip stayed frozen after an inline edit until the user changed pm/month or
  // reloaded the whole page. A request-id discards stale/out-of-order responses
  // so a slow earlier fetch can't clobber the latest figures.
  const kpiReqRef = useRef(0);
  const reloadKpis = useCallback((spinner = false) => {
    const reqId = ++kpiReqRef.current;
    const forKey = `${pm}|${month}`; // [FLUID-6 2026-10-12] whose figures this load brings
    if (spinner) {
      // [TAB-FAST 2026-08-13] The mount effect below asks for the spinner unconditionally, which
      // would undo the seeded initial state a tick later. Check the cache again here so a warm
      // combo swaps straight in: this covers the remount AND a pm/month change back to a
      // combination already fetched. A miss falls back to the spinner, exactly as before.
      const seed = peekCache<InventoryKpis>('/inventory/kpis', { pm: pm || undefined, month: month || undefined });
      if (seed) { setKpis(seed); setLoading(false); setLandedFor(forKey); } else setLoading(true);
    }
    fetchInventoryKpis({ pm: pm || undefined, month: month || undefined })
      .then((d) => { if (reqId === kpiReqRef.current) { setKpis(d); setLandedFor(forKey); } })
      .catch((err) => { console.error('Failed to load inventory KPIs:', err); if (reqId === kpiReqRef.current) setKpis(null); })
      .finally(() => { if (reqId === kpiReqRef.current) setLoading(false); });
  }, [pm, month]);

  useEffect(() => { reloadKpis(true); }, [reloadKpis]);

  // A write anywhere on this page (inline edit, add, delete, renewal, month move)
  // changes these figures — refetch silently so they track the table in real time.
  useEffect(() => onDataChange(() => reloadKpis(false)), [reloadKpis]);

  // [REALTIME] 2026-06-23 — also refresh on a background DB change (any source).
  useRealtime(['inventory'], () => reloadKpis(false));

  // [LOAD-STATES 2026-09-29, user] A failed strip ("—" in every tile) retries by itself every 10 s
  // (paused while the browser tab is hidden) instead of waiting for the next live update.
  // [DB-OUTAGE-FIX 2026-10-28, user] …and so does a HALF-loaded one: the upsell part could not be read
  // (null → "—" on the three upsell tiles, Expected EOM and Current Net In-Bank). It used to stay that way
  // until the next inventory change; now it fills in by itself ~10 s after the source is back.
  const kpisFailed = !loading && (kpis === null || kpis.totalUpsell === null);
  useEffect(() => {
    if (!kpisFailed) return undefined;
    const id = window.setInterval(() => { if (!document.hidden) reloadKpis(false); }, 10000);
    return () => window.clearInterval(id);
  }, [kpisFailed, reloadKpis]);

  // [KPI-TOOLTIP 2026-06-24] Self-contained hover tooltip (copied from Payments'
  // In Bank Summary). Driven purely by hover state — never via the native `title` —
  // so clicking a tile can't flash the browser's default tooltip. A 600ms dwell
  // matches the app-wide GlobalTooltip so it feels deliberate.
  const [kpiTip, setKpiTip] = useState<KpiTip | null>(null);
  // [MYINV-KPI-BREAKDOWN 2026-07-07] Separate state for the two rich breakdown tiles
  // (Refunds/Disputes + Disputes Won); the same dwell timer drives both so the whole
  // strip behaves consistently on hover.
  const [breakdownTip, setBreakdownTip] = useState<BreakdownTip | null>(null);
  const kpiTipTimer = useRef<number | null>(null);
  // [ALL-ROWS 2026-07-07] The breakdown card is interactive (scrollable). Hiding it on
  // the tile's mouseleave is DELAYED so the cursor can travel across the small gap onto
  // the card; the card's own mouseenter cancels the pending hide, keeping it open.
  const breakdownHideTimer = useRef<number | null>(null);
  const cancelBreakdownHide = useCallback(() => {
    if (breakdownHideTimer.current != null) { window.clearTimeout(breakdownHideTimer.current); breakdownHideTimer.current = null; }
  }, []);
  const scheduleBreakdownHide = useCallback(() => {
    cancelBreakdownHide();
    breakdownHideTimer.current = window.setTimeout(() => setBreakdownTip(null), 160);
  }, [cancelBreakdownHide]);
  useEffect(() => () => {
    if (kpiTipTimer.current != null) window.clearTimeout(kpiTipTimer.current);
    if (breakdownHideTimer.current != null) window.clearTimeout(breakdownHideTimer.current);
  }, []);

  // [MYINV-MOBILE-KPI 2026-07-07] Phones render the tiles as a horizontal-scroll
  // carousel (3 visible per screen) instead of the desktop single-row ScaleToFit
  // strip. This hook must run before the early skeleton return below so the hook
  // order stays stable.
  const isMobile = useIsMobile();

  // [PHONE-LAYOUT 2026-10-23] Phones: the page scrolls as one now (and the tiles swipe sideways), while a tip or card
  // opened by a tap is drawn fixed where its tile was. Close it (and a tap's pending 600 ms dwell) the moment the page
  // or the tile row moves under it, as Payments' In Bank tip does. A scroll inside the card's own list (portaled to
  // the body, so it neither holds nor sits in this strip) keeps it open.
  const barRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isMobile) return undefined;
    const onScroll = (e: Event) => {
      const bar = barRef.current;
      const t = e.target;
      if (!bar || !(t === document || (t instanceof Node && (t.contains(bar) || bar.contains(t))))) return;
      if (kpiTipTimer.current != null) { window.clearTimeout(kpiTipTimer.current); kpiTipTimer.current = null; }
      setKpiTip(null);
      setBreakdownTip(null);
    };
    document.addEventListener('scroll', onScroll, true);
    return () => document.removeEventListener('scroll', onScroll, true);
  }, [isMobile]);

  // [FLUID-6 2026-10-12, user] True for a moment each time figures land for a new PM + month (and when they first
  // replace the skeleton): the tiles rise in one after another then (KpiGrid / KpiGridMobile $land). Decided during
  // render, so a tile is never painted at rest first. Above the skeleton return so the hook order stays stable.
  const tilesLand = useLandWindow(landedFor !== null, landedFor ?? '');

  // [MOTION-B 2026-08-20] KPI figures count to their new value when a save or a background
  // refresh moves them (never on first paint - see AnimatedNumber). formatMoney/fmtPct still
  // do all the formatting, so every tile renders exactly the same text as before.
  const money = (v: number | null | undefined) =>
    (v == null ? '—' : <AnimatedNumber value={v} format={(n) => formatMoney(n)} />);
  const pct = (v: number | null | undefined) =>
    (v == null ? '—' : <AnimatedNumber value={v} format={fmtPct} />);

  // [KPI-TOOLTIP 2026-06-24] Tooltip scope, resolved from the SAME pm/month the bar
  // is rendered for — so every description reads correctly whether a single PM or
  // "All PMs" is selected, and tracks the chosen month (the figures themselves are
  // already scoped to pm + month on the server, so the words match the numbers in
  // every case). When the month filter is cleared, the values span the current
  // selection, so the wording falls back to "the selected period" / "next month".
  /* [UPSELL-HEALTH 2026-08-24] A HubSpot-backed tile arrives as null when the fetch failed, and
     renders "—". Explain that on hover, so a dash reads as "not loaded" rather than "broken" —
     it heals by itself once HubSpot answers again (the server retries, rotates credentials and
     never caches a failure). Only applied to the five HubSpot-dependent tiles; Next Month
     Inventory is legitimately null when there is nothing lined up. */
  const upsellUnavailableTip =
    'Not loaded from HubSpot just now — shown as “—” rather than $0 so it can’t be mistaken for a real zero. It comes back on its own within a few minutes.';
  const tipFor = (v: number | null | undefined, normal: string) =>
    (kpis && v == null ? upsellUnavailableTip : normal);

  const whoPhrase = pm || 'all PMs';
  const periodPhrase = month ? formatMonthLabel(month) : 'the selected period';
  const nextPhrase = month ? formatNextMonthLabel(month) : 'next month';

  const cells: {
    label: string;
    // [MOTION-B 2026-08-20] ReactNode, not string: the money/pct helpers above now return an
    // <AnimatedNumber> element for a present value (and the plain em-dash for a null one).
    value: React.ReactNode;
    tone: 'pos' | 'neg' | 'pct' | 'muted';
    tip: string;
    // When present AND it has rows, the tile shows the rich breakdown card on hover
    // (falling back to `tip` when there are no rows). Only the two refund/dispute tiles.
    breakdown?: { items: InventoryRefundDispute[]; tone: 'red' | 'green'; showType: boolean; countLabel: (n: number) => string };
  }[] = [
    /* [KPI-RECURRING-ONLY 2026-09-04, user] This tile counts Recurring rows ONLY — the KPI
       route drops One-Time table-wide (routes/inventory.ts, the `<> 'One-Time'` WHERE) and the
       placedInventory column narrows again to `type = 'Recurring'`, so PIA is dropped too.
       [MYINV-RECURRING-ONLY 2026-09-24, user] My Inventory's table now hides One-Time + PIA as
       well, so the toolbar "Placed Inventory" just below counts the same types as this tile. (Until then the
       toolbar summed EVERY type and the two differed by the One-Time + PIA rows, Sept 2026:
       $2,099.48 + $2,050.00 = $4,149.48.) This tile stays Recurring-only on purpose — it is the denominator of Current and
       Expected Retention %, and widening it would move every retention % with no real change in
       performance. So we name the excluded types on hover instead of changing the maths — kept
       to one short line, because the tip box is small and every tile's reads in one glance. */
    { label: 'Placed Inventory', value: money(kpis?.placedInventory), tone: 'pos',
      tip: `Total inventory placed for ${whoPhrase} in ${periodPhrase}. Recurring rows only — One-Time and PIA are not counted.` },
    { label: 'Placed in Bank', value: money(kpis?.placedInBank), tone: 'pos',
      tip: `Inventory amount received in the bank for ${whoPhrase} in ${periodPhrase}.` },
    { label: 'Expected Retention', value: pct(kpis?.expectedRetentionPct), tone: 'pct',
      tip: `Retention % ${whoPhrase} is expected to bring in ${periodPhrase}.` },
    { label: 'Current Retention', value: pct(kpis?.currentRetentionPct), tone: 'pct',
      tip: `Retention % achieved so far for ${whoPhrase} in ${periodPhrase} (amount in bank ÷ placed inventory).` },
    { label: 'Recurring Upsell', value: money(kpis?.recurringUpsell), tone: 'pos',
      tip: tipFor(kpis?.recurringUpsell, `Recurring upsell booked for ${whoPhrase} in ${periodPhrase}.`) },
    { label: 'One-Time Upsell', value: money(kpis?.oneTimeUpsell), tone: 'pos',
      tip: tipFor(kpis?.oneTimeUpsell, `One-time upsell booked for ${whoPhrase} in ${periodPhrase}.`) },
    { label: 'Total Upsell', value: money(kpis?.totalUpsell), tone: 'pos',
      tip: tipFor(kpis?.totalUpsell, `Recurring + one-time upsell for ${whoPhrase} in ${periodPhrase}.`) },
    { label: 'Refunds / Disputes', value: money(kpis?.refundsDisputes), tone: 'neg',
      tip: `Payments taken in an earlier month that were refunded or disputed in ${periodPhrase}, for ${whoPhrase}.`,
      breakdown: {
        items: kpis?.refundsDisputesBreakdown ?? [],
        tone: 'red',
        showType: true,
        countLabel: (n) => `${n} refund${n !== 1 ? 's' : ''} / dispute${n !== 1 ? 's' : ''}`,
      } },
    { label: 'Disputes Won', value: money(kpis?.disputesWon), tone: 'pos',
      tip: `Disputed amount won back for ${whoPhrase} in ${periodPhrase}.`,
      breakdown: {
        items: kpis?.disputesWonBreakdown ?? [],
        tone: 'green',
        showType: false,
        countLabel: (n) => `${n} dispute${n !== 1 ? 's' : ''} won`,
      } },
    { label: 'Expected EOM', value: money(kpis?.expectedEOM), tone: 'pos',
      tip: tipFor(kpis?.expectedEOM, `Projected end-of-month total for ${whoPhrase} in ${periodPhrase} — current net in bank plus the still-pending expected inventory.`) },
    { label: 'Current Net In-Bank', value: money(kpis?.currentNetInBank), tone: 'pos',
      tip: tipFor(kpis?.currentNetInBank, `Total received in the bank for ${whoPhrase} in ${periodPhrase}, after deducting refunds and disputes.`) },
    { label: 'Next Month Inventory', value: money(kpis?.nextMonthInventory), tone: 'muted',
      tip: `Recurring inventory already lined up for ${whoPhrase} in ${nextPhrase}.` },
  ];

  // First load (no KPIs yet) → skeleton. Subsequent reloads keep the numbers
  // visible and just dim them (see opacity below), which reads better than
  // flashing a skeleton over data the user was already looking at.
  if (loading && !kpis) {
    return <InventoryKpiBarSkeleton count={cells.length} />;
  }

  // Build the tiles once so the desktop single-row (ScaleToFit) grid and the phone
  // 3-per-row grid render the SAME cells + hover handlers without duplicating them.
  const gridChildren = cells.map((c) => (
            <KpiCellBox
              key={c.label}
              onMouseEnter={(e) => {
                // A breakdown tile WITH rows reveals the rich card; otherwise (or when it
                // has no rows) fall back to the plain descriptive text tip. Same 600ms
                // dwell for both, so the whole strip feels consistent.
                const hasBreakdown = !!c.breakdown && c.breakdown.items.length > 0;
                const text = c.tip;
                if (!hasBreakdown && !text) return;
                const anchor = e.currentTarget.getBoundingClientRect();
                if (kpiTipTimer.current != null) window.clearTimeout(kpiTipTimer.current);
                kpiTipTimer.current = window.setTimeout(() => {
                  if (hasBreakdown && c.breakdown) {
                    setKpiTip(null);
                    setBreakdownTip({
                      anchor,
                      items: c.breakdown.items,
                      tone: c.breakdown.tone,
                      showType: c.breakdown.showType,
                      countLabel: c.breakdown.countLabel(c.breakdown.items.length),
                    });
                  } else {
                    setBreakdownTip(null);
                    setKpiTip({ text, anchor });
                  }
                }, 600);
              }}
              onMouseLeave={() => {
                if (kpiTipTimer.current != null) {
                  window.clearTimeout(kpiTipTimer.current);
                  kpiTipTimer.current = null;
                }
                setKpiTip(null);
                // Delay the breakdown card's hide so the cursor can reach it (the card's
                // own mouseenter cancels this); a no-op when no card is showing.
                scheduleBreakdownHide();
              }}
            >
              <KpiCellLabel>{c.label}</KpiCellLabel>
              <KpiCellValue $tone={c.tone}>{c.value}</KpiCellValue>
            </KpiCellBox>
  ));

  return (
    <KpiBarWrap ref={barRef /* [PHONE-LAYOUT 2026-10-23] phones: close a tip when this strip moves */} style={{ opacity: loading ? 0.55 : 1, transition: 'opacity 180ms cubic-bezier(0.4, 0, 0.2, 1)' }}>
      <KpiBarHead>
        <KpiBarTitle>Performance Snapshot</KpiBarTitle>
        <KpiBarContext>{pm || 'All PMs'}{month ? ` · ${formatMonthLabel(month)}` : ''}</KpiBarContext>
      </KpiBarHead>
      {/* Desktop: keep all KPIs on ONE row (ScaleToFit scales the single row to fit).
          Phones: horizontal-scroll carousel (KpiGridMobile) — 3 tiles per screen at
          full readable size, swipe for the rest. Same tiles either way. */}
      {isMobile ? (
        <KpiGridMobile $land={tilesLand /* [FLUID-6 2026-10-12] */}>{gridChildren}</KpiGridMobile>
      ) : (
        <ScaleToFit contentMinWidth={kpiRowMinWidth(cells.length)}>
          <KpiGrid $land={tilesLand /* [FLUID-6 2026-10-12] */}>{gridChildren}</KpiGrid>
        </ScaleToFit>
      )}
      <KpiInfoTip tip={kpiTip} />
      <KpiBreakdownCard tip={breakdownTip} onEnter={cancelBreakdownHide} onLeave={() => setBreakdownTip(null)} />
    </KpiBarWrap>
  );
};

/* ── Component ── */

export interface InventoryPageProps {
  /** Page title shown in the toolbar. Defaults to "Inventory". */
  title?: string;
  /** Initial PM filter value (the PM dropdown stays visible & changeable). */
  defaultPm?: string;
  /** Initial month (YYYY-MM). Defaults to next month, like the Inventory page. */
  defaultMonth?: string;
  /** Column set to render. Defaults to the full `columns` list above. */
  columns?: ColDef[];
  /** Show the Add button + per-row Delete/renewal actions. Defaults to true. */
  showActions?: boolean;
  /** [MYINV-DOWNLOAD 2026-07-15] Show the "Download record" button independently of
   *  showActions. My Inventory sets showActions={false} (no Add / no row actions)
   *  but still wants the export for admins + seniors, so the two had to split.
   *  Defaults to `showActions`, which preserves the Inventory tab exactly.
   *  NB: named ...Button because `showDownload` is already the modal's state. */
  showDownloadButton?: boolean;
  /** Row `type` values to hide (e.g. ['One-Time']). Count + total reflect the
   *  visible rows. Safe because the page fetches every row (pageSize: 0). */
  excludeTypes?: string[];
  /** [MYINV-EXPORT-RECURRING 2026-09-29, user] Type(s) the "Download record" popup starts with
   *  (its Type picker can still add others). Unset = all types, the Inventory tab's behaviour. */
  exportDefaultTypes?: string[];
  /** Show the Type filter dropdown. Defaults to true. */
  showTypeFilter?: boolean;
  /** Show the Brand filter dropdown. Defaults to true. */
  showBrandFilter?: boolean;
  /** Hide the "All PMs" option so the PM dropdown is limited to the scoped list
   *  (My Inventory for non-admins: just themselves, or self + their team). */
  hideAllPms?: boolean;
  /** Show the KPI snapshot strip above the toolbar (My Inventory). The strip
   *  reflects the selected PM + month. Defaults to false. */
  showKpis?: boolean;
  /** [CAMPAIGN-HISTORY 2026-09-04, user] Make the Website value a link to that campaign's full
   *  history (/campaign-history). My Inventory only — on the Inventory tab a click on the website
   *  opens the website EDITOR, so turning this on there would fight the editor. Defaults to false. */
  websiteOpensHistory?: boolean;
  /** [INV-HISTORY-BTN 2026-09-10, user] A THIRD icon button on the Website cell, after Copy and
   *  Open Stripe Data, that opens the campaign's history in the same tab. For the Inventory tab,
   *  where a click on the website itself opens the editor; the history page's back button then
   *  reads "← Inventory". Defaults to false. */
  historyButton?: boolean;
  /** Audit-log section label for edits made here (e.g. "My Inventory"). When
   *  set, row updates send it so the Application Log attributes them correctly. */
  logSection?: string;
  /** Restrict editing to rows whose own month is the current calendar month
   *  (My Inventory). Past/future months — including ones reached via the month
   *  filter — render their editable columns read-only. Defaults to false. */
  currentMonthEditOnly?: boolean;
  /** [ALL-PM-SENIOR 2026-06-23] [MYINV-FUTURE 2026-06-25] Under currentMonthEditOnly,
   *  these column keys edit on the current month AND any FUTURE month (only PAST months
   *  stay read-only), instead of the current month only. My Inventory uses it to let the
   *  all-PM seniors (Sana/Amaan) edit Status/Discount/At-Risk/PM on this month's and all
   *  upcoming months' rows, while everyone else stays current-month-only. Defaults to none. */
  currentAndFutureMonthEditKeys?: string[];
  /** Lock the whole table to admins: non-admins get a read-only view (no inline
   *  edits, no delete, no add) while filters + search keep working. Used by the
   *  main Inventory tab. Defaults to false — My Inventory keeps its own
   *  PM-scoped edit model untouched. */
  adminEditOnly?: boolean;
  /** [BULK-EDIT 2026-08-03] Restrict the bulk-edit column picker to these keys. Absent →
   *  every bulk-safe column that is editable in this view (the Inventory tab). My Inventory
   *  passes ['pm'] per user request 2026-08-03: bulk edit exists there ONLY to reassign PM
   *  Name, so Status / Discount / At-Risk are deliberately excluded even though they remain
   *  editable one cell at a time. A key listed here still has to be bulk-safe AND editable
   *  for the signed-in user, so this can only ever narrow, never widen. When the result is
   *  empty (e.g. a PM who cannot edit the PM column) the whole selection UI is hidden. */
  bulkEditKeys?: string[];
  /** [MYINV-ROW-TINT 2026-06-25] Tint each row by its CURRENT-month payment status:
   *  red when a payment is fully received yet still has an Unmapped Payments balance
   *  (or the row is missing PM / Company Record Id), amber on a partial payment. ONLY
   *  My Inventory passes this — the main Inventory tab leaves it off and is unaffected
   *  (it keeps just its existing missing-PM red flag). Defaults to false. */
  rowStatusTint?: boolean;
  /** [AM-REMINDER 2026-09-24, user] Show the "Add the AM on your campaigns" banner above the
   *  Performance Snapshot. ONLY My Inventory passes this (user: "only on the My Inventory page,
   *  not anywhere else"). Defaults to false, so the Inventory tab is unchanged. */
  amReminder?: boolean;
  /** [MYINV-PM-NO-BLANK 2026-09-28, user] Once a row HAS a PM Name it can never be made blank
   *  again from this view: the cell dropdown drops its blank "—" option on a row that already
   *  has a PM (switching to ANOTHER name still works), and bulk edit of PM Name cannot write a
   *  blank. A row with no PM yet keeps the blank option, so it can still be filled. ONLY My
   *  Inventory passes this (user chose "My Inventory only") — the Inventory tab still offers
   *  blank. UI-only by the user's choice; the server PUT is unchanged. Defaults to false. */
  pmNoBlankOnceSet?: boolean;
  /** [COL-RESIZE-ALL 2026-09-29, user] Names this view's remembered column widths (Excel-style
   *  drag-to-resize, per person in their own browser): 'inventory' (default) or 'my-inventory',
   *  so the two tabs keep separate widths. */
  columnResizeKey?: string;
}

// Self-contained inline-edit field for text/number/month cells. It keeps the
// in-progress text in its OWN local state so typing never lifts to the parent —
// that is what stops the whole inventory table (and its sort) from re-rendering
// on every keystroke. The parent only sees the value on commit (Enter / blur);
// Esc cancels. `doneRef` guarantees a single commit/cancel even though removing
// the focused input fires a trailing blur. The `select` cells keep their own
// path (they save on change, not on typing), so they are intentionally untouched.
const InventoryEditCell: React.FC<{
  initialValue: string;
  inputType: string;
  onCommit: (value: string) => void;
  onCancel: () => void;
}> = ({ initialValue, inputType, onCommit, onCancel }) => {
  const [draft, setDraft] = useState(initialValue);
  const doneRef = useRef(false);
  const commit = () => { if (doneRef.current) return; doneRef.current = true; onCommit(draft); };
  const cancel = () => { if (doneRef.current) return; doneRef.current = true; onCancel(); };
  return (
    <CellInput
      autoFocus
      type={inputType}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        else if (e.key === 'Escape') cancel();
      }}
    />
  );
};

// [LINK-BTN 2026-07-01] Copy text to the clipboard. Uses the async Clipboard API (available
// on https + localhost, both of which this app runs on) with a hidden-textarea fallback so
// the copy still works if that API is unavailable.
function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);
  return new Promise<void>((resolve, reject) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      resolve();
    } catch (err) {
      reject(err);
    }
  });
}

// [LINK-BTN 2026-07-01] Website / Email cell: the value plus two end-of-cell action buttons
// revealed on hover — Copy (two-sheets icon) copies the value straight to the clipboard (no
// need to open the cell), and Open (external-link arrow) will redirect to a URL the user
// will supply later (action intentionally left blank for now). Used in BOTH the Inventory
// (editable) and My Inventory (read-only) views. Clicking a button never starts the cell's
// inline edit (the clicks stopPropagation).
const LinkCellWrap = styled.span`
  /* Hug the content: the two icons FIRST, a small gap, then the value — so the column is only
     as wide as the icons + the longest value (NOT width:100%, which made the column greedy). */
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;

  .lc-val {
    white-space: nowrap;
  }
  /* [COL-RESIZE-TITLE 2026-09-29] Only inside a column someone DRAGGED narrower (its clamp box,
     [data-dt-cut]): the value may shrink beside the icons and ends in an ellipsis instead of being
     sliced off mid-letter. Untouched columns never match, so they size exactly as before. */
  [data-dt-cut] & .lc-val {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  /* Buttons are visible at all times (not hover-revealed). */
  .lc-actions {
    display: inline-flex;
    gap: 1px;
    flex-shrink: 0;
  }
`;

const LinkBtn = styled.button`
  border: none;
  background: transparent;
  color: ${(p) => p.theme.muted};
  font-size: 11px;
  line-height: 1;
  cursor: pointer;
  padding: 3px 4px;
  border-radius: 4px;
  transition: color 120ms cubic-bezier(0.4, 0, 0.2, 1), background 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
    background: ${(p) => p.theme.pill};
  }

  /* [PHONE-LAYOUT 2026-10-22] Phones: each icon a 28 px tap target (was 18 x 18) with a slightly bigger icon. */
  @media (max-width: 768px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    min-height: 28px;
    font-size: 12px;
  }
`;

/* [CAMPAIGN-HISTORY 2026-09-04, user] On My Inventory the website VALUE is a link to that
   campaign's full history. Deliberately opt-in (`openHistory`) and OFF on the Inventory tab:
   there, clicking a website opens the website editor, and the user asked to leave that alone —
   "on the inventory tab i will add a new button there after the open stripe thing … i will do
   that later". Same tab, per the user; the history page carries a "Go to My Inventory" button
   back. stopPropagation keeps the click off the cell's own edit handler. */
const HistoryLink = styled.a`
  color: inherit;
  text-decoration: none;
  border-bottom: 1px dotted ${(p) => p.theme.borderHover};
  cursor: pointer;
  &:hover { color: ${(p) => p.theme.greenInk}; border-bottom-color: ${(p) => p.theme.greenInk}; }
  &:focus-visible { outline: 2px solid ${(p) => p.theme.greenInk}; outline-offset: 2px; border-radius: 2px; }

  /* [PHONE-LAYOUT 2026-10-22] Phones: a 29 px tall tap target (was 17) with the line height unchanged (the -6 px
     margins cancel the padding); the dotted line is drawn under the text, where the border was. */
  @media (max-width: 768px) {
    display: inline-block;
    padding: 6px 0;
    margin: -6px 0;
    border-bottom: none;
    text-decoration: underline dotted ${(p) => p.theme.borderHover};
    text-underline-offset: 3px;
    &:hover { text-decoration-color: ${(p) => p.theme.greenInk}; }
  }
`;

const LinkCell: React.FC<{ kind: 'website' | 'email'; value: any; openHistory?: boolean; historyButton?: boolean }> = ({ kind, value, openHistory, historyButton }) => {
  const navigate = useNavigate();
  const text = value != null ? String(value).trim() : '';
  if (!text) return <>—</>;
  const copy = (e: React.MouseEvent) => {
    e.stopPropagation();
    copyText(text)
      .then(() => toastSuccess(`${kind === 'email' ? 'Email' : 'Website'} copied`))
      .catch(() => {});
  };
  const open = (e: React.MouseEvent) => {
    e.stopPropagation();
    // [LINK-BTN 2026-07-01] Open THIS row's website in Stripe's dashboard org-search — the
    // website is substituted (URL-encoded) into the `query` param. Website column only.
    const url = `https://dashboard.stripe.com/acct_1QGLr0DESMDzGXdo/search?query=${encodeURIComponent(text)}&search_context_id=org_6U5mmwSLSQ5AMAMtTYkNTTU&type=organization`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  // [INV-HISTORY-BTN 2026-09-10, user] `from=inventory` tells the history page to send its back
  // button to the Inventory tab. A real <a href>, so ctrl/cmd-click still opens a new tab; a plain
  // click navigates in-app, in the same tab. stopPropagation comes FIRST, on every click, so the
  // cell's own handler never opens the website editor underneath.
  const historyHref = `/campaign-history?website=${encodeURIComponent(text)}&from=inventory`;
  const openHistoryBtn = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    navigate(historyHref);
  };
  return (
    <LinkCellWrap>
      <span className="lc-actions">
        <LinkBtn type="button" title={`Copy ${kind}`} aria-label={`Copy ${kind}`} onClick={copy}>
          <i className="fas fa-copy" />
        </LinkBtn>
        {/* Open-link (redirect) button is Website-only — Email shows just the Copy icon. */}
        {kind === 'website' && (
          <LinkBtn type="button" title="Open Stripe Data" aria-label="Open Stripe Data" onClick={open}>
            <i className="fas fa-arrow-up-right-from-square" />
          </LinkBtn>
        )}
        {/* [INV-HISTORY-BTN 2026-09-10] Third button, Website-only, opt-in (the Inventory tab). */}
        {kind === 'website' && historyButton && (
          <LinkBtn as="a" href={historyHref} title="See campaign history" aria-label="See campaign history" onClick={openHistoryBtn}>
            <i className="fas fa-clock-rotate-left" />
          </LinkBtn>
        )}
      </span>
      {/* [COL-RESIZE-TITLE 2026-09-29] data-dt-cut-inner: in a dragged column the cut happens HERE
          (see LinkCellWrap), so the resize hook checks this element for the full-text hover. */}
      <span className="lc-val" data-dt-cut-inner="">
        {kind === 'website' && openHistory ? (
          <HistoryLink
            href={`/campaign-history?website=${encodeURIComponent(text)}`}
            title={`See the full history for ${text}`}
            onClick={(e) => {
              // Plain left-click navigates in-app; ctrl/cmd/middle-click keeps the browser's
              // own "open in a new tab", which a real <a href> gives us for free.
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
              e.preventDefault();
              e.stopPropagation();
              navigate(`/campaign-history?website=${encodeURIComponent(text)}`);
            }}
          >
            {text}
          </HistoryLink>
        ) : text}
      </span>
    </LinkCellWrap>
  );
};

/**
 * [BULK-EDIT 2026-08-03] One table row, memoised.
 *
 * WHY this exists: the <tbody> is memoised as a block (see the tableBody useMemo) so
 * unrelated page state — opening a modal, a delete dialog — can't re-render hundreds of
 * rows. Selection breaks that model: `selectedIds` genuinely changes on every checkbox
 * click, so as a plain memo dep it would re-render EVERY row on EVERY tick of a
 * multi-row selection — exactly the click-lag the tbody memo was added to kill.
 *
 * Pushing the memo down to the row fixes it: the tbody still rebuilds its (cheap)
 * element list, but React.memo means only the row whose `selected` flipped actually
 * re-renders. Same trick for `editing` — passing this row's editing key rather than the
 * page-wide editing object means opening a cell repaints one row, not the table.
 *
 * `editingKey`, `editValue`, `filters` and `allBrands` are intentionally NOT read here:
 * renderCell reads the live values through helpersRef (reassigned every parent render,
 * so never stale). They are declared as props purely so React.memo's shallow compare
 * still re-renders this row when they change.
 */
interface InvRowProps {
  row: InventoryRow;
  cols: ColDef[];
  showActions: boolean;
  viewOnly: boolean;
  rowStatusTint: boolean;
  /** Bulk selection is available on this page at all. */
  selectable: boolean;
  /** This row may be bulk-edited (in scope / editable month). Unselectable rows show a
   *  disabled checkbox so the reason is visible rather than the row silently ignoring clicks. */
  rowSelectable: boolean;
  selected: boolean;
  onToggle: (id: number) => void;
  // [MOTION-B 2026-08-20] Part of the memo comparison, so the bloom repaints only its own row.
  flash?: boolean;
  editingKey: string | null;
  editValue: string;
  filters: InventoryFiltersResponse;
  allBrands: string[];
  helpersRef: React.MutableRefObject<any>;
  // [COL-RESIZE-ALL 2026-09-29] column key -> width variable for dragged columns (stable between drags).
  cut?: Record<string, string>;
}

// [COL-RESIZE-ALL 2026-09-29] The body cell's left + right padding (Td pads 6px a side).
const INV_CELL_PAD_X = 12;

const InvRow = React.memo<InvRowProps>(({
  row, cols, showActions, viewOnly, rowStatusTint, selectable, rowSelectable, selected, onToggle, flash, helpersRef,
  editingKey, cut,
}) => {
  const h = helpersRef.current;
  // [CELL-PRESS 2026-10-13, user] A dropdown cell opens on the mouse PRESS — only while no cell is open: an open editor
  // saves on its blur, which that press's own mousedown causes, so then the click path stays and nothing typed is lost.
  // The press is cancelled (no mousedown), so the new list's "click outside" never sees it. Touch / keyboard: the click.
  const cellPressedRef = useRef(false);
  const editable = h.isRowEditable(row);
  const flagged = !(row.pm && String(row.pm).trim()) || !(row.company_record_id && String(row.company_record_id).trim());
  const tint = rowStatusTint ? myInventoryRowTint(row) : undefined;
  return (
    <Tr
      $flash={flash}
      data-flip-key={String(row.id)}
      style={{ opacity: editable ? 1 : 0.7, background: flagged ? 'rgba(239,68,68,0.15)' : undefined }}
    >
      {selectable && (
        <SelectTd>
          <RowCheckbox
            type="checkbox"
            checked={selected}
            disabled={!rowSelectable}
            onChange={() => onToggle(row.id)}
            title={rowSelectable ? 'Select row' : 'This row is not editable from here'}
            aria-label={`Select ${row.website || 'row'}`}
          />
        </SelectTd>
      )}
      {cols.map((col) => {
        const content = h.renderCell(row, col);
        const isDash = content === '—';
        // [COL-RESIZE-ALL 2026-09-29] A dragged column's cell content sits in the clamp box, except
        // the cell being edited right now (its editor must never be cut). A dragged column drops its
        // min width so it can go narrower than the default.
        const v = cut?.[col.key];
        const align = isDash ? 'center' : col.align;
        return (
          <Td
            key={col.key}
            $align={align}
            $tint={tint}
            onPointerDown={(e) => {
              const hp = helpersRef.current;
              cellPressedRef.current = col.type === 'select' && isPlainMousePress(e) && !hp.isEditingAny() && hp.isCellEditable(row, col);
              if (cellPressedRef.current) { e.preventDefault(); hp.startEdit(row, col); }
            }}
            onClick={(e) => {
              if (clickEndsPress(cellPressedRef, e)) return; // the press already opened it
              helpersRef.current.startEdit(row, col);
            }}
            style={{ cursor: h.isCellEditable(row, col) ? 'pointer' : 'default', minWidth: v ? undefined : col.width }}
          >
            {v && editingKey !== col.key
              ? <CutCell data-dt-cut="" style={cutCellStyle(v, INV_CELL_PAD_X, align)}>{content}</CutCell>
              : content}
          </Td>
        );
      })}
      {showActions && !viewOnly && (
        <Td $align="center" style={{ display: 'flex', gap: '2px', justifyContent: 'center', minWidth: '70px' }}>
          {row.type === 'PIA' && (
            <ActionBtn
              onClick={() => helpersRef.current.openRenewalModal(row)}
              title={row.renewal_status ? `PIA: ${row.renewal_status}` : 'Set PIA renewal status'}
              style={row.renewal_status ? { color: '#f59e0b' } : undefined}
            >
              <i className="fas fa-pen"></i>
            </ActionBtn>
          )}
          {/* [INV-SPLIT 2026-10-07, user] Admins, current month: split a website between PMs, or undo it. */}
          {h.canUnsplitRow?.(row) && (
            <SplitActionBtn onClick={() => helpersRef.current.openUnsplit(row)} title="Merge the PM parts back into one row">
              Undo split
            </SplitActionBtn>
          )}
          {h.canSplitRow?.(row) && (
            <SplitActionBtn onClick={() => helpersRef.current.openSplit(row)} title="Split this website between PMs">
              Split
            </SplitActionBtn>
          )}
          {editable && !(Number(row.split_part) > 0) && (
            <DeleteBtn onClick={() => helpersRef.current.handleDelete(row.id, row.website)} title="Delete row">
              <i className="fas fa-trash-alt"></i>
            </DeleteBtn>
          )}
        </Td>
      )}
    </Tr>
  );
});
InvRow.displayName = 'InvRow';

const InventoryPage: React.FC<InventoryPageProps> = ({
  title = 'Inventory',
  defaultPm = '',
  defaultMonth,
  columns: propColumns,
  showActions = true,
  showDownloadButton,
  excludeTypes,
  exportDefaultTypes, // [MYINV-EXPORT-RECURRING 2026-09-29]
  showTypeFilter = true,
  showBrandFilter = true,
  hideAllPms = false,
  showKpis = false,
  websiteOpensHistory = false, // [CAMPAIGN-HISTORY 2026-09-04] My Inventory only
  historyButton = false, // [INV-HISTORY-BTN 2026-09-10] Inventory tab only
  logSection,
  currentMonthEditOnly = false,
  currentAndFutureMonthEditKeys,
  adminEditOnly = false,
  bulkEditKeys, // [BULK-EDIT 2026-08-03]
  rowStatusTint = false,
  amReminder = false, // [AM-REMINDER 2026-09-24] My Inventory only
  pmNoBlankOnceSet = false, // [MYINV-PM-NO-BLANK 2026-09-28] My Inventory only
  columnResizeKey = 'inventory', // [COL-RESIZE-ALL 2026-09-29]
}) => {
  // Manual Add is admin-only (same policy as Payments Add). Non-admins never see
  // the button, and the backend POST /api/inventory is gated by requireAdmin.
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  // [DOWNLOAD-ACCESS 2026-07-30] Export is an admin/senior capability on EVERY surface.
  const isSenior = !!user?.is_senior;
  // [MOBILE 2026-07-07] Phones (≤768px) get a distinct three-row toolbar layout
  // (search+count / primary filters / status+actions). Presentation-only — the
  // desktop toolbar below 769px is byte-for-byte unchanged.
  const isMobile = useIsMobile();
  // Main Inventory tab: non-admins get a fully read-only table — every inline
  // edit, the per-row delete, and the renewal action are suppressed (filters +
  // search still work). My Inventory leaves adminEditOnly off, so its PM-scoped
  // editing is unaffected.
  const viewOnly = adminEditOnly && !isAdmin;
  // Effective column set — a parametrized view (e.g. "My Inventory") can pass a
  // subset with its own editability; everything below renders from `cols`.
  const cols = propColumns ?? columns;
  // Landing month: configurable (a parametrized view can pass its own) but defaults
  // to the CURRENT month. [INV-MONTH 2026-06-29] Was next month (current + 1, where the
  // auto-generated renewal rows live) — changed per request to land on the current month.
  const initialMonth = defaultMonth || getCurrentMonth();
  const [data, setData] = useState<InventoryRow[]>([]);
  // pageSize: 0 tells the API to return every matching row (pagination removed).
  // The row count + totals shown come from the client-filtered set (see visibleData below),
  // not this server-page meta — only the setter is kept so load()/resetPage keep tracking page.
  const [, setPagination] = useState<PaginationMeta>({ page: 1, pageSize: 0, totalRows: 0, totalPages: 0 });
  // [PLACED-MONTH-PM 2026-07-01] The toolbar "Placed Inventory" figure must reflect the
  // WHOLE month's placed value for the selected PM — independent of the Status (and
  // Type/Brand/Search) filters — so switching the status dropdown never changes it. It's
  // fetched month+PM-only alongside the main (status-filtered) table load below. `null`
  // means "not applicable" (cross-month search / all-months) → fall back to the visible
  // set's total.
  const [placedTotal, setPlacedTotal] = useState<number | null>(null);
  // [LOAD-STATES 2026-09-29, user] A failed load used to show "Placed Inventory : $0.00" and "No records
  // found". Now the figure shows "—" and the table the "Couldn't load inventory" countdown card.
  const [loadFailed, setLoadFailed] = useState(false);
  // Read the current excludeTypes from inside load() WITHOUT putting it in load()'s deps:
  // a parent (a parametrized view) may pass a fresh array literal each render, which
  // would churn load()'s identity and re-trigger a non-silent load → skeleton flash
  // (see the skeleton-flash invariant). A ref sidesteps that.
  const excludeTypesRef = useRef(excludeTypes);
  useEffect(() => { excludeTypesRef.current = excludeTypes; }, [excludeTypes]);
  const [sort, setSort] = useState<SortConfig>({ sortBy: 'website', sortOrder: 'asc' });
  const [search, setSearch] = useState('');
  // [SINGLE-SELECT] 2026-06-23 — Toolbar filters are single-select dropdowns.
  // State is still kept as an array (held at length 0 or 1) so the API payload,
  // KPI derivations and option-pinning logic all stay array-shaped unchanged; an
  // empty array means "all" (no filter). Month defaults to the landing month; PM
  // defaults to the configured default (e.g. My Inventory's own name), if any.
  const [months, setMonths] = useState<string[]>(initialMonth ? [initialMonth] : []);
  const [typeFilters, setTypeFilters] = useState<string[]>([]);
  const [pmFilters, setPmFilters] = useState<string[]>(defaultPm ? [defaultPm] : []);
  const [brandFilters, setBrandFilters] = useState<string[]>([]);
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  // [AM-NAMES 2026-09-04, user] The AM toolbar filter is SINGLE-select by the user's ruling
  // (multi-select belongs to the row cell), so unlike its neighbours this is a plain string
  // rather than a 0-or-1 array — '' means "All AMs". It asks "which rows is this AM on",
  // which is a CONTAINS test against the row's ', '-joined list, not an equality test.
  const [amFilter, setAmFilter] = useState<string>('');
  // Single-value derivations: the KPI strip and the Add-button gating only make
  // sense for one month/PM, so they activate when exactly one is chosen.
  const kpiMonth = months.length === 1 ? months[0] : '';
  const kpiPm = pmFilters.length === 1 ? pmFilters[0] : '';
  const [filters, setFilters] = useState<InventoryFiltersResponse>({ types: [], pms: [], brands: [], statuses: [], months: [], ams: [] });
  const [loading, setLoading] = useState(true);
  const loadRequestIdRef = useRef(0);
  // [FLUID-6 2026-10-12, user] "inventory and my inventory is not having the animation there in the data". Counts the
  // times rows land for the person to see: the first load (a tab visit), and every month / type / PM / AM / brand /
  // status / search change, a retry and an Add. Each count runs the row wave on the table (landRef, below): every row
  // on screen rises in, top to bottom (components/Fluid.tsx landRows). Bumped ONCE per landing — by a cached copy
  // shown at once, or by the rows that replace the skeleton (landPendingRef = the skeleton is up) — and never by a
  // silent refresh, a save, an inline edit or a sort, so those never replay it.
  const [landing, setLanding] = useState(0);
  const landPendingRef = useRef(false);
  // [AM-REMINDER-SKELETON 2026-09-24, user] The view (amViewKeyOf) the rows in `data` belong to,
  // stamped by load() as they land. See amSettled below.
  const amLoadedKeyRef = useRef('');
  // The view whose load FAILED (its rows never landed): the skeleton ends there, but nothing
  // is saved for it, because `data` still holds another view's rows (or none).
  const amFailedKeyRef = useRef('');
  const editingRef = useRef(false);

  // [EDIT-RACE 2026-07-01] Rows with an in-flight (or just-committed) inline write are
  // "protected": a background/silent full-table reload must NOT overwrite their optimistic
  // value with a server snapshot whose read hadn't caught up yet — THAT was the rapid-edit
  // flicker (value → 0 → old value → 0). Map is rowId → protect-until epoch ms: Infinity
  // while the write is in flight, then a short grace window after it settles so a reload that
  // was already in flight when the write finished still can't revert the freshly-saved value.
  const rowProtectRef = useRef<Map<number, number>>(new Map());
  const protectTimersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const protectRow = (id: number) => {
    const t = protectTimersRef.current.get(id);
    if (t) { clearTimeout(t); protectTimersRef.current.delete(id); }
    rowProtectRef.current.set(id, Infinity);
  };
  const releaseRow = (id: number) => {
    rowProtectRef.current.set(id, Date.now() + 1500);
    const prev = protectTimersRef.current.get(id);
    if (prev) clearTimeout(prev);
    protectTimersRef.current.set(id, setTimeout(() => {
      rowProtectRef.current.delete(id);
      protectTimersRef.current.delete(id);
    }, 1600));
  };
  useEffect(() => () => { protectTimersRef.current.forEach((t) => clearTimeout(t)); }, []);

  // Editing state: { rowId-colKey: editingValue }
  const [editing, setEditing] = useState<{ id: number; key: string } | null>(null);

  // [MOTION-B 2026-08-20] The row that was just saved, so it can bloom briefly and the eye
  // lands on the change. Set to null for one tick first, otherwise editing the SAME row twice
  // is no state change and the CSS animation - already parked at its end state - never replays.
  const [flashId, setFlashId] = useState<number | null>(null);
  const flashTimer = useRef<number | null>(null);
  const flashRow = useCallback((id: number) => {
    if (flashTimer.current != null) window.clearTimeout(flashTimer.current);
    setFlashId(null);
    window.setTimeout(() => setFlashId(id), 0);
    flashTimer.current = window.setTimeout(() => { setFlashId(null); flashTimer.current = null; }, 1400);
  }, []);
  useEffect(() => () => { if (flashTimer.current != null) window.clearTimeout(flashTimer.current); }, []);
  // Mirror `editing` into a ref so the auto-refresh interval can read the latest
  // value without resetting its timer on every keystroke.
  useEffect(() => { editingRef.current = editing !== null; }, [editing]);
  const [editValue, setEditValue] = useState('');
  // The cell's value when editing began — used to detect a real user change so
  // clicking through cells without typing never writes (and a background
  // refresh mid-edit can't make an untouched cell look changed).
  const editOriginalRef = useRef('');
  // [AM-NAMES 2026-09-04, user] The AM Names editor is the one cell that COMMITS ON CLOSE
  // rather than on each pick (ticking three names must be ONE write and ONE audit entry,
  // not three). Its in-progress value therefore has to survive until the popover closes,
  // and reading it out of a render closure would risk committing a stale selection — so it
  // lives here. Seeded in startEdit, updated on every tick, read once in onClose.
  const amDraftRef = useRef('');
  // [AM-NAMES 2026-09-04] One-shot latch for that commit. GlassSelect fires onClose from
  // inside a setState updater, and React StrictMode (on — see index.tsx) double-invokes
  // those in development, so the handler can run TWICE for one close. Every other consumer
  // passes cancelEdit there, which is harmless twice; a COMMIT is not — it would fire two
  // PUTs and log two identical audit entries. Latched here rather than fixed in GlassSelect,
  // which would mean moving the notify out of the updater for every caller in the app.
  const amCommittedRef = useRef(false);

  /*
   * [EDIT QUEUE 2026-09-03, user] Cell edits are applied ONE AT A TIME.
   *
   * Each edit used to fire its own request the moment it was committed, so editing three cells
   * quickly put three writes in flight together. Every inventory write fires
   * `trg_inventory_after_update_payments`, which updates `payments` — so concurrent edits
   * compete for the same rows as the background jobs (inventoryCalc / AutoMap /
   * autoInventoryReconciler). That contention is real, not theoretical: the server log holds
   * 1,555 "Deadlock found when trying to get lock" errors, up to 97 in a single day. A deadlocked
   * write comes back as a 500 and the user sees "Save failed" on an edit that was perfectly valid.
   *
   * This chains every save onto the previous one, so N edits become N sequential writes: they all
   * land, in the order they were made, and none of them can deadlock against each other. The UI
   * is unaffected — the optimistic paint still happens immediately, so typing stays instant.
   *
   * [SAVE-QUEUE 2026-09-09, user] The queue itself now lives in hooks/useSaveQueue (shared with
   * Payments), and EVERY write on this page goes through it — the dropdown editor, the renewal
   * modal and the bulk edit used to bypass it and fire straight at the API. It also reports how
   * many writes were ahead when an edit was added, so the user is told "added to queue" when
   * they have outrun the network, and "updated" when the write actually lands.
   */
  const { enqueue: enqueueWrite } = useSaveQueue();
  const enqueueSave = <T,>(job: () => Promise<T>, what = 'Change'): Promise<T> => {
    const { promise, ahead } = enqueueWrite(job);
    if (ahead > 0) queuedToast(what, ahead);
    return promise;
  };

  // Delete confirmation (liquid-glass dialog)
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; website: string } | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Renewal modal state (PIA rows only)
  const [renewalRow, setRenewalRow] = useState<InventoryRow | null>(null);
  const [renewalStatus, setRenewalStatus] = useState('');
  const [renewalMonth, setRenewalMonth] = useState('');
  const [renewalAmount, setRenewalAmount] = useState('');
  const [renewalSaving, setRenewalSaving] = useState(false);

  // Add-row modal state
  const emptyAddForm = {
    // [AM-NAMES 2026-09-04, user] Held as the same ', '-joined string the column stores, so
    // the value the form collects is the value that lands in the database — no translation
    // step between the Add modal and the cell editor to drift apart.
    website: '', email: '', num_campaigns: '', type: 'Recurring', pm: '', am_names: '', brand: '',
    amount: '', payment_type: '', billing_date: '', month: '', status: 'Expected',
  };
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ ...emptyAddForm });
  const [addSaving, setAddSaving] = useState(false);

  // [INVENTORY-CSV-EXPORT 2026-07-03] "Download record" modal is a self-contained
  // component (DownloadRecordsModal) with its OWN state — see its definition for
  // why (isolates re-renders from this page + its DataTable). This page only holds
  // the open/closed flag.
  const [showDownload, setShowDownload] = useState(false);

  /* ── [BULK-EDIT 2026-08-03] Multi-row selection state ──
     `selectedIds` holds row ids only, never row objects, so it can't go stale against a
     background reload. It is cleared whenever the visible set changes (filters/search) —
     see the effect below — so a selection can never survive into a set the user isn't
     looking at. */
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkField, setBulkField] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  // The last applied batch, kept so the bar can offer a one-click revert. Cleared on
  // undo, on the next bulk edit, and whenever the filters move.
  const [undoBatch, setUndoBatch] = useState<{ batchId: string; label: string; count: number } | null>(null);
  const [undoBusy, setUndoBusy] = useState(false);

  // [FLUID-3 2026-10-12, user] Every window on this page stays mounted through its 0.18 s close, so the close can
  // play (they used to vanish in one frame); each is "open" for the motion one frame after it mounts. The state
  // above stays the only source of truth for whether a window is open. downloadSeq keys each Download opening, so
  // one reopened while the last is still closing starts from fresh choices, as it always did.
  const renewalPres = usePresence(renewalRow, fluid.dialogOutMs);
  const renewalLand = useLandWindow(!!renewalRow, String(renewalRow?.id ?? ''));
  const addPres = usePresence(isAdmin && showAddModal, fluid.dialogOutMs);
  const downloadPres = usePresence(showDownload, fluid.dialogOutMs);
  const [downloadSeq, setDownloadSeq] = useState(0);
  const bulkPres = usePresence(showBulkModal, fluid.dialogOutMs);

  // Toolbar Type / Brand / Status dropdowns are scoped to the month(s) in view so
  // they only offer values that actually exist there — e.g. June shows its ~4
  // statuses, not every legacy status accumulated across all of history. Refetched
  // whenever the month selection changes. The `months` list it returns is always
  // the full set (you can still switch to any month) and `pms` is the HubSpot
  // roster (deliberately not data-scoped — HubSpot is the PM source of truth).
  // A currently-selected value that isn't in the scoped list is pinned in at
  // render time (see *Options below) so it stays visible and un-checkable.
  // [PM-FILTER-PAST 2026-10-26, user] The toolbar PM filter's names come from here too
  // (`filterPms`): a PAST month lists the PMs on that month's rows (people who have left
  // HubSpot included), the current month and later the live roster. So an answer is applied
  // only while its month is still the one in view — a slow answer for the month just left
  // must never land over the new month's PM list. A tab that hides types (My Inventory)
  // asks for the names on the rows it shows; sent for a past month only, so the current
  // month's request stays exactly the one the idle prefetch warms.
  const filtersMonthsRef = useRef('');
  const filtersOpts = (ms: string[]) => {
    const ex = excludeTypesRef.current;
    return ex && ex.length && ms.length > 0 && ms.every((m) => m < getCurrentMonth())
      ? { excludeTypes: ex }
      : undefined;
  };
  useEffect(() => {
    const key = months.join(',');
    filtersMonthsRef.current = key;
    fetchInventoryFilters(months, filtersOpts(months))
      .then((f) => { if (filtersMonthsRef.current === key) setFilters(f); })
      .catch(console.error);
  }, [months]);

  // The brand CELL editor keeps the FULL brand list (across every month) so
  // editing a row's brand is never limited to brands already used in the month
  // currently in view. Fetched once; the toolbar filter uses the scoped list above.
  const [allBrands, setAllBrands] = useState<string[]>([]);
  useEffect(() => {
    fetchInventoryFilters().then((f) => setAllBrands(f.brands)).catch(() => {});
  }, []);

  // Load data
  const load = useCallback(async (silent = false) => {
    const reqId = ++loadRequestIdRef.current;
    // [SKELETON 2026-07-01] Whether the table skeleton shows is decided per-call (the `silent`
    // arg), not by a shared mutable flag — a background reload can no longer race a filter-
    // change load and suppress its skeleton. A filter/search/PM/month change calls load()
    // (silent=false → skeleton); the interval / post-write / realtime refresh calls load(true).
    // [TAB-FAST 2026-08-13] Seed from the SWR cache so a revisit (or a warmed first open)
    // shows rows immediately instead of a skeleton. The params MUST mirror the fetch below
    // exactly or the key never matches — cacheKey drops the empty arrays and the undefined
    // search, so this is the same key the idle prefetch warms.
    //
    // GUARDED ON rowProtect: [EDIT-RACE 2026-07-01] keeps a just-edited row from being
    // reverted by a server read that hadn't caught up, and the merge that enforces it only
    // runs on the real response below. Seeding while any row is inside its write-protection
    // window would paint the stale cached value over a fresh edit — the exact bug that fix
    // exists to prevent — so when anything is protected we skip the seed and show the
    // skeleton, as before. On mount (the case this is for) nothing is ever protected.
    const seed = !silent && rowProtectRef.current.size === 0
      ? peekCache<{ data: InventoryRow[]; pagination: PaginationMeta }>('/inventory', {
          page: 1,
          pageSize: 0,
          month: months,
          type: typeFilters,
          pm: pmFilters,
          brand: brandFilters,
          status: statusFilters,
          // [AM-NAMES 2026-09-04] MUST mirror the fetch below exactly or the seed key never
          // matches and every revisit shows a skeleton. cacheKey drops empty values, so an
          // unset AM filter keys identically to before this column existed.
          am: amFilter || undefined,
          search: search || undefined,
        })
      : undefined;
    // [AM-REMINDER-SKELETON 2026-09-24, user] The view this load is for (same filters as the fetch).
    const viewKey = amViewKeyOf(months, typeFilters, pmFilters, brandFilters, statusFilters, amFilter, search);
    if (!silent) {
      if (seed) {
        amLoadedKeyRef.current = viewKey; amFailedKeyRef.current = ''; setData(seed.data); setPagination(seed.pagination); setLoading(false);
        landPendingRef.current = false; setLanding((n) => n + 1); // [FLUID-6 2026-10-12] the cached rows rise in now
      } else {
        setLoading(true);
        landPendingRef.current = true; // [FLUID-6 2026-10-12] the rows that replace this skeleton rise in
      }
    }
    try {
      // Second fetch: the toolbar "Placed Inventory" total, scoped to month + PM ONLY
      // (no status/type/brand/search) so it shows the whole month's placed value and
      // moves only when the month or PM changes. Only meaningful for a single selected
      // month and outside search — otherwise `null`, and the display falls back to the
      // visible set's total. Runs on every load() trigger (filter change, interval,
      // post-write, realtime) so it tracks writes without its own subscriptions.
      const ex = excludeTypesRef.current;
      const placedScoped = !search && months.length === 1;
      const [res, placedRes] = await Promise.all([
        fetchInventory({
          page: 1,
          pageSize: 0,
          // Sorting is done client-side (over the full fetched set), so the sort
          // selection is intentionally NOT sent — changing it must not refetch.
          // Each filter is sent as an array → the backend matches with IN(...).
          month: months,
          type: typeFilters,
          pm: pmFilters,
          brand: brandFilters,
          status: statusFilters,
          // [AM-NAMES 2026-09-04, user] One name or nothing — the server matches the whole
          // delimited token inside the row's ', '-joined list.
          am: amFilter || undefined,
          search: search || undefined,
        }),
        placedScoped
          // Rows are only needed when some types are hidden (excludeTypes — My Inventory hides
          // One-Time + PIA, [MYINV-RECURRING-ONLY 2026-09-24]); otherwise the server's
          // SUM(amount) total suffices, so pageSize:1 keeps that fetch light.
          ? fetchInventory({ page: 1, pageSize: ex && ex.length ? 0 : 1, month: months, pm: pmFilters })
          : Promise.resolve(null),
      ]);
      // Superseded load: don't clear `loading` (that would flash "No records
      // found" before the winning load fills the table). The winner clears it.
      if (reqId !== loadRequestIdRef.current) return;
      amLoadedKeyRef.current = viewKey; // [AM-REMINDER-SKELETON 2026-09-24]
      amFailedKeyRef.current = '';

      // [EDIT-RACE 2026-07-01] Merge, don't clobber: keep the LOCAL (optimistic / just-saved)
      // copy of any row still inside its write-protection window, so a reload whose server read
      // hadn't caught up can't briefly revert a freshly-edited value. Every other row takes the
      // fresh server copy. With no protected rows this is a plain replace, exactly as before.
      setData((prev) => {
        const prot = rowProtectRef.current;
        if (prot.size === 0) return res.data;
        const now = Date.now();
        const localById = new Map(prev.map((r) => [r.id, r]));
        return res.data.map((sr) => {
          const until = prot.get(sr.id);
          return until && until > now && localById.has(sr.id) ? (localById.get(sr.id) as InventoryRow) : sr;
        });
      });
      setPagination(res.pagination);
      if (placedRes) {
        if (ex && ex.length) {
          // Exclude the hidden types so the figure matches what the view actually shows.
          const hidden = new Set(ex.map((t) => t.trim().toLowerCase()));
          setPlacedTotal(
            placedRes.data
              .filter((r) => !hidden.has(String((r as any).type ?? '').trim().toLowerCase()))
              .reduce((sum, r) => sum + (Number((r as any).amount) || 0), 0),
          );
        } else {
          setPlacedTotal(placedRes.totalAmount || 0);
        }
      } else {
        setPlacedTotal(null);
      }
      setLoadFailed(false); // [LOAD-STATES 2026-09-29]
      setLoading(false);
      // [FLUID-6 2026-10-12] Rows landing where the skeleton (or the "Couldn't load" card) was rise in — whichever load
      // brings them, even a live refresh that overtook the skeleton's own load. A cached copy already did its wave.
      if (landPendingRef.current) { landPendingRef.current = false; setLanding((n) => n + 1); }
    } catch (err) {
      if (reqId !== loadRequestIdRef.current) return;
      if (!silent) console.error('Failed to load inventory:', err);
      // [LOAD-STATES 2026-09-29, user] A quiet refresh keeps the table; a real failed load shows the
      // countdown card unless a cached copy of this exact view is already showing.
      if (!silent) { setLoadFailed(true); if (!seed) { setData([]); setPlacedTotal(null); } }
      // [AM-REMINDER-SKELETON 2026-09-24] A failed load ends the skeleton too, or the banner's
      // placeholder would shimmer on above a finished table until the next reload. It is marked
      // as failed, NOT as loaded, so no answer is saved from rows that are not this view's.
      amFailedKeyRef.current = viewKey;
      setLoading(false);
    }
  }, [months, typeFilters, pmFilters, brandFilters, statusFilters, amFilter, search]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 60s. Skip while the tab is hidden (no point fetching for
  // an unfocused tab) or while a cell is being edited (so a background refresh
  // never clobbers an in-progress edit). Runs silently — no loading spinner.
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.hidden || editingRef.current) return;
      load(true);
      // Refresh the (month-scoped) dropdown lists so newly-landed rows fold in —
      // a newly-created month appears in the always-full month list, and any new
      // type/brand/status for the month in view becomes selectable. An active
      // selection survives: out-of-list picks are pinned in at render time.
      // [PM-FILTER-PAST 2026-10-26, user] Same month guard + options as the month effect above.
      const key = months.join(',');
      fetchInventoryFilters(months, filtersOpts(months))
        .then((f) => { if (filtersMonthsRef.current === key) setFilters(f); })
        .catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, [load]);

  // Live refresh: after any successful write, silently reload so the footer
  // totals (a server-side aggregate) and any rows the backend recomputed track
  // the change immediately — inline edits/deletes only patch local state, so
  // without this the footer total lagged until the 60s tick. Skipped while a
  // cell is being edited so a refresh never clobbers an in-progress edit.
  useEffect(() => onDataChange(() => {
    if (editingRef.current) return;
    load(true);
  }), [load]);

  // [REALTIME] 2026-06-23 — same silent reload when the server signals an
  // inventory-affecting DB change from ANY source (sync, reconciler, another
  // user, a maintenance script), so the table + KPIs track it within seconds.
  useRealtime(['inventory'], () => {
    if (editingRef.current) return;
    load(true);
  });

  // [INV-PM-RANK 2026-09-21, user] The PM Recommendations ranking for the PM Name dropdown, one
  // fetch per inventory month in view, kept as month → row id → ranked PMs. Fetched only when this
  // viewer can edit the PM column (the server serves it to admins + seniors only; anyone else gets
  // an empty ranking and the plain list). Search mode spans months, so it ranks the months its rows
  // sit in, from 2026 on like the month dropdown. Refetched on the same signals the table reloads
  // on — this tab's own writes and the live inventory version — and deliberately NOT held back
  // while a cell is open: the open PM dropdown draws from the snapshot it opened with
  // (pmRankingForEdit), so a fetch landing mid-choice can never reorder the list under the pointer.
  const pmColEditable = !viewOnly && cols.some((c) => c.key === 'pm' && c.editable);
  const pmRankKey = useMemo(() => {
    if (!pmColEditable) return '';
    const list = months.length
      ? months
      : [...new Set(data.map((r) => String(r.month || '').slice(0, 7)))]
          .filter((m) => /^\d{4}-\d{2}$/.test(m) && m >= MIN_DROPDOWN_MONTH);
    return [...list].sort().join(',');
  }, [pmColEditable, months, data]);
  const [pmRanking, setPmRanking] = useState<Record<string, InventoryPmRanking['rows']>>({});
  const pmRankReqRef = useRef(0);
  const loadPmRanking = useCallback(() => {
    if (!pmRankKey) return;
    const reqId = ++pmRankReqRef.current;
    const list = pmRankKey.split(',');
    // A month that fails keeps what it had — the dropdown falls back to the plain list, never breaks.
    Promise.all(list.map((m) => fetchInventoryPmRanking(m).catch(() => null))).then((res) => {
      if (reqId !== pmRankReqRef.current) return; // superseded by a newer month / refresh
      setPmRanking((prev) => {
        const next: Record<string, InventoryPmRanking['rows']> = {};
        list.forEach((m, i) => {
          const r = res[i];
          if (r) next[m] = r.rows;
          else if (prev[m]) next[m] = prev[m];
        });
        return next;
      });
    });
  }, [pmRankKey]);
  useEffect(() => { loadPmRanking(); }, [loadPmRanking]);
  useEffect(() => onDataChange(loadPmRanking), [loadPmRanking]);
  useRealtime(['inventory'], loadPmRanking);

  // The ranking an OPEN PM dropdown was drawn from — taken when the cell opened (a new `editing`
  // object per open) and frozen until it closes.
  const pmEditSnapRef = useRef<{ editing: object; ranked: InventoryPmRanking['rows'][string] | null } | null>(null);
  const pmRankingForEdit = (row: InventoryRow): InventoryPmRanking['rows'][string] | null => {
    if (!editing) return null;
    if (pmEditSnapRef.current?.editing !== editing) {
      pmEditSnapRef.current = { editing, ranked: pmRanking[String(row.month || '').slice(0, 7)]?.[String(row.id)] ?? null };
    }
    return pmEditSnapRef.current.ranked;
  };

  /* ── Handlers ── */

  const handleSort = (column: string) => {
    setSort((prev) => ({
      sortBy: column,
      sortOrder: prev.sortBy === column && prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const resetPage = () => setPagination((prev) => ({ ...prev, page: 1 }));

  // A cell is editable when its column allows it. My Inventory additionally
  // clamps edits to the current calendar month: a row in any other month (past
  // or future, incl. one reached via the month filter) stays read-only — except
  // columns in currentAndFutureMonthEditKeys, which edit on the current month AND
  // any FUTURE month (only PAST months stay read-only) — e.g. the all-PM seniors
  // editing Status/Discount/At-Risk/PM on this month's and all upcoming months' rows.
  const isCellEditable = (row: InventoryRow, col: ColDef): boolean => {
    if (viewOnly) return false;
    // [INV-SPLIT 2026-10-07] A PM part of a split website keeps its PM, website and month: the PM's
    // retention shares hang off them. Undo the split to change those.
    if (Number(row.split_part) > 0 && (col.key === 'pm' || col.key === 'website' || col.key === 'month')) return false;
    // [INV-OVERRIDE 2026-08-04] The calculated columns are a special case and are handled
    // BEFORE the col.editable check, because they are not `editable` in the normal sense —
    // the cell shows a computed figure and what you type goes to a separate override
    // column. Opened only for ADMINS and only on LAST month (July while August is current):
    // the current month still rebuilds itself from payments, and older months are settled.
    // The server enforces both gates again — this only decides whether the cell opens.
    if (col.overrideKey) {
      if (!isAdmin) return false;
      return String(row.month || '').slice(0, 7) === getLastMonth();
    }
    if (!col.editable) return false;
    // [MYINV-ADMIN-ALLMONTHS 2026-07-20] Admins edit ANY month (past included) — the
    // server already allows it (scope.all bypasses the month lock), so exempt admins
    // from the clamp here. Seniors stay current + future (currentAndFutureMonthEditKeys);
    // non-admins stay current-month-only.
    if (currentMonthEditOnly && !isAdmin) {
      const rowMonth = String(row.month || '').slice(0, 7);
      if (currentAndFutureMonthEditKeys?.includes(col.key)) {
        // Current month or any FUTURE month is editable; only PAST months are read-only.
        if (rowMonth < getCurrentMonth()) return false;
      } else if (rowMonth !== getCurrentMonth()) {
        return false;
      }
    }
    return true;
  };

  const startEdit = (row: InventoryRow, col: ColDef) => {
    // [INV-OVERRIDE 2026-08-04] Override columns aren't `editable`; isCellEditable owns them.
    if (!col.editable && !col.overrideKey) return;
    if (!isCellEditable(row, col)) return;
    // Already editing this exact cell — don't re-init (a click inside the open
    // input must not reset what the user has typed).
    if (editing && editing.id === row.id && editing.key === col.key) return;
    // [INV-OVERRIDE 2026-08-04] Seed an override cell with the value it is CURRENTLY
    // SHOWING (sortValue resolves the derived columns exactly the way the cell renders
    // them — computeUnmapped, computePendingPayment, computeDelayedDays), so opening a
    // populated cell shows that number instead of an empty box.
    // Safe against creating an override by accident: saveEdit bails when the committed
    // text still equals editOriginalRef, so opening and closing without typing writes
    // nothing. An override is only ever stored when you actually change the number.
    const val = col.overrideKey ? sortValue(row, col.key) : (row as any)[col.key];
    // Month column: extract YYYY-MM from "2026-03-01T00:00:00.000Z"
    const initial = col.key === 'month' && val ? val.slice(0, 7) : (val != null ? String(val) : '');
    setEditing({ id: row.id, key: col.key });
    setEditValue(initial);
    editOriginalRef.current = initial;
    // [AM-NAMES 2026-09-04] Harmless for every other column; the AM editor needs it seeded
    // so closing without ticking anything commits the value the cell already held (i.e.
    // nothing — saveEdit bails when the committed text equals editOriginalRef).
    amDraftRef.current = initial;
    amCommittedRef.current = false;
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditValue('');
  };

  // `committed` is the final text from <InventoryEditCell> (Enter / blur). The
  // in-progress text now lives in that child's local state, so the parent only
  // sees it here — which is what stops the table re-rendering on every keystroke.
  const saveEdit = async (committed: string) => {
    if (!editing) return;
    const target = editing;
    const raw = committed;

    // Close the editor synchronously, before the network call. This is what
    // makes clicking straight onto another cell work like Google Sheets: the
    // blur commits and closes this cell now, so the click that follows opens
    // the next cell immediately — and this save's async completion can no
    // longer reach back and clobber that newly-opened editor.
    setEditing(null);
    setEditValue('');

    // No actual change → don't write. Just clicking through cells (or pressing
    // Enter without typing) must not fire a save. Compared against the value
    // captured when editing began.
    if (raw === editOriginalRef.current) return;

    const col = cols.find((c) => c.key === target.key);
    let value: any = raw;
    if (col?.key === 'month') {
      value = raw ? `${raw}-01` : null;
    } else if (col?.type === 'number') {
      value = raw === '' ? null : Number(raw);
    }
    const isMonthMove = target.key === 'month';
    // [INV-OVERRIDE 2026-08-04] For a calculated column the value goes to its OVERRIDE
    // column, never to the computed one (writing that would be silently discarded on the
    // next read). Clearing the cell sends null, which drops the override and puts the cell
    // back on the calculated figure.
    const writeKey = col?.overrideKey || target.key;

    // [EDIT-FAST 2026-06-26] Optimistic update — paint the edited value INSTANTLY so the
    // edit feels immediate instead of waiting on the round-trip. A month MOVE relocates/
    // merges the row, so it's skipped here and reloaded after. Snapshot the pre-edit row
    // for rollback on failure, and bump the load id so any reload already in flight can't
    // revert this paint.
    const prevRow = !isMonthMove ? data.find((r) => r.id === target.id) : undefined;
    if (!isMonthMove) {
      protectRow(target.id);
      setData((prev) => prev.map((r) => (r.id === target.id
        ? ({ ...r, [writeKey]: value, ...(col?.overrideKey && value != null ? { [target.key]: value } : {}) } as InventoryRow)
        : r)));
    }
    loadRequestIdRef.current++;

    try {
      const updated = await enqueueSave(() => updateInventoryRow(target.id, { [writeKey]: value } as any, { section: logSection }), col?.label || 'Change');
      loadRequestIdRef.current++;
      // A month change moves the row out of the current view (and may merge into another
      // row) — reload SILENTLY so the table and totals reflect the move without a skeleton.
      // Otherwise replace with the server row so derived columns (unmapped, totals, tint)
      // reflect the change.
      if (isMonthMove) {
        load(true);
      } else {
        setData((prev) => prev.map((r) => (r.id === target.id ? updated : r)));
        flashRow(target.id); // [MOTION-B 2026-08-20] point the eye at the row that just changed
      }
      toastSuccess(`${col?.label || 'Field'} updated`);
    } catch (err: any) {
      console.error('Failed to save:', err);
      // Roll back the optimistic paint so the cell shows its real (pre-edit) value.
      if (!isMonthMove && prevRow) {
        setData((prev) => prev.map((r) => (r.id === target.id ? prevRow : r)));
      }
      alertDialog({ title: 'Save failed', message: err?.response?.data?.error || 'Failed to save change', destructive: true });
    } finally {
      // Keep the row protected for a short grace window after the write settles (see
      // rowProtectRef) so a reload already in flight can't revert it, then let reloads through.
      if (!isMonthMove) releaseRow(target.id);
    }
  };

  const handleAdd = () => {
    // Default the new row's month to the latest selected month, or the landing
    // month (next month) when nothing is filtered.
    const defaultAddMonth = months.length ? [...months].sort().slice(-1)[0] : initialMonth;
    setAddForm({ ...emptyAddForm, month: defaultAddMonth });
    setShowAddModal(true);
  };

  const updateAddForm = (key: string, value: string) =>
    setAddForm((prev) => ({ ...prev, [key]: value }));

  const submitAdd = async () => {
    if (!addForm.website.trim()) { alertDialog({ title: 'Missing field', message: 'Website is required.' }); return; }
    if (!addForm.month) { alertDialog({ title: 'Missing field', message: 'Month is required.' }); return; }
    setAddSaving(true);
    try {
      await createInventoryRow({
        website: addForm.website.trim(),
        email: addForm.email.trim() || null,
        num_campaigns: addForm.num_campaigns ? Number(addForm.num_campaigns) : null,
        type: addForm.type || null,
        pm: addForm.pm.trim() || null,
        // [AM-NAMES 2026-09-04, user] Normalised before it leaves, so a row created here is
        // byte-identical to the same assignment made by editing the cell afterwards.
        am_names: normalizeAmNames(addForm.am_names) || null,
        brand: addForm.brand.trim() || null,
        amount: addForm.amount ? Number(addForm.amount) : 0,
        payment_type: addForm.payment_type || null,
        billing_date: addForm.billing_date ? Number(addForm.billing_date) : null,
        month: addForm.month,
        status: addForm.status || null,
      } as any);
      setShowAddModal(false);
      load();
      toastSuccess('Inventory row added');
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Failed to create row';
      alertDialog({ title: 'Could not create row', message: msg, destructive: true });
    } finally {
      setAddSaving(false);
    }
  };

  const handleDelete = (id: number, website: string) => {
    setDeleteTarget({ id, website });
  };

  /* [INV-SPLIT 2026-10-07, user] Split a website between PMs, or undo it: admins, current month,
     Inventory tab only (My Inventory has no row actions). */
  const [splitTarget, setSplitTarget] = useState<InventoryRow | null>(null);
  const splitPres = usePresence(splitTarget, fluid.dialogOutMs); // [FLUID-3 2026-10-12] held for its close
  const [unsplitTarget, setUnsplitTarget] = useState<InventoryRow | null>(null);
  const [unsplitBusy, setUnsplitBusy] = useState(false);
  const isCurrentMonthRow = (row: InventoryRow) => String(row.month || '').slice(0, 7) === getCurrentMonth();
  const canSplitRow = (row: InventoryRow) =>
    isAdmin && showActions && !viewOnly && isCurrentMonthRow(row) && !(Number(row.split_part) > 0) && Number(row.amount) > 0;
  const canUnsplitRow = (row: InventoryRow) =>
    isAdmin && showActions && !viewOnly && isCurrentMonthRow(row) && Number(row.split_part) > 0;
  const openSplit = (row: InventoryRow) => setSplitTarget(row);
  const openUnsplit = (row: InventoryRow) => setUnsplitTarget(row);
  const onSplitDone = (result: { parts: InventoryRow[]; paymentsUnmapped: number }) => {
    const who = result.parts.map((p) => p.pm).filter(Boolean).join(' and ');
    const unmapped = result.paymentsUnmapped
      ? ` · ${result.paymentsUnmapped} payment${result.paymentsUnmapped === 1 ? '' : 's'} unmapped, map them again by PM`
      : '';
    toastSuccess(`${splitTarget?.website || 'Website'} split between ${who}${unmapped}`);
    setSplitTarget(null);
    load(true);
  };
  const confirmUnsplit = async () => {
    if (!unsplitTarget) return;
    setUnsplitBusy(true);
    try {
      const result = await unsplitInventoryRow(unsplitTarget.id);
      toastSuccess(`${unsplitTarget.website} is one row again (${result.row?.pm || 'first PM'})`);
      setUnsplitTarget(null);
      load(true);
    } catch (err: any) {
      toastError(err?.response?.data?.error || 'Could not undo the split');
    } finally {
      setUnsplitBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await deleteInventoryRow(deleteTarget.id);
      setData((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      setDeleteTarget(null);
      toastSuccess('Inventory row deleted');
    } catch (err) {
      console.error('Failed to delete:', err);
    } finally {
      setDeleteBusy(false);
    }
  };

  /* ── Renewal modal ── */
  const openRenewalModal = (row: InventoryRow) => {
    setRenewalRow(row);
    setRenewalStatus(row.renewal_status || '');
    setRenewalMonth(row.renewal_date ? row.renewal_date.slice(0, 7) : '');
    setRenewalAmount(row.renewal_amount != null ? String(row.renewal_amount) : '');
  };

  const closeRenewalModal = () => {
    setRenewalRow(null);
    setRenewalStatus('');
    setRenewalMonth('');
    setRenewalAmount('');
  };

  const saveRenewal = async () => {
    if (!renewalRow) return;
    const renewalId = renewalRow.id;
    setRenewalSaving(true);
    protectRow(renewalId);
    try {
      const fields: any = {
        renewal_status: renewalStatus || null,
      };
      if (renewalStatus === 'Future Renewal') {
        fields.renewal_date = renewalMonth ? `${renewalMonth}-01` : null;
        fields.renewal_amount = renewalAmount ? Number(renewalAmount) : null;
      } else {
        // Clear date/amount if not Future Renewal
        fields.renewal_date = null;
        fields.renewal_amount = null;
      }
      // [SAVE-QUEUE 2026-09-09] Queued like every other write on the page (was a direct call).
      const updated = await enqueueSave(() => updateInventoryRow(renewalId, fields, { section: logSection }), 'Renewal');
      loadRequestIdRef.current++;
      setData((prev) => prev.map((r) => (r.id === renewalId ? updated : r)));
      closeRenewalModal();
      toastSuccess('Renewal updated');
    } catch (err) {
      console.error('Failed to save renewal:', err);
    } finally {
      releaseRow(renewalId);
      setRenewalSaving(false);
    }
  };

  /* ── Sort icon ── */
  const getSortIcon = (key: string) => {
    if (sort.sortBy !== key) return <i className="fas fa-sort sort-icon"></i>;
    return sort.sortOrder === 'asc'
      ? <i className="fas fa-sort-up sort-icon active"></i>
      : <i className="fas fa-sort-down sort-icon active"></i>;
  };

  /* ── Render cell ── */
  const renderCell = (row: InventoryRow, col: ColDef) => {
    const isEditing = editing?.id === row.id && editing?.key === col.key;
    const val = (row as any)[col.key];

    // [STATUS-TEXT 2026-06-25] Status shows its value as plain text coloured by status
    // (both tabs); only while it's being edited does the select replace it.
    if (col.key === 'status' && !isEditing) {
      return val ? <StatusText $status={val}>{val}</StatusText> : '—';
    }

    // [CAMPAIGN-STATUS 2026-07-06] Read-only pill (both tabs) — the server computes the
    // 'New Campaign' / 'Existing Campaign' value; here we just style it.
    if (col.key === 'campaign_status') {
      return val ? <CampaignTag $isNew={val === 'New Campaign'}>{val}</CampaignTag> : '—';
    }

    // [AM-NAMES 2026-09-04, user] The AM list, shown identically on both tabs and whether or
    // not this viewer may edit it (hence: above the read-only split below, like Status).
    // Rendered through parseAmNames so the display is canonical even if the stored string
    // has odd spacing, and a name that has since left the roster simply stops showing.
    if (col.key === 'am_names' && !isEditing) {
      const names = parseAmNames(val);
      if (!names.length) return '—';
      const text = names.join(', ');
      return <AmCell title={text}>{text}</AmCell>;
    }

    // [LINK-BTN 2026-07-01] Website & Email render the value + hover copy/open buttons in
    // BOTH tabs (editable Inventory + read-only My Inventory), so it's handled before the
    // editable/read-only split. While the cell is being edited the input below takes over.
    if ((col.key === 'website' || col.key === 'email') && !isEditing) {
      const cell = <LinkCell kind={col.key} value={val} openHistory={websiteOpensHistory} historyButton={historyButton} />;
      // [INV-SPLIT 2026-10-07, user] Mark each PM part of a split website.
      if (col.key === 'website' && Number(row.split_part) > 0) {
        return (
          <>
            {cell}
            <SplitTag title="This website is split between PMs this month">Split {row.split_part}/{row.split_count || '?'}</SplitTag>
          </>
        );
      }
      return cell;
    }

    // Read-only column
    // [INV-OVERRIDE 2026-08-04] An override column is NOT `editable` (its cell shows a
    // computed figure), so without this it would always take the static branch below and
    // the editor could never open — the cell showed the pointer cursor and did nothing.
    // Only step aside while this cell is ACTIVELY being edited, so every column keeps its
    // special display formatting (computeUnmapped, computeSalesPending, …) the rest of the time.
    if (!col.editable && !(col.overrideKey && isEditing)) {
      // Columns that carry special formatting whether or not they're editable
      // (a parametrized view like My Inventory shows these read-only).
      if (col.key === 'amount') return formatMoney(val);
      if (col.key === 'month') return val ? formatMonthLabel(val.slice(0, 7)) : '—';
      if (col.key === 'discount') return formatMoney(Number(val) || 0);
      // Computed display columns (values shared with the sort comparator).
      if (col.key === 'sales_pending_payment') return formatMoney(computeSalesPending(row));
      if (col.key === 'at_risk_expected_payment') return formatMoney(computeAtRiskExpected(row));
      if (col.key === 'delayed_days') { const d = computeDelayedDays(row); return d > 0 ? d : '—'; }
      if (col.key === 'unmapped_payments') return formatMoney(computeUnmapped(row));
      if (col.key === 'pending_good') return formatMoney(computePendingPayment(row));
      // Recovery & PIA Amount default to $0.00 (never a dash) when no matching
      // payments exist; PIA Month falls through below and shows "—" when unset.
      if (col.key === 'recovery' || col.key === 'pia_amount') return formatMoney(num(val));
      if (MONEY_DISPLAY_COLS.has(col.key)) return formatMoney(val);
      return val ?? '—';
    }

    // Currently editing this cell
    if (isEditing) {
      // Month column: use month picker
      if (col.key === 'month') {
        return (
          <InventoryEditCell
            initialValue={editOriginalRef.current}
            inputType="month"
            onCommit={saveEdit}
            onCancel={cancelEdit}
          />
        );
      }
      // [AM-NAMES 2026-09-04, user] The multi-select cell — checkboxes, one click toggles,
      // the popover STAYS OPEN so several AMs can be ticked in one go. This is the only
      // place in the app where multi-select is offered (the toolbar filter and the bulk
      // modal are both single-name, by the user's ruling).
      //
      // COMMIT ON CLOSE, not on each tick: ticking three names is ONE write and ONE audit
      // entry instead of three, and the intermediate states ("A", then "A, B") never reach
      // the database. saveEdit already no-ops when the committed value matches the value the
      // cell opened with, so opening and closing the dropdown writes nothing.
      //
      // Escape ABANDONS: GlassSelect tells us why it closed, so backing out with Escape
      // leaves the row exactly as it was. Every other close (clicking away, clicking another
      // cell) commits.
      if (col.key === 'am_names') {
        return (
          <CellSelect liquidList
            defaultOpen
            multiple
            multiCheckbox
            multiUnitLabel="AMs"
            values={parseAmNames(editValue)}
            onChangeValues={(next) => {
              // Canonical (roster order, ', '-joined) on every tick, so the value compared
              // against editOriginalRef on commit is byte-comparable — re-ticking the same
              // people in a different order is correctly seen as no change at all.
              const joined = joinAmNames(next);
              amDraftRef.current = joined;
              setEditValue(joined);
            }}
            onClose={(reason) => {
              if (reason === 'escape') { cancelEdit(); return; }
              if (amCommittedRef.current) return; // StrictMode replay — see amCommittedRef
              amCommittedRef.current = true;
              saveEdit(amDraftRef.current);
            }}
          >
            {/* The empty sentinel is REQUIRED here, not decoration: GlassSelect reads it as
                the clear-all row, and omitting it turns on the require-one rule that refuses
                to untick the last remaining name. An AM assignment has to be removable, so
                this row both clears the cell and gives the trigger its "no AM" label. */}
            <option value="">— None —</option>
            {amOptions.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </CellSelect>
        );
      }
      if (col.type === 'select') {
        const opts = col.options || getFilterOptions(col.key);
        // [INV-PM-RANK 2026-09-21, user] PM Name lists THIS row's PM Recommendations ranking first
        // ("1. Name · 20 campaigns · $15,230 (Sep)"), then every other PM of the usual roster as a
        // plain name — "ranked first, rest below", so nobody pickable before disappears (on a ROW
        // row, everyone but the ROW PM; on a normal row the server lists the ROW PM as the last
        // number, with her count and $). Picking still saves exactly as before: the option VALUE is
        // the bare name. No ranking yet → the plain list.
        const ranked = col.key === 'pm' ? pmRankingForEdit(row) : null;
        const rankedNames = new Set(ranked ? ranked.candidates.map((c) => c.pm) : []);
        const rowMonth = String(row.month || '').slice(0, 7);
        // [MYINV-PM-NO-BLANK 2026-09-28, user] A row that already has a PM loses the blank "—"
        // option (My Inventory only). GlassSelect shows the FIRST option when the value matches
        // none, so a PM missing from today's lists (e.g. no longer on the roster) is listed first
        // as itself — otherwise the cell would read as if it belonged to the top-ranked PM.
        const currentPm = String(row.pm ?? '').trim();
        const lockPmBlank = col.key === 'pm' && pmNoBlankOnceSet && currentPm !== '';
        const keepCurrentPm = lockPmBlank && !rankedNames.has(currentPm) && !opts.includes(currentPm);
        return (
          <CellSelect liquidList
            defaultOpen
            value={editValue}
            onChange={(e) => {
              const newVal = e.target.value;
              if (lockPmBlank && newVal === '') { cancelEdit(); return; } // never blank a set PM
              const value = newVal === '' ? null : newVal;
              const editId = editing.id;
              const editKey = editing.key;
              // [EDIT-FAST 2026-06-26] Optimistic, like saveEdit: paint the new value AND close
              // the editor INSTANTLY so a dropdown switch (e.g. status Expected → Retained) feels
              // immediate instead of waiting on the round-trip. Snapshot the pre-edit row for
              // rollback, bump the load id so an in-flight reload can't revert the paint, then
              // reconcile with the server's recomputed row in the background.
              const prevRow = data.find((r) => r.id === editId);
              protectRow(editId);
              setData((prev) => prev.map((r) => (r.id === editId ? ({ ...r, [editKey]: value } as InventoryRow) : r)));
              cancelEdit();
              loadRequestIdRef.current++;
              // [SAVE-QUEUE 2026-09-09] Through the same queue as the cell editor. This path used
              // to fire directly, so a dropdown change made while a cell save was in flight raced
              // it for the payments trigger — the exact deadlock the queue exists to prevent.
              enqueueSave(() => updateInventoryRow(editId, { [editKey]: value } as any, { section: logSection }), col.label || 'Change')
                .then((updated) => {
                  loadRequestIdRef.current++;
                  setData((prev) => prev.map((r) => (r.id === editId ? updated : r)));
                  toastSuccess(`${col.label || 'Field'} updated`);
                })
                .catch((err: any) => {
                  console.error('Failed to save:', err);
                  // Roll back the optimistic paint to the row's real pre-edit value.
                  if (prevRow) setData((prev) => prev.map((r) => (r.id === editId ? prevRow : r)));
                  alertDialog({ title: 'Save failed', message: err?.response?.data?.error || 'Failed to save change', destructive: true });
                })
                .finally(() => releaseRow(editId));
            }}
            onClose={() => cancelEdit()}
          >
            {!lockPmBlank && <option value="">—</option>}
            {keepCurrentPm && <option value={currentPm}>{currentPm}</option>}
            {ranked && ranked.candidates.map((c) => (
              <option key={c.pm} value={c.pm}>{pmRankLabel(c, ranked.rule, rowMonth)}</option>
            ))}
            {opts.filter((o) => !rankedNames.has(o)).map((o) => (
              <option key={o} value={o}>
                {col.key === 'status' ? <StatusText $status={o}>{o}</StatusText> : o}
              </option>
            ))}
          </CellSelect>
        );
      }
      return (
        <InventoryEditCell
          initialValue={editOriginalRef.current}
          inputType={col.type === 'number' ? 'number' : 'text'}
          onCommit={saveEdit}
          onCancel={cancelEdit}
        />
      );
    }

    // Display mode — clickable for current month only
    if (col.key === 'month' && val) return formatMonthLabel(val.slice(0, 7));
    if (col.key === 'amount') return formatMoney(val);
    // Discount defaults to $0.00 when unset (not blank).
    if (col.key === 'discount') return formatMoney(Number(val) || 0);
    // At-Risk Expected Payment shows the override-or-auto figure, not the raw
    // (possibly null) stored override.
    if (col.key === 'at_risk_expected_payment') return formatMoney(computeAtRiskExpected(row));
    return val ?? '—';
  };

  // Every row renders the same in every month — full opacity and a delete button.
  const isRowEditable = (_row: InventoryRow): boolean => true;

  // [AM-NAMES 2026-09-04, user] Hardcoded roster — no month-pinning needed (unlike the
  // toolbar lists further down, it never varies with the month, so a selected value can
  // never fall out of it). Served by the server so there is ONE source; the local copy in
  // utils/amRoster is the fallback when the client is newer than the server it talks to.
  // ⚠ MUST stay above the row-render memo below: that memo calls renderCell, which calls
  // getFilterOptions, which reads this — declared any later and the first render throws.
  const amOptions = filters.ams && filters.ams.length ? filters.ams : [...AM_NAMES];

  const getFilterOptions = (key: string): string[] => {
    if (key === 'pm') return filters.pms;
    // [AM-NAMES 2026-09-04, user] The roster, NOT a DISTINCT over the data: the eight names
    // must be pickable on a month where nobody has been assigned yet. Served by the server so
    // there is one source, with the local copy as the fallback when talking to an older build.
    if (key === 'am_names') return amOptions;
    // Cell editor uses the full brand list (not the month-scoped toolbar list) so
    // a row's brand can be set to any brand, not only ones used in the month in view.
    if (key === 'brand') return allBrands;
    if (key === 'status') return filters.statuses;
    return [];
  };

  /* ── Render ── */

  // [EDIT-INSTANT 2026-07-01] Stable key for the (possibly new-each-render) excludeTypes prop
  // so the memo below only recomputes on a REAL change, not on every parent render.
  const excludeKey = (excludeTypes ?? []).map((t) => t.trim().toLowerCase()).filter(Boolean).join('|');

  // [EDIT-INSTANT 2026-07-01] Client-side mirror of the SAME filters the server applies:
  // hidden types (excludeTypes — My Inventory's One-Time + PIA) PLUS the active Month / PM / Type / Brand /
  // Status toolbar selections. On a fresh load this changes nothing — the server already
  // returned exactly this set. But after an OPTIMISTIC inline edit it re-runs synchronously,
  // so a row whose edited value no longer matches the active filter (e.g. Status Retained →
  // Cancelled while filtering "Retained", or a PM reassigned out of the selected PM) drops out
  // of the table INSTANTLY — no waiting on the write round-trip + reload. Comparisons mirror
  // the server: exact match for Type/Brand/PM, YYYY-MM for Month, space/case-insensitive for
  // Status (statusEq). All toolbar filters are cleared while searching, so this is a no-op in
  // search mode. The whole set is fetched (pageSize: 0), so the count + Placed-Inventory
  // fallback total are derived from this filtered set to stay consistent with the table.
  const visibleData = useMemo(() => {
    const hidden = excludeKey ? new Set(excludeKey.split('|')) : null;
    let rows = hidden
      ? data.filter((r) => !hidden.has(String((r as any).type ?? '').trim().toLowerCase()))
      : data;
    if (months.length) rows = rows.filter((r) => months.includes(String((r as any).month ?? '').slice(0, 7)));
    if (typeFilters.length) rows = rows.filter((r) => typeFilters.includes(String((r as any).type ?? '')));
    if (pmFilters.length) rows = rows.filter((r) => pmFilters.includes(String((r as any).pm ?? '')));
    if (brandFilters.length) rows = rows.filter((r) => brandFilters.includes(String((r as any).brand ?? '')));
    if (statusFilters.length) rows = rows.filter((r) => statusFilters.some((s) => statusEq(s, (r as any).status)));
    // [AM-NAMES 2026-09-04, user] Mirrors the server's `am` clause: a CONTAINS test over the
    // row's parsed list, NOT an equality test, because a row can carry several AMs. Going
    // through parseAmNames is what keeps "Gaurav Kumar" from matching "Gaurav Kumar Gaur" —
    // a substring test on the raw string would return both people's rows.
    if (amFilter) rows = rows.filter((r) => parseAmNames((r as any).am_names).includes(amFilter));
    return rows;
  }, [data, excludeKey, months, typeFilters, pmFilters, brandFilters, statusFilters, amFilter]);

  // Count + Placed-fallback total reflect what's actually on screen (the client-filtered set),
  // so both stay correct the instant an edit drops a row — never one reload behind.
  const shownCount = visibleData.length;
  const shownTotal = visibleData.reduce((sum, r) => sum + (Number((r as any).amount) || 0), 0);
  // [PLACED-MONTH-PM 2026-07-01] "Placed Inventory" shows the month+PM total when it
  // applies (single month, not searching), so it never changes with the status filter;
  // otherwise (cross-month search / all-months) it falls back to the visible set's total.
  const placedDisplay = placedTotal != null ? placedTotal : shownTotal;

  // [AM-REMINDER 2026-09-24, user] Progress for the "add the AM" banner (My Inventory only).
  // Counts the rows ON SCREEN whose AM cell this user can actually edit — the same gate the
  // cell uses (isCellEditable), plus never a past month, even for admins — and how many of
  // them already carry an AM. Derived from visibleData, so it moves the moment an AM is saved
  // (optimistic edit) and costs no extra request. isCellEditable is recreated every render
  // but reads only props + user, so the deps list those values instead (see isRowSelectable).
  const amCol = amReminder ? cols.find((c) => c.key === 'am_names') : undefined;
  const amProgress = useMemo(() => {
    if (!amCol) return null;
    const cur = getCurrentMonth();
    let total = 0;
    let done = 0;
    for (const r of visibleData) {
      if (String((r as any).month ?? '').slice(0, 7) < cur) continue;
      if (!isCellEditable(r, amCol)) continue;
      total += 1;
      if (parseAmNames((r as any).am_names).length) done += 1;
    }
    return { total, done };
  }, [amCol, visibleData, viewOnly, isAdmin, currentMonthEditOnly, currentAndFutureMonthEditKeys]);
  // Shown only while at least one editable row in view still has no AM; it disappears by
  // itself once they are all filled (and on a past month, where nothing is editable).
  const showAmBanner = !!amProgress && amProgress.total > 0 && amProgress.done < amProgress.total;

  // [AM-REMINDER-SKELETON 2026-09-24, user] While this view's rows are loading, the banner's place
  // is part of the skeleton (AmBannerSkeleton) instead of the banner. "Settled" = not loading AND
  // the rows in `data` were loaded for THIS view: in the one render between a filter change and
  // load() switching the skeleton on, `data` still holds the previous view's rows, and saving
  // their answer then would file it under the new view.
  const amViewKey = amReminder ? amViewKeyOf(months, typeFilters, pmFilters, brandFilters, statusFilters, amFilter, search) : '';
  // This view's rows are really on screen: the only state an answer may be saved from.
  const amRowsLoaded = amReminder && !loading && amLoadedKeyRef.current === amViewKey;
  // The skeleton ends when the rows land OR the load failed (the table has finished either way).
  const amSettled = amRowsLoaded || (amReminder && !loading && amFailedKeyRef.current === amViewKey);
  const amUserKey = user?.id != null ? String(user.id) : '';
  // Save the answer each time the view's rows land, and again whenever it changes (e.g. the last
  // AM is filled in). Never after a failed load. Search views are not saved: every typed search
  // would be its own view and push out real ones.
  useEffect(() => {
    if (amRowsLoaded && amUserKey && !search) rememberAmBanner(amUserKey, amViewKey, showAmBanner);
  }, [amRowsLoaded, amUserKey, amViewKey, search, showAmBanner]);
  const amBannerExpected = amReminder && !amSettled && (
    (amUserKey && !search ? recallAmBanner(amUserKey, amViewKey) : undefined)
    // Never seen here: a current or future month in view (or no month filter) can have the banner,
    // a past month cannot (nothing there is editable).
    ?? (months.length === 0 || months.some((m) => m >= getCurrentMonth()))
  );

  // Sorted rows for the table body. Sorts a COPY of the already client-filtered visibleData,
  // so it only recomputes when that set, the sort, or the search changes — never on a
  // keystroke. Output order is byte-for-byte the same as before for a given input set.
  const sortedData = useMemo(() => {
    const rows = visibleData.slice();
    rows.sort((a, b) => {
      // Rows with a blank PM or a missing Company Record ID are flagged (red) and
      // pinned to the top — except while searching, where we sort purely by the
      // active column so matches read top-down across months.
      const aFlagged = !(a.pm && String(a.pm).trim()) || !(a.company_record_id && String(a.company_record_id).trim());
      const bFlagged = !(b.pm && String(b.pm).trim()) || !(b.company_record_id && String(b.company_record_id).trim());
      if (!search && aFlagged !== bFlagged) return aFlagged ? -1 : 1;
      const col = cols.find((c) => c.key === sort.sortBy);
      const av = sortValue(a, sort.sortBy);
      const bv = sortValue(b, sort.sortBy);
      const aEmpty = av == null || av === '';
      const bEmpty = bv == null || bv === '';
      if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : aEmpty ? 1 : -1;
      const numeric = col?.type === 'number' || (typeof av === 'number' && typeof bv === 'number');
      const cmp = numeric
        ? Number(av) - Number(bv)
        : String(av).toLowerCase().localeCompare(String(bv).toLowerCase());
      return sort.sortOrder === 'desc' ? -cmp : cmp;
    });
    return rows;
  }, [visibleData, search, sort.sortBy, sort.sortOrder, cols]);

  // [COL-RESIZE-ALL 2026-09-29, user] Excel-style drag-to-resize, shared with every table
  // (components/ColumnResize). Off on phones. This table's Th pads 10px a side (headerPadX 20) and
  // its Td 6px a side (INV_CELL_PAD_X). The checkbox and Delete columns are not resizable.
  const colResize = useColumnResize({
    storageKey: `dt-${columnResizeKey}-colw:${user?.id ?? 'anon'}`,
    enabled: !isMobile,
    columnKeys: cols.map((c) => c.key),
    headerPadX: 20,
    refresh: sortedData,
  });
  // [SEARCH-HIGHLIGHT 2026-09-29, user] Amber highlight of the searched text in the rows.
  useSearchHighlight(colResize.tableElRef, search, loading ? null : sortedData);

  /**
   * [PERF 2026-08-14] ⚠ DO NOT REINTRODUCE PROGRESSIVE / CHUNKED ROW RENDERING HERE.
   *
   * A previous revision (b998f88, reverted the same day) painted the first 60 rows and appended
   * the remaining ~250 on the next frame, to shorten time-to-first-paint. It worked, and it was
   * still WRONG for this table: `Table` is `table-layout: auto` (see the styled component near
   * the top of this file), so the browser derives every column width from the content of ALL
   * rows currently in the DOM. Painting 60 rows and then 306 makes it measure twice, and every
   * column visibly jumps to a new width as the rest arrive. The user's words: it "shows that the
   * application is breaking".
   *
   * Chunking here is only safe if column widths stop depending on row content first - i.e.
   * `table-layout: fixed` with an explicit <colgroup>, which is a deliberate visual change to
   * the whole table, not a perf tweak to smuggle in alongside one.
   *
   * It is also no longer needed. The mount cost it was masking was mostly formatMoney rebuilding
   * an Intl.NumberFormat 3,366 times per table build (~84 ms); with MONEY_FMT hoisted that is
   * ~1.9 ms, so the full 306 rows now render in one pass without the jump.
   */

  // [FLIP-REORDER 2026-06-26] Glide a row into place when it re-sorts after an edit (e.g. a
  // flagged-red row that drops to its position once a PM is assigned) instead of jumping.
  const flipBodyRef = useFlipReorder<HTMLTableSectionElement>([sortedData]);

  // [FLUID-6 2026-10-12, user] The row wave: each new `landing` (see where it is declared) makes every row on screen
  // rise in, top to bottom, before the frame is painted. landRef sits on TableWrapper, which exists only while real
  // rows show (never the skeleton). After the FLIP effect above, so a reorder's measuring happens first.
  // The toolbar filters are part of the key: a filter change first re-renders the rows already on screen, narrowed in
  // place (visibleData), and load() (an effect) swaps in the cached copy or the skeleton a moment later. Starting the
  // wave on that first render keeps those rows from being painted at rest and then snapped to the wave's start; the
  // landing that follows restarts it over the new rows before any row has begun to rise (rows wait 40 ms). The search
  // text is left out: the server applies it, so it never narrows the rows in place.
  const landRef = useRef<HTMLDivElement>(null);
  const landViewKey = amViewKeyOf(months, typeFilters, pmFilters, brandFilters, statusFilters, amFilter);
  useRowsLand(landRef, landing > 0 ? `${landing}|${landViewKey}` : null);

  /* ── [BULK-EDIT 2026-08-03] Multi-row edit ── */

  // Columns this VIEW may bulk-edit: bulk-safe, editable here, and — when the view pins a
  // list via `bulkEditKeys` — one of those keys. My Inventory pins ['pm'], so its picker
  // offers PM Name and nothing else. A read-only view (non-admin on the Inventory tab) or a
  // user with none of the pinned columns editable gets an empty list and no selection UI.
  const bulkKeyFilter = bulkEditKeys ? bulkEditKeys.join('|') : '';
  const bulkCols = useMemo(() => {
    const pinned = bulkKeyFilter ? new Set(bulkKeyFilter.split('|')) : null;
    return cols.filter((c) => c.editable && BULK_SAFE_KEYS.has(c.key) && (!pinned || pinned.has(c.key)));
  }, [cols, bulkKeyFilter]);
  const bulkEnabled = !viewOnly && bulkCols.length > 0;

  // A row can be selected when at least one bulk-safe column is actually editable on it —
  // i.e. it passes the same month/role gate an inline edit would. This keeps out-of-scope
  // and past-month rows out of the selection instead of letting the server reject them later.
  // NB: isCellEditable is recreated every render but reads only props + `user`, which are
  // fixed for a given page state — so it is deliberately not a dep. The values that can
  // actually change the answer are listed below. (No eslint-disable comment here: this
  // project's ESLint config does not register react-hooks/exhaustive-deps, and referencing
  // an unknown rule FAILS the CRA build.)
  const isRowSelectable = useCallback(
    (row: InventoryRow) => bulkCols.some((c) => isCellEditable(row, c)),
    [bulkCols, viewOnly, isAdmin, currentMonthEditOnly, currentAndFutureMonthEditKeys],
  );

  const toggleRow = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // The selectable rows currently on screen, and how many of them are ticked.
  const selectableVisible = useMemo(
    () => (bulkEnabled ? sortedData.filter(isRowSelectable) : []),
    [bulkEnabled, sortedData, isRowSelectable],
  );
  const selectedVisibleIds = useMemo(
    () => selectableVisible.filter((r) => selectedIds.has(r.id)).map((r) => r.id),
    [selectableVisible, selectedIds],
  );
  const allVisibleSelected = selectableVisible.length > 0 && selectedVisibleIds.length === selectableVisible.length;
  const someVisibleSelected = selectedVisibleIds.length > 0 && !allVisibleSelected;

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const visible = selectableVisible.map((r) => r.id);
      const everySelected = visible.length > 0 && visible.every((id) => prev.has(id));
      // Untick this page's rows, keep any selection outside it (there isn't one today —
      // the page fetches a single filtered set — but this stays correct if that changes).
      if (everySelected) {
        const next = new Set(prev);
        visible.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      visible.forEach((id) => next.add(id));
      return next;
    });
  }, [selectableVisible]);

  // A filter/search change swaps the visible set out from under the selection, so drop it
  // (and the undo handle — the bar belongs to the view the edit was made in). Without this
  // a user could switch month and act on rows they can no longer see.
  useEffect(() => {
    setSelectedIds((prev) => (prev.size ? new Set() : prev));
    setUndoBatch(null);
  }, [months, typeFilters, pmFilters, brandFilters, statusFilters, amFilter, search]);

  const bulkCol = bulkCols.find((c) => c.key === bulkField);

  const openBulkModal = () => {
    const first = bulkCols[0];
    setBulkField(first ? first.key : '');
    setBulkValue('');
    setShowBulkModal(true);
  };

  const applyBulk = async () => {
    const col = bulkCols.find((c) => c.key === bulkField);
    if (!col) return;
    // Only ever send rows that are BOTH selected and currently visible+selectable.
    const ids = selectedVisibleIds;
    if (!ids.length) return;

    const raw = bulkValue.trim();
    // [MYINV-PM-NO-BLANK 2026-09-28, user] Bulk can only ASSIGN a PM here, never clear one.
    if (pmNoBlankOnceSet && col.key === 'pm' && raw === '') {
      alertDialog({ title: 'Pick a PM', message: 'Choose a PM name to assign. PM Name cannot be made blank here.' });
      return;
    }
    const value = raw === '' ? null : (col.type === 'number' ? Number(raw) : raw);
    if (col.type === 'number' && value !== null && !Number.isFinite(value as number)) {
      alertDialog({ title: 'Invalid value', message: `${col.label} must be a number.` });
      return;
    }

    setBulkSaving(true);
    try {
      // [SAVE-QUEUE 2026-09-09] One request, but it must not interleave with a cell save that is
      // still in flight — same queue, same ordering guarantee.
      const result = await enqueueSave(() => bulkUpdateInventory(ids, col.key, value, { section: logSection }), 'Bulk edit');

      // No optimistic paint here (unlike the single-cell edit): a bulk write can legitimately
      // apply to only SOME of the selection, so painting first would show values that never
      // landed. One silent reload afterwards is both simpler and always accurate.
      setShowBulkModal(false);
      clearSelection();
      setUndoBatch(result.batchId && result.updated > 0
        ? { batchId: result.batchId, label: col.label, count: result.updated }
        : null);
      loadRequestIdRef.current++;
      await load(true);

      const bits: string[] = [`${result.updated} record${result.updated === 1 ? '' : 's'} updated`];
      if (result.unchanged) bits.push(`${result.unchanged} already set`);
      if (result.skipped.length) bits.push(`${result.skipped.length} skipped`);
      const msg = bits.join(' · ');
      if (result.skipped.length) {
        // Name the reason rather than silently dropping rows.
        const reasons = Array.from(new Set(result.skipped.map((s) => s.reason))).join('; ');
        toastError(`${msg} — ${reasons}`);
      } else if (result.updated === 0) {
        toastSuccess(msg);
      } else {
        toastSuccess(msg);
      }
    } catch (err: any) {
      // A batched write can fail PART-WAY, so never claim "nothing changed" here. If the
      // server hands back a batch id, keep the Undo affordance alive so whatever did land
      // can still be reverted, and reload so the table shows the real state either way.
      const data = err?.response?.data;
      if (data?.batchId) {
        setShowBulkModal(false);
        clearSelection();
        setUndoBatch({ batchId: data.batchId, label: col.label, count: ids.length });
      }
      loadRequestIdRef.current++;
      load(true);
      alertDialog({
        title: 'Bulk update failed',
        message: data?.error || 'Failed to update the selected records.',
        destructive: true,
      });
    } finally {
      setBulkSaving(false);
    }
  };

  const undoBulk = async () => {
    if (!undoBatch) return;
    setUndoBusy(true);
    try {
      const result = await undoBulkInventoryUpdate(undoBatch.batchId, { section: logSection });
      setUndoBatch(null);
      loadRequestIdRef.current++;
      await load(true);
      toastSuccess(
        result.skipped
          ? `${result.restored} reverted · ${result.skipped} left alone (changed since)`
          : `${result.restored} record${result.restored === 1 ? '' : 's'} reverted`
      );
    } catch (err: any) {
      alertDialog({
        title: 'Undo failed',
        message: err?.response?.data?.error || 'Could not revert that change.',
        destructive: true,
      });
    } finally {
      setUndoBusy(false);
    }
  };

  // [PERF 2026-07-03] Route the row-render helpers through a ref so the memoised
  // <tbody> below can call the LATEST versions without listing them as deps —
  // they're recreated every render, so as deps they'd defeat the memo. Reassigned
  // on every render (before the memo reads it), so there are never stale closures.
  const rowHelpersRef = useRef<any>(null);
  rowHelpersRef.current = { renderCell, isCellEditable, isRowEditable, startEdit, openRenewalModal, handleDelete,
    canSplitRow, canUnsplitRow, openSplit, openUnsplit, // [INV-SPLIT 2026-10-07]
    isEditingAny: () => editingRef.current }; // [CELL-PRESS 2026-10-13] is any cell's editor open right now?

  // [PERF 2026-07-03] Memoise the entire table body. It rebuilds ONLY when the
  // visible content genuinely changes — the rows, columns, edit state, or the
  // in-cell editor's option lists — NOT when an unrelated page state toggles
  // (opening the Download / Add modal, a delete dialog, a renewal modal, etc.).
  // Those toggles used to re-render every one of the (hundreds of) rows before the
  // modal could paint, which is why opening a modal / hitting Cancel felt slow.
  // With the element reference unchanged, React skips the whole table subtree.
  // Handlers come from rowHelpersRef so they stay out of the deps without going
  // stale; every other value the rows read is either in the dep list below or a
  // module-level pure helper (compute*, myInventoryRowTint, formatMoney, …).
  const tableBody = useMemo(() => (
    <tbody ref={flipBodyRef}>
      {sortedData.map((row) => (
        // [BULK-EDIT 2026-08-03] Row body lives in the memoised <InvRow>, so ticking a
        // checkbox re-renders ONE row instead of the whole table. This map still runs on
        // every selection change, but building a few hundred elements whose props are
        // unchanged is cheap — React.memo drops them before any cell renders.
        <InvRow
          key={row.id}
          row={row}
          cols={cols}
          showActions={showActions}
          viewOnly={viewOnly}
          rowStatusTint={rowStatusTint}
          selectable={bulkEnabled}
          rowSelectable={bulkEnabled && isRowSelectable(row)}
          selected={selectedIds.has(row.id)}
          onToggle={toggleRow}
          flash={row.id === flashId}
          editingKey={editing?.id === row.id ? editing.key : null}
          editValue={editing?.id === row.id ? editValue : ''}
          filters={filters}
          allBrands={allBrands}
          helpersRef={rowHelpersRef}
          cut={colResize.cut}
        />
      ))}
    </tbody>
    // Deps are the visible-state values only; row handlers come from rowHelpersRef
    // (a ref) so they're intentionally not listed. See the comment above.
  ), [sortedData, cols, editing, editValue, filters, allBrands, showActions, viewOnly, rowStatusTint,
      bulkEnabled, isRowSelectable, selectedIds, toggleRow, flashId, colResize.cut]);

  // Dropdown option lists. Selected values are pinned in even if they're not in
  // the fetched list yet (e.g. next month before its rows land, or the default
  // PM) so they always render and stay un-checkable.
  const monthOptions = [...months.filter((m) => !filters.months.includes(m)), ...filters.months];
  // [PM-FILTER-PAST 2026-10-26, user] The PM FILTER lists `filterPms` — for a past month the PMs on
  // that month's rows, so a PM who has left HubSpot can still be picked there; the current month
  // and later the live roster. (`filters.pms` stays the roster for the PM Name cell, bulk edit,
  // Add and Split.) Falls back to `pms` against a server that does not send it yet.
  const pmFilterNames = filters.filterPms ?? filters.pms;
  const pmOptions = [...pmFilters.filter((p) => !pmFilterNames.includes(p)), ...pmFilterNames];
  // Type/Brand/Status lists are month-scoped (from the fetch above); pin any
  // currently-selected value that isn't in the scoped list so it stays visible
  // and the user can still un-check it after switching months.
  const typeOptions = [...typeFilters.filter((t) => !filters.types.includes(t)), ...filters.types];
  const brandOptions = [...brandFilters.filter((b) => !filters.brands.includes(b)), ...filters.brands];
  const statusOptions = [...statusFilters.filter((s) => !filters.statuses.includes(s)), ...filters.statuses];
  // Add is available when nothing is filtered to a past-only set — i.e. no month
  // chosen, or at least one chosen month is the current month or later.
  const canAdd = months.length === 0 || months.some((m) => m >= getCurrentMonth());

  // [MOBILE 2026-07-07] The toolbar controls are built once here so the desktop
  // toolbar and the phone (three-row) toolbar can arrange the SAME elements
  // without duplicating any handler/scope logic. Nulls collapse to nothing, so
  // the desktop branch below stays identical to the previous inline JSX.
  // [LOAD-STATES 2026-09-29] Named so the empty state's "Clear search" runs the exact same reset.
  const applySearch = (v: string) => { setSearch(v); setMonths(v ? [] : (initialMonth ? [initialMonth] : [])); if (v) { setTypeFilters([]); setPmFilters([]); setBrandFilters([]); setStatusFilters([]); setAmFilter(''); } else { setPmFilters(defaultPm ? [defaultPm] : []); } setSort(v ? { sortBy: 'month', sortOrder: 'desc' } : { sortBy: 'website', sortOrder: 'asc' }); resetPage(); };
  const searchEl = (
    <SearchBar inline liquid value={search} onChange={applySearch} placeholder="Search inventory..." />
  );
  const failedView = loadFailed && data.length === 0; // [LOAD-STATES 2026-09-29]
  const recordCountEl = <RecordCount>{failedView ? 'Not loaded' : `${shownCount.toLocaleString()} records fetched`}</RecordCount>;
  const monthSelect = (
    <FilterSelect liquid value={months[0] || ''} onChange={(e) => { setMonths(e.target.value ? [e.target.value] : []); resetPage(); }}>
      {/* [MONTH-NO-ALL] 2026-06-23 — "All Months" is never an explicit pick;
          it only appears while a search has cleared the month (months empty),
          which keeps the select valid. */}
      {months.length === 0 && <option value="">All Months</option>}
      {monthOptions.map((m) => <option key={m} value={m}>{formatMonthFull(m)}</option>)}
    </FilterSelect>
  );
  const typeSelect = showTypeFilter ? (
    <FilterSelect liquid value={typeFilters[0] || ''} onChange={(e) => { setTypeFilters(e.target.value ? [e.target.value] : []); resetPage(); }}>
      <option value="">All Types</option>
      {typeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
    </FilterSelect>
  ) : null;
  const pmSelect = (
    <FilterSelect liquid value={pmFilters[0] || ''} onChange={(e) => { setPmFilters(e.target.value ? [e.target.value] : []); resetPage(); }}>
      {/* "All PMs" normally only for admins/seniors; also shown while searching, when
          the PM filter is cleared so the search spans the user's whole scope. */}
      {(!hideAllPms || !!search) && <option value="">All PMs</option>}
      {pmOptions.map((p) => <option key={p} value={p}>{p}</option>)}
    </FilterSelect>
  );
  // [AM-NAMES 2026-09-04, user] SINGLE-select by the user's ruling — "show me the rows this
  // AM is on". Sits right after PM, mirroring the column order in the table. Always shown:
  // unlike Type/Brand it is a fixed roster, so there is never a month where it would be empty.
  const amSelect = (
    <FilterSelect liquid value={amFilter} onChange={(e) => { setAmFilter(e.target.value); resetPage(); }}>
      <option value="">All AMs</option>
      {amOptions.map((a) => <option key={a} value={a}>{a}</option>)}
    </FilterSelect>
  );
  const brandSelect = showBrandFilter ? (
    <FilterSelect liquid value={brandFilters[0] || ''} onChange={(e) => { setBrandFilters(e.target.value ? [e.target.value] : []); resetPage(); }}>
      <option value="">All Brands</option>
      {brandOptions.map((b) => <option key={b} value={b}>{b}</option>)}
    </FilterSelect>
  ) : null;
  const statusSelect = (
    <FilterSelect liquid value={statusFilters[0] || ''} onChange={(e) => { setStatusFilters(e.target.value ? [e.target.value] : []); resetPage(); }}>
      <option value="">All Statuses</option>
      {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
    </FilterSelect>
  );
  const addBtn = (showActions && canAdd && isAdmin) ? (
    <AddButton onClick={handleAdd}>
      <i className="fas fa-plus" style={{ fontSize: 11 }}></i> Add
    </AddButton>
  ) : null;
  // [INVENTORY-CSV-EXPORT 2026-07-03] Download button — sits right after Add (so on
  // desktop it lands immediately right of the Status dropdown). Read-only export;
  // the server enforces PM scope so it only ever exports rows the viewer is
  // allowed to see.
  // [MYINV-DOWNLOAD 2026-07-15] Gated by its OWN prop. [DOWNLOAD-ACCESS 2026-07-30] The
  // DEFAULT is now admin-or-senior (it used to fall back to showActions, which left the
  // Inventory tab's button open to everyone). My Inventory passes the same test explicitly.
  // NOTE: this is UI gating. The rows still come from GET /api/inventory, which the whole
  // page uses, so it cannot be blocked server-side without a dedicated export endpoint —
  // the data returned is already clamped to the viewer's PM scope either way.
  // [FLUID-3 2026-10-12] Each opening gets a new downloadSeq (the window's key) — see where it is declared.
  const downloadBtn = (showDownloadButton ?? (isAdmin || isSenior)) ? (
    <DownloadButton onClick={() => { setDownloadSeq((n) => n + 1); setShowDownload(true); }} title="Download inventory records to a CSV (Excel) file">
      <i className="fas fa-download" style={{ fontSize: 11 }}></i> Download record
    </DownloadButton>
  ) : null;
  // [INV-MOBILE-STATUS-ROW 2026-07-07] The phone toolbar's bottom action row only
  // exists when there are action buttons. [INV PHONE TOOLBAR 2026-09-23, user] It now
  // holds ONLY Add + Download; Status always stays in the filter grid, right after
  // All Brands. [MYINV PHONE DOWNLOAD 2026-09-23, user] Inventory tab only: My
  // Inventory DOES show Download (admins/seniors; the old note here said it had no
  // buttons), and it now sits in the filter grid instead. See MobileGridWide above.
  const hasActionRow = !!(addBtn || downloadBtn);

  // [PHONE-LAYOUT 2026-10-22] The blocks above the toolbar (My Inventory: the "Add the AM" banner or its skeleton,
  // then the Performance Snapshot), built ONCE. Desktop renders them in FrozenTop above the toolbar exactly as before.
  // [PHONE-LAYOUT 2026-10-23, user] Phones render them right UNDER the toolbar (the title and the filters that set
  // them come first), in the page's one scroll — no longer inside the table's own scroll box.
  const aboveToolbar = (
    <>
      {/* [AM-REMINDER 2026-09-24, user] Slim one-line notice pill ABOVE the Performance Snapshot.
          [AM-REMINDER-SKELETON 2026-09-24, user] Until this view's rows are in, its place in the
          skeleton instead, and only where the banner is expected (see amBannerExpected). */}
      {amBannerExpected && <AmBannerSkeleton />}
      {amSettled && showAmBanner && amProgress && (
        <AmBannerWrap>
          <AmBanner role="status" aria-live="polite">
            <AmBannerIcon aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <line x1="19" y1="8" x2="19" y2="14" />
                <line x1="22" y1="11" x2="16" y2="11" />
              </svg>
            </AmBannerIcon>
            {/* ⚠ AmBannerSkeleton repeats this copy so it wraps the same: change both together. */}
            <AmBannerText>
              <AmBannerTitle>Add the AM on your campaigns</AmBannerTitle>
              <AmBannerDivider aria-hidden="true" />
              <AmBannerBody>
                You now have access to the <b>AM Names</b> column. Click the AM Names cell on each
                campaign and select the Account Manager working on it.
              </AmBannerBody>
            </AmBannerText>
            {/* The count pill doubles as the progress bar: its green fill grows with each AM saved. */}
            <AmBannerPill title={`${amProgress.done} of ${amProgress.total} campaigns have an AM`}>
              <AmBannerPillFill
                aria-hidden="true"
                style={{ width: `${Math.round((amProgress.done / amProgress.total) * 100)}%` }}
              />
              <AmBannerPillText>
                <strong><AnimatedNumber value={amProgress.done} format={formatWholeNumber} /></strong>
                {' '}/ {amProgress.total} have an AM
              </AmBannerPillText>
            </AmBannerPill>
          </AmBanner>
        </AmBannerWrap>
      )}
      {showKpis && <InventoryKpiBar pm={kpiPm} month={kpiMonth} />}
    </>
  );

  return (
    <Container>
      <FrozenTop>
      {/* [PHONE-LAYOUT 2026-10-22] Desktop: the banner + Performance Snapshot here, as before. Phones: under the toolbar. */}
      {!isMobile && aboveToolbar}
      <Toolbar>
        <ToolbarLeft>
          <PageTitle>{title}</PageTitle>
          <TitleDivider />
          <EffectiveInventory>
            Placed Inventory :<strong>{failedView ? '—' : formatMoney(placedDisplay) /* [LOAD-STATES 2026-09-29] */}</strong>
          </EffectiveInventory>
        </ToolbarLeft>
        {/* [SEARCH-SCOPE 2026-06-26] Searching CLEARS the PM (+ type/brand/status) filters so
            the search spans the user's FULL allowed scope, not just the one selected PM:
            an individual searches only their own rows, a team owner searches their whole team,
            an admin/senior searches everything — the server enforces this via pmScopeClause.
            Month is cleared too (search all months). Clearing the search restores the default
            PM view (My Inventory's own name; admins/seniors → All PMs). */}
        {isMobile ? (
          // [MOBILE 2026-07-07] [MYINV-MOBILE 2026-07-07] Deterministic rows (see the
          // styled defs): search+count, then ALL filter dropdowns three-per-row (My
          // Inventory's Month / PM / Status land on ONE row; the full Inventory tab's
          // five wrap 3-then-2), then an actions row (Add/Download) only when those
          // buttons exist (never rendered on My Inventory, which has none).
          <>
            <MobileSearchRow>
              {searchEl}
              {recordCountEl}
            </MobileSearchRow>
            <MobileFilterRow>
              {monthSelect}
              {typeSelect}
              {pmSelect}
              {amSelect}
              {brandSelect}
              {/* [INV PHONE TOOLBAR 2026-09-23, user] Status always sits here, right after
                  All Brands, on both tabs (the Inventory tab used to drop it to the action row). */}
              {statusSelect}
              {/* [MYINV PHONE DOWNLOAD 2026-09-23, user] My Inventory (showActions off, so never
                  an Add): Download joins the grid right after Status instead of its own row. */}
              {!showActions && downloadBtn && <MobileGridWide>{downloadBtn}</MobileGridWide>}
            </MobileFilterRow>
            {showActions && hasActionRow && (
              <MobileActionRow>
                {addBtn}
                {downloadBtn}
              </MobileActionRow>
            )}
          </>
        ) : (
          // Desktop: search fills the middle of the toolbar (matches the Payments
          // page), filters + actions on the right — unchanged.
          <>
            {searchEl}
            <Filters>
              {recordCountEl}
              {monthSelect}
              {typeSelect}
              {pmSelect}
              {amSelect}
              {brandSelect}
              {statusSelect}
              {addBtn}
              {downloadBtn}
            </Filters>
          </>
        )}
      </Toolbar>
      {/* [PHONE-LAYOUT 2026-10-23, user] Phones: the banner + Performance Snapshot under the title and filters. */}
      {isMobile && aboveToolbar}
      {/* [BULK-EDIT 2026-08-03] Selection bar. Lives inside FrozenTop so it stays pinned
          above the scrolling rows, and only exists when there's something to act on —
          a live selection, or an applied batch still offering its Undo. */}
      {bulkEnabled && (selectedVisibleIds.length > 0 || undoBatch) && (
        <BulkBar>
          {selectedVisibleIds.length > 0 ? (
            <>
              <BulkCount>
                {selectedVisibleIds.length.toLocaleString()} record{selectedVisibleIds.length === 1 ? '' : 's'} selected
              </BulkCount>
              {!allVisibleSelected && selectableVisible.length > selectedVisibleIds.length && (
                <>
                  <BulkSep />
                  <BulkLink type="button" onClick={toggleSelectAll}>
                    Select all {selectableVisible.length.toLocaleString()}
                  </BulkLink>
                </>
              )}
              <BulkSep />
              <BulkLink type="button" onClick={clearSelection}>Clear</BulkLink>
              <BulkBtn type="button" onClick={openBulkModal} style={{ marginLeft: 'auto' }}>
                <i className="fas fa-pen" style={{ fontSize: 10 }} /> Edit
              </BulkBtn>
            </>
          ) : (
            <BulkCount style={{ fontWeight: 600 }}>
              {undoBatch!.count.toLocaleString()} record{undoBatch!.count === 1 ? '' : 's'} updated — {undoBatch!.label}
            </BulkCount>
          )}
          {undoBatch && (
            <BulkBtn
              type="button"
              $muted
              onClick={undoBulk}
              disabled={undoBusy}
              title="Revert the last bulk change"
              style={selectedVisibleIds.length > 0 ? undefined : { marginLeft: 'auto' }}
            >
              <i className="fas fa-rotate-left" style={{ fontSize: 10 }} />
              {undoBusy ? 'Undoing…' : 'Undo'}
            </BulkBtn>
          )}
        </BulkBar>
      )}
      </FrozenTop>

      <TableScroll>
      {loading ? (
        // [BULK-EDIT 2026-08-03] Prefix a blank column so the skeleton keeps the same
        // column count as the real table and rows don't shift when it fills in.
        <TableSkeleton
          columns={bulkEnabled ? [{ key: '__select', label: '', width: '34px' } as ColDef, ...cols] : cols}
          actions={showActions && !viewOnly}
        />
      ) : failedView ? (
        <LoadFailed what={title.toLowerCase()} onRetry={() => load()} />
      ) : visibleData.length === 0 ? (
        // [LOAD-STATES 2026-09-29, user] Say what is empty and offer the fix.
        search
          ? <EmptyState icon="search" title={`No inventory matches “${search}”`} hint="Check the spelling, or clear the search to see every campaign." action={{ label: 'Clear search', onClick: () => applySearch('') }} />
          : <EmptyState title={`No inventory${months.length === 1 ? ` in ${formatMonthFull(months[0])}` : ''} for these filters`} hint="Change the month or filters to see more campaigns." />
      ) : (
        <TableWrapper ref={landRef /* [FLUID-6 2026-10-12] the row wave's root */}>
          <Table ref={colResize.tableElRef} style={colResize.tableStyle}>
            <Thead>
              <tr>
                {/* [BULK-EDIT 2026-08-03] Select-all for the rows currently on screen.
                    Indeterminate when only some are ticked, disabled when none of the
                    visible rows may be bulk-edited. */}
                {bulkEnabled && (
                  <SelectTh>
                    <RowCheckbox
                      type="checkbox"
                      checked={allVisibleSelected}
                      disabled={selectableVisible.length === 0}
                      ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
                      onChange={toggleSelectAll}
                      title={allVisibleSelected ? 'Clear selection' : 'Select all shown rows'}
                      aria-label="Select all shown rows"
                    />
                  </SelectTh>
                )}
                {cols.map((col) => {
                  // [COL-RESIZE-ALL 2026-09-29] A dragged column's header takes its width from the
                  // variable (overriding the default min width) and its label is cut with an
                  // ellipsis; the handle needs the header positioned.
                  const dragged = !!colResize.cut?.[col.key];
                  return (
                    <Th
                      key={col.key}
                      $sortable={col.sortable}
                      $align={col.align}
                      style={{
                        minWidth: col.width,
                        ...(colResize.active ? { position: 'relative' as const } : {}),
                        ...colResize.thStyle(col.key),
                      }}
                      onClick={() => col.sortable && handleSort(col.key)}
                    >
                      {dragged ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle', ...colResize.labelStyle(col.key) }}>
                          <CutLabel data-dt-cut="">{col.label}</CutLabel>
                          {col.sortable && getSortIcon(col.key)}
                        </span>
                      ) : (
                        <>
                          {col.label}
                          {col.sortable && getSortIcon(col.key)}
                        </>
                      )}
                      {colResize.active && <ResizeHandle {...colResize.handleProps(col.key, col.label)} />}
                    </Th>
                  );
                })}
                {/* [ACTIONS-HEADER 2026-10-07, user] "Delete" → "Actions": the cell now holds Split / Undo split too. */}
                {showActions && !viewOnly && <Th $align="center" style={{ minWidth: '70px' }}>Actions</Th>}
              </tr>
            </Thead>
            {/* [PERF 2026-07-03] Memoised — see the tableBody useMemo above. */}
            {tableBody}
          </Table>
        </TableWrapper>
      )}
      </TableScroll>

      {/* Renewal Modal — portaled to <body> so the fixed overlay anchors to the
          viewport (AppLayout's PageFade transform would otherwise centre it in
          the middle of the full scrollable page).
          [FLUID-3 2026-10-12] Held mounted through its close (renewalPres, whose value is the row it was opened
          for); inert and showing its last open content while it goes (KeepWhileClosing). */}
      {renewalPres.mounted && renewalPres.value && createPortal(
        <Overlay onClick={closeRenewalModal} $open={!!renewalRow && renewalPres.entered} inert={!renewalRow}>
          <Modal onClick={(e) => e.stopPropagation()} $open={!!renewalRow && renewalPres.entered} $land={renewalLand}>
            <KeepWhileClosing open={!!renewalRow}>
            <ModalTitle>PIA Renewal</ModalTitle>
            <ModalSubtitle>{renewalPres.value.website}</ModalSubtitle>
            <ModalField>
              <ModalLabel>Renewal Status</ModalLabel>
              <ModalSelect liquidList
                autoFocus
                value={renewalStatus}
                onChange={(e) => setRenewalStatus(e.target.value)}
              >
                <option value="">— Select —</option>
                {RENEWAL_STATUS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </ModalSelect>
            </ModalField>
            {renewalStatus === 'Future Renewal' && (
              <>
                <ModalField>
                  <ModalLabel>Renewal Month</ModalLabel>
                  <ModalInput
                    type="month"
                    value={renewalMonth}
                    onChange={(e) => setRenewalMonth(e.target.value)}
                  />
                </ModalField>
                <ModalField>
                  <ModalLabel>Renewal Amount ($)</ModalLabel>
                  <ModalInput
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={renewalAmount}
                    onChange={(e) => setRenewalAmount(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveRenewal(); }}
                  />
                </ModalField>
              </>
            )}
            <ModalActions>
              <ModalBtn onClick={closeRenewalModal}>Cancel</ModalBtn>
              <ModalBtn $primary onClick={saveRenewal} disabled={renewalSaving}>
                {renewalSaving ? 'Saving...' : 'Save'}
              </ModalBtn>
            </ModalActions>
            </KeepWhileClosing>
          </Modal>
        </Overlay>,
        document.body
      )}

      {/* [BULK-EDIT 2026-08-03] Bulk edit modal — pick ONE column, set ONE value, applied
          to every selected row. Portaled for the same viewport-anchoring reason as the
          others. The confirm line spells out exactly what is about to happen (column,
          value, row count) because this is the one action here that writes many rows.
          [FLUID-3 2026-10-12] Held mounted through its close (bulkPres); inert and showing its last open
          content while it goes — an applied edit clears the selection at once, which read "Edit 0 records". */}
      {bulkPres.mounted && createPortal(
        <Overlay onClick={() => !bulkSaving && setShowBulkModal(false)} $open={showBulkModal && bulkPres.entered} inert={!showBulkModal}>
          <Modal onClick={(e) => e.stopPropagation()} $open={showBulkModal && bulkPres.entered}>
            <KeepWhileClosing open={showBulkModal}>
            <ModalTitle>Edit {selectedVisibleIds.length.toLocaleString()} record{selectedVisibleIds.length === 1 ? '' : 's'}</ModalTitle>
            <ModalSubtitle>Set one column to the same value on every selected row.</ModalSubtitle>

            <ModalField>
              <ModalLabel>Column</ModalLabel>
              {/* A view can pin the bulk feature to a single column (My Inventory → PM Name).
                  A one-option dropdown reads as broken, so show the column as a plain label
                  when there's no choice to make. */}
              {bulkCols.length === 1 ? (
                <BulkPreview style={{ marginTop: 0 }}><strong>{bulkCols[0].label}</strong></BulkPreview>
              ) : (
                <ModalSelect liquidList
                  autoFocus
                  value={bulkField}
                  onChange={(e) => { setBulkField(e.target.value); setBulkValue(''); }}
                >
                  {bulkCols.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </ModalSelect>
              )}
            </ModalField>

            <ModalField>
              <ModalLabel>New value</ModalLabel>
              {bulkCol?.type === 'select' ? (
                // Same option list the inline cell editor uses, so a bulk change can only
                // ever write a value an individual edit could have written.
                <ModalSelect liquidList value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}>
                  {/* [MYINV-PM-NO-BLANK 2026-09-28, user] On My Inventory the empty row for PM Name
                      is only a "nothing chosen yet" prompt — Update stays disabled until a name is picked. */}
                  <option value="">{pmNoBlankOnceSet && bulkCol.key === 'pm' ? '— Select a PM —' : '— Clear (blank) —'}</option>
                  {(bulkCol.options || getFilterOptions(bulkCol.key)).map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </ModalSelect>
              ) : (
                <ModalInput
                  type={bulkCol?.type === 'number' ? 'number' : 'text'}
                  step={bulkCol?.type === 'number' ? 'any' : undefined}
                  value={bulkValue}
                  placeholder={bulkCol?.type === 'number' ? '0' : 'Leave empty to clear'}
                  onChange={(e) => setBulkValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !bulkSaving) applyBulk(); }}
                />
              )}
            </ModalField>

            <BulkPreview>
              Set <strong>{bulkCol?.label ?? '—'}</strong> to{' '}
              <strong>{bulkValue.trim() === '' ? '(blank)' : bulkValue.trim()}</strong> on{' '}
              <strong>{selectedVisibleIds.length.toLocaleString()}</strong> record{selectedVisibleIds.length === 1 ? '' : 's'}.
              <br />
              This can be reverted with Undo straight after.
            </BulkPreview>

            <ModalActions>
              <ModalBtn onClick={() => setShowBulkModal(false)} disabled={bulkSaving}>Cancel</ModalBtn>
              <ModalBtn $primary onClick={applyBulk} disabled={bulkSaving || !bulkField || selectedVisibleIds.length === 0
                || (pmNoBlankOnceSet && bulkField === 'pm' && bulkValue.trim() === '') /* [MYINV-PM-NO-BLANK 2026-09-28] */}>
                {bulkSaving ? 'Updating…' : `Update ${selectedVisibleIds.length.toLocaleString()}`}
              </ModalBtn>
            </ModalActions>
            </KeepWhileClosing>
          </Modal>
        </Overlay>,
        document.body
      )}

      {/* Add Row Modal — portaled for the same viewport-anchoring reason. */}
      {/* [INVENTORY-CSV-EXPORT 2026-07-03] Download-record modal — its own
          component with local state (see DownloadRecordsModal) so its interactions
          never re-render this page + its DataTable.
          [FLUID-3 2026-10-12] Held mounted through its close (downloadPres). */}
      {downloadPres.mounted && (
        <DownloadRecordsModal
          key={downloadSeq}
          closing={downloadPres.closing}
          monthOptions={filters.months}
          // [CSV-COLUMN-PICKER 2026-07-28] The picker offers THIS page's columns:
          // the full Inventory set here, My Inventory's narrower set there.
          gridColumns={cols}
          defaultTypes={exportDefaultTypes} // [MYINV-EXPORT-RECURRING 2026-09-29]
          onClose={() => setShowDownload(false)}
        />
      )}

      {/* [FLUID-3 2026-10-12] Held mounted through its close (addPres); inert and showing its last open content
          while it goes. */}
      {addPres.mounted && createPortal(
        <Overlay onClick={() => setShowAddModal(false)} $open={isAdmin && showAddModal && addPres.entered} inert={!(isAdmin && showAddModal)}>
          <WideModal onClick={(e) => e.stopPropagation()} $open={isAdmin && showAddModal && addPres.entered}>
            <KeepWhileClosing open={isAdmin && showAddModal}>
            <ModalTitle>Add Inventory Row</ModalTitle>
            <ModalSubtitle>Enter the details for the new inventory row.</ModalSubtitle>
            <FormGrid>
              <FullRow>
                <ModalField style={{ marginBottom: 0 }}>
                  <ModalLabel>Website *</ModalLabel>
                  <ModalInput
                    autoFocus
                    placeholder="example.com"
                    value={addForm.website}
                    onChange={(e) => updateAddForm('website', e.target.value)}
                  />
                </ModalField>
              </FullRow>

              <ModalField style={{ marginBottom: 0 }}>
                <ModalLabel>Email</ModalLabel>
                <ModalInput
                  placeholder="client@example.com"
                  value={addForm.email}
                  onChange={(e) => updateAddForm('email', e.target.value)}
                />
              </ModalField>

              <ModalField style={{ marginBottom: 0 }}>
                <ModalLabel># of Campaigns</ModalLabel>
                <ModalInput
                  type="number"
                  min="0"
                  placeholder="0"
                  value={addForm.num_campaigns}
                  onChange={(e) => updateAddForm('num_campaigns', e.target.value)}
                />
              </ModalField>

              <ModalField style={{ marginBottom: 0 }}>
                <ModalLabel>Type</ModalLabel>
                <ModalSelect liquidList value={addForm.type} onChange={(e) => updateAddForm('type', e.target.value)}>
                  {TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </ModalSelect>
              </ModalField>

              <ModalField style={{ marginBottom: 0 }}>
                <ModalLabel>PM</ModalLabel>
                <ModalSelect liquidList value={addForm.pm} onChange={(e) => updateAddForm('pm', e.target.value)}>
                  <option value="">—</option>
                  {filters.pms.map((p) => <option key={p} value={p}>{p}</option>)}
                </ModalSelect>
              </ModalField>

              {/* [AM-NAMES 2026-09-04, user] Multi-select here too — the Add form is row data,
                  and a new row must be able to start with more than one AM. Same roster, same
                  checkboxes and same stored format as the cell editor. */}
              <ModalField style={{ marginBottom: 0 }}>
                <ModalLabel>AM Names</ModalLabel>
                <ModalSelect liquidList
                  multiple
                  multiCheckbox
                  multiUnitLabel="AMs"
                  values={parseAmNames(addForm.am_names)}
                  onChangeValues={(next) => updateAddForm('am_names', joinAmNames(next))}
                >
                  <option value="">—</option>
                  {amOptions.map((a) => <option key={a} value={a}>{a}</option>)}
                </ModalSelect>
              </ModalField>

              <ModalField style={{ marginBottom: 0 }}>
                <ModalLabel>Brand</ModalLabel>
                <ModalSelect liquidList value={addForm.brand} onChange={(e) => updateAddForm('brand', e.target.value)}>
                  <option value="">—</option>
                  {allBrands.map((b) => <option key={b} value={b}>{b}</option>)}
                </ModalSelect>
              </ModalField>

              <ModalField style={{ marginBottom: 0 }}>
                <ModalLabel>Amount ($)</ModalLabel>
                <ModalInput
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={addForm.amount}
                  onChange={(e) => updateAddForm('amount', e.target.value)}
                />
              </ModalField>

              <ModalField style={{ marginBottom: 0 }}>
                <ModalLabel>Payment Type</ModalLabel>
                <ModalSelect liquidList value={addForm.payment_type} onChange={(e) => updateAddForm('payment_type', e.target.value)}>
                  <option value="">—</option>
                  {PAY_TYPE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </ModalSelect>
              </ModalField>

              <ModalField style={{ marginBottom: 0 }}>
                <ModalLabel>Billing Date (day)</ModalLabel>
                <ModalInput
                  type="number"
                  min="1"
                  max="31"
                  placeholder="1-31"
                  value={addForm.billing_date}
                  onChange={(e) => updateAddForm('billing_date', e.target.value)}
                />
              </ModalField>

              <ModalField style={{ marginBottom: 0 }}>
                <ModalLabel>Month *</ModalLabel>
                <ModalInput
                  type="month"
                  value={addForm.month}
                  onChange={(e) => updateAddForm('month', e.target.value)}
                />
              </ModalField>

              <ModalField style={{ marginBottom: 0 }}>
                <ModalLabel>Status</ModalLabel>
                <ModalSelect liquidList value={addForm.status} onChange={(e) => updateAddForm('status', e.target.value)}>
                  <option value="">—</option>
                  {STATUS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </ModalSelect>
              </ModalField>
            </FormGrid>

            <ModalActions>
              <ModalBtn onClick={() => setShowAddModal(false)}>Cancel</ModalBtn>
              <ModalBtn $primary onClick={submitAdd} disabled={addSaving}>
                {addSaving ? 'Adding...' : 'Add Data'}
              </ModalBtn>
            </ModalActions>
            </KeepWhileClosing>
          </WideModal>
        </Overlay>,
        document.body
      )}

      {/* [INV-SPLIT 2026-10-07, user]
          [FLUID-3 2026-10-12] Mounted while splitPres holds it — through its close too, still showing the row it
          was opened for (it used to be keyed to "split-closed" the moment it closed, which unmounted it at once). */}
      {splitPres.mounted && splitPres.value && (
        <SplitInventoryModal
          key={`split-${splitPres.value.id}`}
          row={splitPres.value}
          closing={splitPres.closing}
          pms={filters.pms}
          onClose={() => setSplitTarget(null)}
          onDone={onSplitDone}
        />
      )}
      <ConfirmDialog liquid
        open={unsplitTarget !== null}
        title="Undo split"
        message={<>Merge <strong>{unsplitTarget?.website}</strong> back into one row for the first PM? The other part goes to the Trash, and the PM shares on this month&apos;s payments are removed (their retention stays).</>}
        confirmLabel="Undo split"
        cancelLabel="Cancel"
        busy={unsplitBusy}
        icon="fa-object-ungroup"
        onConfirm={confirmUnsplit}
        onCancel={() => setUnsplitTarget(null)}
      />

      <ConfirmDialog liquid
        open={deleteTarget !== null}
        title="Delete Inventory Row"
        message={<>Delete <strong>{deleteTarget?.website}</strong> from inventory?</>}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        destructive
        busy={deleteBusy}
        icon="fa-trash-alt"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Container>
  );
};

export default InventoryPage;
